// dashboard-agent-ia/src/pages/business/BusinessDashboard.js
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import useInactivity from '../../hooks/useInactivity'
import axios from 'axios'
import PhoneInput, { availableCountries } from '../../components/PhoneInput'
import {
  MdSmartToy, MdChat, MdBarChart, MdSettings, MdLogout,
  MdPeople, MdTrendingUp, MdNotifications, MdClose, MdAdd,
  MdQuestionAnswer, MdCalendarToday, MdPsychology,
  MdShoppingCart, MdPersonSearch, MdRequestQuote,
  MdLocalShipping, MdCheck, MdDelete, MdSend,
  MdPlayArrow, MdMic, MdLock, MdUpgrade, MdEdit,
  MdSave, MdChevronLeft, MdMenu, MdAutoAwesome, MdCode,
  MdLightMode, MdDarkMode, MdDownload, MdUpload,
  MdRefresh,
  MdShare,
} from 'react-icons/md'

const API_URL = process.env.REACT_APP_API_URL

// Formatage monétaire
// eslint-disable-next-line no-unused-vars
const formatMoney = (val) => {
  if (!val && val !== 0) return ''
  return parseInt(val.toString().replace(/\D/g,'')||'0').toLocaleString('fr-FR')
}
// eslint-disable-next-line no-unused-vars
const parseMoney = (val) => parseInt(val.toString().replace(/\s/g,'').replace(/\D/g,'')||'0') || 'https://agent-ia-senegal.onrender.com'

// ─── Utilitaires formatage ───
const toTitleCase = (str) => {
  if (!str) return ''
  return str.split(' ').map(word => {
    if (!word) return word
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  }).join(' ')
}
// capitalizeWords — identique à Register (ne force pas lowercase sur le reste)
const capitalizeWords = (str) => {
  if (!str) return ''
  return str.split(' ').map(word => {
    if (!word) return word
    const chars = [...word]
    return chars[0].toUpperCase() + chars.slice(1).join('')
  }).join(' ')
}
const toSentenceCase = (str) => str.charAt(0).toUpperCase() + str.slice(1)
const formatNumber = (val) => {
  const num = val.replace(/\D/g, '')
  return num.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}
const parseNumber = (val) => val.replace(/\s/g, '')

// ─── Placeholders dynamiques par secteur ───
const getPlaceholders = (sector = '') => {
  const s = (sector || '').toLowerCase()
  if (s.includes('immo') || s.includes('btp') || s.includes('construction') || s.includes('terrain')) return {
    clientName: 'Ex: Amadou Diallo', description: 'Ex: Villa F8 Almadies - Achat avec financement',
    montant: 'Ex: 75 000 000', amount: 'Ex: 75 000 000 FCFA', orderLabel: 'Dossier',
    phone: 'Ex: +221 77 000 00 00', service: 'Ex: Estimation bien immobilier',
    question: 'Ex: Quels sont vos frais d\'agence ?', answer: 'Ex: Nos frais sont de 5% du prix de vente.',
    category: 'Prix', skill: 'Ex: Négocier un compromis de vente',
    critere: 'Ex: Estimation immobilière', prix: 'Ex: 150 000',
    relanceMsg: 'Bonjour {nom}, avez-vous visité le bien qui vous intéressait ?'
  }
  if (s.includes('clinique') || s.includes('santé') || s.includes('médecin') || s.includes('pharma')) return {
    clientName: 'Ex: Fatou Sène', description: 'Ex: Consultation générale - Fièvre persistante',
    montant: 'Ex: 15 000', amount: 'Ex: 15 000 FCFA', orderLabel: 'Patient',
    phone: 'Ex: +221 77 000 00 00', service: 'Ex: Consultation pédiatrique',
    question: 'Ex: Quels sont vos horaires de consultation ?', answer: 'Ex: Nous consultons du lundi au vendredi de 8h à 17h.',
    category: 'Consultations', skill: 'Ex: Ne jamais donner de diagnostic par WhatsApp',
    critere: 'Ex: Consultation générale', prix: 'Ex: 10 000',
    relanceMsg: 'Bonjour {nom}, comment vous sentez-vous depuis votre dernière consultation ?'
  }
  if (s.includes('tourisme') || s.includes('hôtel') || s.includes('hotel') || s.includes('voyage') || s.includes('agence de voyage')) return {
    clientName: 'Ex: Moussa Traoré', description: 'Ex: Circuit Maroc 7 jours - 2 personnes',
    montant: 'Ex: 850 000', amount: 'Ex: 850 000 FCFA', orderLabel: 'Réservation',
    phone: 'Ex: +221 77 000 00 00', service: 'Ex: Séjour Dubaï tout compris',
    question: 'Ex: Quels documents faut-il pour voyager au Maroc ?', answer: 'Ex: Passeport valide + billet retour. Aucun visa requis pour les Sénégalais.',
    category: 'Séjours', skill: 'Ex: Toujours mentionner les conditions d\'annulation',
    critere: 'Ex: Circuit Maroc 7 jours', prix: 'Ex: 750 000',
    relanceMsg: 'Bonjour {nom}, avez-vous eu le temps de consulter notre offre de voyage ?'
  }
  if (s.includes('restaurant') || s.includes('traiteur') || s.includes('food')) return {
    clientName: 'Ex: Mariama Ba', description: 'Ex: Thiéboudienne x2 + Yassa poulet + Bissap',
    montant: 'Ex: 8 500', amount: 'Ex: 8 500 FCFA', orderLabel: 'Commande',
    phone: 'Ex: +221 77 000 00 00', service: 'Ex: Menu du jour',
    question: 'Ex: Livrez-vous à Dakar Plateau ?', answer: 'Ex: Oui, nous livrons dans tout Dakar entre 30 et 45 minutes.',
    category: 'Menu', skill: 'Ex: Toujours indiquer le délai de préparation',
    critere: 'Ex: Menu déjeuner complet', prix: 'Ex: 5 000',
    relanceMsg: 'Bonjour {nom}, vous nous manquez ! Découvrez notre menu du jour ️'
  }
  if (s.includes('boutique') || s.includes('commerce') || s.includes('vente') || s.includes('mode')) return {
    clientName: 'Ex: Ibrahima Fall', description: 'Ex: Sac cuir marron - Taille M - Ref: BAG-001',
    montant: 'Ex: 25 000', amount: 'Ex: 25 000 FCFA', orderLabel: 'Commande',
    phone: 'Ex: +221 77 000 00 00', service: 'Ex: Vente en ligne',
    question: 'Ex: Faites-vous les livraisons en province ?', answer: 'Ex: Oui, nous livrons dans toutes les régions du Sénégal sous 48h.',
    category: 'Produits', skill: 'Ex: Toujours vérifier la disponibilité avant de confirmer',
    critere: 'Ex: Livraison Dakar', prix: 'Ex: 2 000',
    relanceMsg: 'Bonjour {nom}, votre article est toujours disponible ! ️'
  }
  if (s.includes('transport') || s.includes('logistique') || s.includes('livraison') || s.includes('taxi')) return {
    clientName: 'Ex: Seydou Ndiaye', description: 'Ex: Transport Dakar → Thiès - 3 colis',
    montant: 'Ex: 12 000', amount: 'Ex: 12 000 FCFA', orderLabel: 'Course',
    phone: 'Ex: +221 77 000 00 00', service: 'Ex: Livraison express',
    question: 'Ex: Quel est votre tarif pour Dakar-Thiès ?', answer: 'Ex: Notre tarif Dakar-Thiès est de 12 000 FCFA pour une berline.',
    category: 'Tarifs', skill: 'Ex: Toujours confirmer l\'adresse exacte avant départ',
    critere: 'Ex: Trajet Dakar-Thiès', prix: 'Ex: 12 000',
    relanceMsg: "Bonjour {nom}, avez-vous besoin d'un transport aujourd'hui ?"
  }
  if (s.includes('event') || s.includes('événement') || s.includes('organisation')) return {
    clientName: 'Ex: Aïssatou Diallo', description: 'Ex: Mariage 200 personnes - Salle climatisée',
    montant: 'Ex: 2 500 000', amount: 'Ex: 2 500 000 FCFA', orderLabel: 'Événement',
    phone: 'Ex: +221 77 000 00 00', service: 'Ex: Organisation mariage',
    question: 'Ex: Proposez-vous la décoration incluse ?', answer: 'Ex: Oui, décoration de base incluse. Options premium disponibles.',
    category: 'Organisation', skill: 'Ex: Toujours demander un acompte de 30% à la réservation',
    critere: 'Ex: Organisation complète mariage', prix: 'Ex: 1 500 000',
    relanceMsg: 'Bonjour {nom}, votre événement approche ! Avez-vous confirmé tous les détails ?'
  }
  if (s.includes('info') || s.includes('tech') || s.includes('digital') || s.includes('web') || s.includes('informatique')) return {
    clientName: 'Ex: Cheikh Diop', description: 'Ex: Création site e-commerce avec paiement Wave',
    montant: 'Ex: 350 000', amount: 'Ex: 350 000 FCFA', orderLabel: 'Projet',
    phone: 'Ex: +221 77 000 00 00', service: 'Ex: Développement web',
    question: 'Ex: Quel est votre délai pour un site vitrine ?', answer: 'Ex: Nous livrons un site vitrine en 7 à 14 jours ouvrables.',
    category: 'Développement', skill: 'Ex: Toujours fournir un cahier des charges avant de commencer',
    critere: 'Ex: Site vitrine 5 pages', prix: 'Ex: 150 000',
    relanceMsg: 'Bonjour {nom}, où en êtes-vous dans votre projet digital ?'
  }
  return {
    clientName: 'Ex: Aminata Diallo', description: 'Ex: Description de la prestation',
    montant: 'Ex: 50 000', amount: 'Ex: 50 000 FCFA', orderLabel: 'Commande',
    phone: 'Ex: +221 77 000 00 00', service: 'Ex: Prestation standard',
    question: 'Ex: Quels sont vos tarifs ?', answer: 'Ex: Nos tarifs dépendent de la prestation. Contactez-nous pour un devis.',
    category: 'Général', skill: 'Ex: Répondre dans les 5 minutes',
    critere: 'Ex: Prestation de base', prix: 'Ex: 50 000',
    relanceMsg: 'Bonjour {nom}, avez-vous des questions sur nos services ?'
  }
}




const ALL_MODULES = ['faq', 'skills', 'leads', 'personality', 'catalog', 'suivi', 'devis', 'modeles', 'social', 'rdv', 'relances', 'email', 'automation', 'notifications', 'qrcode', 'paiements', 'employes']

const PLAN_MODULES = {
  trial:   ALL_MODULES, // 14 jours — tout ouvert
  starter: ['faq', 'skills', 'leads', 'personality'],
  pro:     ['faq', 'skills', 'leads', 'personality', 'catalog', 'suivi', 'devis', 'modeles', 'social', 'rdv'],
  premium: ALL_MODULES,
}

// En mode dev on ouvre tout
// eslint-disable-next-line no-unused-vars
const IS_DEV = process.env.NODE_ENV !== 'production'

const getFaqCategories = (sector = '') => {
  const s = (sector || '').toLowerCase()
  if (s.includes('immo') || s.includes('btp')) return ['Général','Biens','Visites','Prix','Financement','Contrats','Contact','Autre']
  if (s.includes('clinique') || s.includes('santé') || s.includes('médecin')) return ['Général','Consultations','Traitements','Urgences','Tarifs','Assurance','Contact','Autre']
  if (s.includes('tourisme') || s.includes('hôtel') || s.includes('hotel') || s.includes('voyage') || s.includes('agence')) return ['Général','Séjours','Circuits','Hébergement','Transports','Visa & Formalités','Tarifs','Autre']
  if (s.includes('restaurant') || s.includes('food') || s.includes('traiteur')) return ['Général','Menu','Commandes','Livraison','Réservations','Allergènes','Horaires','Autre']
  if (s.includes('boutique') || s.includes('commerce') || s.includes('vente')) return ['Général','Produits','Prix','Livraison','Retours','Paiement','Horaires','Autre']
  if (s.includes('transport') || s.includes('taxi')) return ['Général','Tarifs','Zones','Réservations','Véhicules','Paiement','Contact','Autre']
  if (s.includes('info') || s.includes('tech') || s.includes('digital') || s.includes('web')) return ['Général','Services','Tarifs','Délais','Support','Technologies','Contact','Autre']
  if (s.includes('event') || s.includes('événement')) return ['Général','Prestations','Tarifs','Disponibilités','Références','Contact','Autre']
  return ['Général','Horaires','Prix & Paiement','Produits & Services','Contact','Autre']
}
const FAQ_CATEGORIES = getFaqCategories('')

const FAQ_SUGGESTIONS_BY_SECTOR = {
  restaurant: [
    { question: 'Quels sont vos horaires ?', answer: 'Nous sommes ouverts du lundi au samedi de 9h à 22h.', category: 'Horaires' },
    { question: 'Faites-vous des livraisons ?', answer: 'Oui, nous livrons dans un rayon de 5km. Commande minimum : 5 000 FCFA.', category: 'Livraison' },
    { question: 'Acceptez-vous les réservations ?', answer: 'Oui, envoyez votre nom, date et nombre de personnes.', category: 'Contact' },
    { question: 'Quel est votre menu du jour ?', answer: 'Notre menu change chaque jour. Demandez-nous !', category: 'Produits & Services' },
  ],
  boutique: [
    { question: 'Quels sont vos modes de paiement ?', answer: 'Nous acceptons Wave, Orange Money et espèces.', category: 'Prix & Paiement' },
    { question: 'Faites-vous des retours ?', answer: 'Oui, sous 7 jours avec le reçu.', category: 'Produits & Services' },
    { question: 'Livrez-vous à domicile ?', answer: 'Oui, la livraison est disponible sur Dakar.', category: 'Livraison' },
    { question: 'Quels sont vos horaires ?', answer: 'Ouvert du lundi au samedi de 9h à 20h.', category: 'Horaires' },
  ],
  clinique: [
    { question: 'Comment prendre un rendez-vous ?', answer: 'Envoyez votre nom, la spécialité et vos disponibilités.', category: 'Contact' },
    { question: 'Quels sont vos horaires ?', answer: 'Lundi au vendredi 8h-17h, samedi 8h-12h.', category: 'Horaires' },
    { question: 'Acceptez-vous les urgences ?', answer: 'Oui, nous avons une permanence pour les urgences.', category: 'Produits & Services' },
    { question: "Quels sont vos tarifs ?", answer: "Nos tarifs varient selon la spécialité. Contactez-nous.", category: 'Prix & Paiement' },
  ],
  default: [
    { question: 'Quels sont vos horaires ?', answer: 'Nous sommes disponibles du lundi au vendredi de 8h à 18h.', category: 'Horaires' },
    { question: 'Comment nous contacter ?', answer: 'Vous pouvez nous écrire directement sur WhatsApp.', category: 'Contact' },
    { question: 'Quels sont vos moyens de paiement ?', answer: 'Nous acceptons Wave, Orange Money et espèces.', category: 'Prix & Paiement' },
    { question: 'Livrez-vous à domicile ?', answer: 'Contactez-nous pour connaître nos conditions de livraison.', category: 'Livraison' },
  ]
}

const getSuggestions = (sector = '') => {
  const s = sector.toLowerCase()
  if (s.includes('restaurant') || s.includes('traiteur') || s.includes('fast')) return FAQ_SUGGESTIONS_BY_SECTOR.restaurant
  if (s.includes('boutique') || s.includes('commerce') || s.includes('chaussure') || s.includes('sac')) return FAQ_SUGGESTIONS_BY_SECTOR.boutique
  if (s.includes('clinique') || s.includes('santé') || s.includes('médecin') || s.includes('pharmacie')) return FAQ_SUGGESTIONS_BY_SECTOR.clinique
  return FAQ_SUGGESTIONS_BY_SECTOR.default
}

const getCategoryColor = (cat) => {
  const colors = {
    'Horaires': { bg: 'rgba(59,130,246,0.2)', text: '#60a5fa', border: 'rgba(59,130,246,0.4)' },
    'Prix & Paiement': { bg: 'rgba(16,185,129,0.2)', text: '#34d399', border: 'rgba(16,185,129,0.4)' },
    'Livraison': { bg: 'rgba(245,158,11,0.2)', text: '#fbbf24', border: 'rgba(245,158,11,0.4)' },
    'Produits & Services': { bg: 'rgba(139,92,246,0.2)', text: '#a78bfa', border: 'rgba(139,92,246,0.4)' },
    'Contact': { bg: 'rgba(6,182,212,0.2)', text: '#22d3ee', border: 'rgba(6,182,212,0.4)' },
    'Autre': { bg: 'rgba(107,114,128,0.2)', text: '#9ca3af', border: 'rgba(107,114,128,0.4)' },
  }
  return colors[cat] || colors['Autre']
}

const formatPhone = (phone) => {
  if (!phone) return ''
  const d = phone.replace(/\D/g, '')
  if (d.length === 12 && d.startsWith('221')) {
    const n = d.slice(3)
    return `(+221) ${n.slice(0,2)} ${n.slice(2,5)} ${n.slice(5,7)} ${n.slice(7)}`
  }
  return phone
}

const genId = () => Math.random().toString(36).substr(2, 9)

// ─── Theme Hook ───
const useTheme = () => {
  const [isDark, setIsDark] = useState(localStorage.getItem('theme') === 'dark')
  const toggle = () => {
    const next = !isDark
    setIsDark(next)
    localStorage.setItem('theme', next ? 'dark' : 'light')
  }
  return { isDark, toggle }
}

// ─── Theme Colors ───
const getTheme = (isDark) => ({
  bg: isDark ? 'linear-gradient(135deg,#0f172a 0%,#1e1b4b 50%,#0f172a 100%)' : '#f1f5f9',
  sidebar: isDark ? 'linear-gradient(180deg,#0f172a 0%,#1e1b4b 60%,#0f172a 100%)' : 'rgba(255,255,255,0.97)',
  sidebarBorder: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)',
  card: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.95)',
  cardBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
  cardShadow: isDark ? '0 8px 32px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.08)',
  text: isDark ? '#f1f5f9' : '#1e293b',
  textSub: isDark ? '#94a3b8' : '#64748b',
  textMuted: isDark ? '#475569' : '#94a3b8',
  navActive: 'linear-gradient(135deg,#3b82f6,#1d4ed8)',
  navHover: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(59,130,246,0.08)',
  navText: isDark ? '#94a3b8' : '#64748b',
  inputBg: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)',
  inputBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)',
  modalBg: isDark ? 'rgba(13,20,40,0.98)' : 'rgba(255,255,255,0.99)',
  modalBorder: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
  userBox: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
})

// ─── Skeleton ───
const SkeletonStat = ({ isDark }) => (
  <div className="rounded-2xl p-6 animate-pulse" style={{background:isDark?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.06)',border:'1px solid rgba(0,0,0,0.08)'}}>
    <div className="flex justify-between mb-4"><div className="w-11 h-11 rounded-xl bg-slate-200 opacity-30"/><div className="w-12 h-6 rounded-lg bg-slate-200 opacity-30"/></div>
    <div className="h-8 w-16 rounded bg-slate-200 opacity-30 mb-2"/><div className="h-3 w-24 rounded bg-slate-200 opacity-30"/>
  </div>
)

// ─── Modal ───

// ── Descriptions des modules (bouton aide) ──
const MODULE_HELP = {
  faq: {
    title: "Module FAQ — Questions Fréquentes",
    desc: "Configurez les questions et réponses que vos clients posent le plus souvent. L'IA utilisera ces réponses pour répondre automatiquement sur WhatsApp sans jamais inventer.",
    tips: ["Ajoutez au moins 5-8 questions", "Soyez précis dans les réponses", "Incluez les prix et horaires"]
  },
  catalog: {
    title: "Module Catalogue",
    desc: "Listez vos produits, services ou biens avec leurs prix et descriptions. Quand un client demande un produit sur WhatsApp, l'IA consulte ce catalogue pour répondre avec les bonnes informations.",
    tips: ["Importez depuis Excel ou JSON", "Ajoutez des photos pour plus d'impact", "Mettez à jour les stocks régulièrement"]
  },
  leads: {
    title: "Module Collecte de Leads",
    desc: "Configurez des questions de qualification pour identifier vos prospects. L'IA pose ces questions automatiquement aux nouveaux clients pour collecter leurs informations et évaluer leur intérêt.",
    tips: ["3 à 6 questions suffisent", "Commencez par les besoins essentiels", "Terminez par le budget ou la disponibilité"]
  },
  rdv: {
    title: "Module Rendez-vous",
    desc: "Permettez à vos clients de prendre rendez-vous directement via WhatsApp. L'IA gère le calendrier, confirme les créneaux disponibles et envoie des rappels automatiques.",
    tips: ["Définissez vos créneaux disponibles", "Configurez la durée de chaque RDV", "Activez les rappels J-1"]
  },
  devis: {
    title: "Module Devis Automatique",
    desc: "Créez une grille tarifaire de vos prestations. L'IA génère automatiquement des devis PDF et les envoie au client sur WhatsApp quand il en fait la demande.",
    tips: ["Listez toutes vos prestations avec fourchettes de prix", "L'IA calcule le total automatiquement", "Le PDF est envoyé instantanément"]
  },
  skills: {
    title: "Module Compétences IA",
    desc: "Définissez les règles de comportement de votre agent IA. Ces instructions guident l'IA sur comment répondre, quoi éviter et comment représenter votre entreprise.",
    tips: ["Ajoutez des règles métier spécifiques", "Marquez les règles critiques comme prioritaires", "Soyez précis et concret"]
  },
  modeles: {
    title: "Module Modèles de Messages",
    desc: "Créez des templates de messages réutilisables pour les situations courantes. L'IA peut les utiliser pour envoyer des confirmations, rappels et suivis automatiquement.",
    tips: ["Utilisez {nom}, {date}, {montant} comme variables", "Créez un modèle par situation type", "Testez les variables avant de sauvegarder"]
  },
  relances: {
    title: "Module Relances Automatiques",
    desc: "Configurez des messages de relance envoyés automatiquement aux clients inactifs. L'IA les contacte à J+1, J+3, J+7 pour maintenir l'engagement et relancer les ventes.",
    tips: ["Commencez par une relance douce J+1", "Variez le ton selon le délai", "Incluez toujours une valeur ou une offre"]
  },
  suivi: {
    title: "Module Suivi des Commandes",
    desc: "Gérez le statut des commandes et dossiers de vos clients. L'IA informe automatiquement le client quand son statut change et répond aux questions de suivi.",
    tips: ["Personnalisez les statuts selon votre secteur", "Mettez à jour les statuts régulièrement", "Le client est notifié automatiquement"]
  },
  social: {
    title: "Module Réseaux Sociaux",
    desc: "Générez du contenu professionnel pour vos réseaux sociaux. L'IA crée des posts adaptés à chaque plateforme (TikTok, Facebook, Instagram, LinkedIn) selon votre secteur.",
    tips: ["Spécifiez le sujet et le ton souhaité", "Chaque plateforme a son format optimal", "Bientôt: publication directe via API"]
  },
  paiements: {
    title: "Module Paiements",
    desc: "Configurez vos moyens de paiement acceptés (Wave, Orange Money, carte bancaire). L'IA guide le client dans le processus de paiement et confirme les transactions.",
    tips: ["Activez Wave et Orange Money en priorité", "Configurez le lien de paiement", "L'IA confirme les paiements reçus"]
  },
  personality: {
    title: "Module Personnalité IA",
    desc: "Personnalisez le style de communication de votre agent IA : ton (professionnel, chaleureux, expert), langue (FR/EN/Wolof) et instructions spéciales pour votre secteur.",
    tips: ["Choisissez le ton selon votre clientèle", "Activez le Wolof pour les clients locaux", "Ajoutez des instructions spécifiques à votre métier"]
  },
  settings: {
    title: "Paramètres Entreprise",
    desc: "Configurez les informations légales, logo, numéros de téléphone et moyens de paiement de votre entreprise. Ces infos sont utilisées dans les devis PDF et par l'IA.",
    tips: ["Ajoutez votre vrai numéro de téléphone", "Uploadez votre logo pour les devis professionnels", "Renseignez le NINEA pour les documents officiels"]
  },
  stats: {
    title: "Statistiques",
    desc: "Visualisez les performances de votre agent IA : messages reçus, clients servis, taux de réponse et questions les plus posées.",
    tips: ["Consultez les stats chaque semaine", "Les top questions guident votre FAQ", "Le taux de réponse doit être > 95%"]
  },
  notifications: {
    title: "Notifications",
    desc: "Recevez les alertes en temps réel : nouveaux prospects, paiements à confirmer, messages non lus. Validez ou rejetez les paiements Wave/Orange Money.",
    tips: ["Consultez les notifications chaque jour", "Validez les paiements rapidement", "Les paiements non confirmés restent en attente"]
  },
  conversations: {
    title: "Conversations WhatsApp",
    desc: "Visualisez toutes les conversations de vos clients avec l'IA. Intervenez manuellement si besoin en envoyant un message direct depuis le dashboard.",
    tips: ["Supervisez les conversations quotidiennement", "Intervenez si l'IA ne répond pas correctement", "Utilisez le bouton CR pour un compte-rendu IA"]
  },
  widget: {
    title: "Widget WhatsApp",
    desc: "Générez un bouton WhatsApp à intégrer sur votre site web. Les visiteurs peuvent vous contacter directement sur WhatsApp en un clic.",
    tips: ["Copiez le code dans le <body> de votre site", "Choisissez la position et la couleur", "Testez le lien avant de publier"]
  },
  employes: {
    title: "Module Employés",
    desc: "Gérez vos employés et communiquez avec eux via WhatsApp directement depuis le dashboard. Messages individuels ou groupés par département.",
    tips: ["Ajoutez le numéro WhatsApp de chaque employé", "Envoyez des messages groupés à tout votre personnel", "Créez des départements pour cibler vos messages"]
  },
}

// ── Composant bouton aide réutilisable ──
const ModuleHelpBtn = ({ moduleId, isDark }) => {
  const [show, setShow] = useState(false)
  const th = getTheme(isDark)
  const help = MODULE_HELP[moduleId]
  if (!help) return null
  return (
    <div style={{position:'relative', display:'inline-block'}}>
      <button
        onClick={() => setShow(!show)}
        title="Aide — Rôle du module"
        style={{
          width: 28, height: 28, borderRadius: '50%',
          background: show ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.1)',
          border: '1px solid rgba(59,130,246,0.3)',
          color: '#60a5fa', fontSize: 13, fontWeight: 800,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', flexShrink: 0,
        }}>
        ?
      </button>
      {show && (
        <div style={{
          position: 'absolute', top: 36, right: 0, zIndex: 200,
          width: 300, background: th.modalBg,
          border: '1px solid rgba(59,130,246,0.3)',
          borderRadius: 16, padding: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom: 10}}>
            <p style={{fontWeight:700, fontSize:13, color:'#60a5fa', margin:0}}>{help.title}</p>
            <button onClick={() => setShow(false)} style={{background:'none',border:'none',cursor:'pointer',color:th.textMuted,fontSize:16,padding:0}}>✕</button>
          </div>
          <p style={{fontSize:13, color:th.textSub, lineHeight:1.6, marginBottom:10}}>{help.desc}</p>
          <div style={{borderTop:`1px solid ${th.cardBorder}`, paddingTop:10}}>
            <p style={{fontSize:11, fontWeight:700, color:th.textMuted, marginBottom:6}}>💡 CONSEILS</p>
            {help.tips.map((tip, i) => (
              <p key={i} style={{fontSize:12, color:th.textSub, marginBottom:4}}>• {tip}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const Modal = ({ title, icon, color, onClose, children, wide = false, extraWide = false, isDark, moduleId }) => {
  const th = getTheme(isDark)
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4" style={{background:'rgba(0,0,0,0.7)',backdropFilter:'blur(16px)'}}>
      <div className={`w-full ${extraWide?'sm:max-w-4xl':wide?'sm:max-w-3xl':'sm:max-w-lg'} rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl max-h-[92vh] sm:max-h-[90vh] flex flex-col`}
        style={{background:th.modalBg, border:`1px solid ${th.modalBorder}`}}>
        <div className="flex items-center justify-between p-6 flex-shrink-0" style={{borderBottom:`1px solid ${th.modalBorder}`}}>
          <div className="flex items-center gap-3">
            <div style={{width:46,height:46,borderRadius:14,background:color,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,boxShadow:'0 4px 12px '+color+'40'}}>{icon}</div>
            <div>
              <h3 className="font-bold text-lg" style={{color:th.text}}>{title}</h3>
              <p className="text-xs" style={{color:th.textMuted}}> Français · English · Wolof · Audio</p>
            </div>
          </div>
          <div style={{display:'flex', alignItems:'center', gap:8}}>
            {moduleId && <ModuleHelpBtn moduleId={moduleId} isDark={isDark}/>}
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center transition-all" style={{color:th.textSub, background:th.inputBg}}>
              <MdClose size={20}/>
            </button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  )
}

const LockedModal = ({ module, plan, onClose, isDark }) => {
  const th = getTheme(isDark)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.7)',backdropFilter:'blur(16px)'}}>
      <div className="w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl text-center" style={{background:th.modalBg,border:`1px solid ${th.modalBorder}`}}>
        <div className="p-8">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{background:th.inputBg}}>
            <MdLock size={32} style={{color:th.textSub}}/>
          </div>
          <h3 className="font-bold text-xl mb-2" style={{color:th.text}}>Module verrouillé</h3>
          <p className="text-sm mb-1" style={{color:th.textSub}}>Le module <strong style={{color:th.text}}>{module}</strong> n'est pas inclus dans votre plan <span style={{color:'#f59e0b',fontWeight:'bold'}} className="capitalize">{plan}</span>.</p>
          <p className="text-xs mb-6" style={{color:th.textMuted}}>Passez au plan supérieur pour débloquer tous les modules.</p>
          <div className="space-y-3">
            <button className="w-full py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2" style={{background:'linear-gradient(135deg,#f59e0b,#d97706)'}}><MdUpgrade size={18}/>Passer au plan Pro</button>
            <button onClick={onClose} className="w-full py-2.5 rounded-xl text-sm transition-colors" style={{background:th.inputBg,color:th.textSub}}>Fermer</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// eslint-disable-next-line no-unused-vars
const ZeroBadge = ({ isDark }) => {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium mb-4" style={{background:'rgba(59,130,246,0.1)',border:'1px solid rgba(59,130,246,0.25)'}}>
      <MdAutoAwesome size={13} className="text-blue-400"/>
      <span style={{color:'#93c5fd'}}>Zéro Hallucination — L'IA ne répond qu'avec ces données.</span>
    </div>
  )
}

const StyledInput = ({placeholder,value,onChange,className='',isDark}) => {
  const th = getTheme(isDark)
  return (
    <input value={value} onChange={onChange} placeholder={placeholder}
      className={`w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${className}`}
      style={{background:th.inputBg, border:`1px solid ${th.inputBorder}`, color:th.text}}/>
  )
}

const StyledTA = ({placeholder,value,onChange,rows=2,isDark}) => {
  const th = getTheme(isDark)
  return (
    <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows}
      className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
      style={{background:th.inputBg, border:`1px solid ${th.inputBorder}`, color:th.text}}/>
  )
}

// ─── MODULE FAQ COMPLET ───
const FAQModal = ({ businessId, business, onClose, isDark }) => {
  const th = getTheme(isDark)
  const ph = getPlaceholders(business?.sector)
  const [faqs, setFaqs] = useState([])
  const [q, setQ] = useState(''); const [a, setA] = useState(''); const [category, setCategory] = useState('Autre')
  const [editId, setEditId] = useState(null); const [editData, setEditData] = useState({})
  const [saving, setSaving] = useState(false); const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('list')
  const [filterCat, setFilterCat] = useState('Tous'); const [searchQ, setSearchQ] = useState('')
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState('')
  const [customCat, setCustomCat] = useState('')

  const suggestions = getSuggestions(business?.sector || '')

  useEffect(() => {
    axios.get(`${API_URL}/api/businesses/${businessId}/modules`)
      .then(r => setFaqs(r.data.faq || [])).catch(() => setFaqs([])).finally(() => setLoading(false))
  }, [businessId])

  const addFaq = () => {
    if (!q || !a) return
    setFaqs([...faqs, { id: genId(), question: q, answer: a, category, count: 0 }])
    setQ(''); setA(''); setCategory('Autre'); setActiveTab('list')
  }
  const addSuggestion = (sug) => {
    if (faqs.find(f => f.question === sug.question)) {
      // Désélectionner — supprimer si déjà ajouté
      setFaqs(faqs.filter(f => f.question !== sug.question))
    } else {
      setFaqs([...faqs, { id: genId(), ...sug, count: 0 }])
    }
  }
  const startEdit = (faq) => { setEditId(faq.id); setEditData({ question: faq.question, answer: faq.answer, category: faq.category || 'Autre' }) }
  const saveEdit = () => { setFaqs(faqs.map(f => f.id === editId ? { ...f, ...editData } : f)); setEditId(null) }
  const deleteFaq = (id) => setFaqs(faqs.filter(f => f.id !== id))

  // Export XLSX (remplace CSV)
  const exportXLSX = async () => {
    if (faqs.length === 0) { alert('Aucune FAQ à exporter'); return }
    try {
      const XLSX = await import('xlsx').then(m => m.default || m)
      const ws = XLSX.utils.json_to_sheet(faqs.map(f => ({
        Question: f.question, Réponse: f.answer, Catégorie: f.category || 'Autre'
      })))
      ws['!cols'] = [{wch:50},{wch:80},{wch:20}]
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'FAQ')
      XLSX.writeFile(wb, `faq-${Date.now()}.xlsx`)
    } catch(e) { alert('Erreur export: ' + e.message) }
  }

  // Export JSON
  const exportJSON = () => {
    if (faqs.length === 0) { alert('Aucune FAQ à exporter'); return }
    const data = faqs.map(f => ({ question: f.question, answer: f.answer, category: f.category || 'Autre' }))
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `faq-${Date.now()}.json`; a.click()
    URL.revokeObjectURL(url)
  }

  // Import JSON
  const importJSON = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result)
        const items = Array.isArray(data) ? data : (data.faqs || [])
        const newFaqs = items.filter(i => i.question || i.Question).map(i => ({
          id: Date.now().toString(36) + Math.random().toString(36).slice(2),
          question: i.question || i.Question || '',
          answer: i.answer || i.Réponse || i.reponse || '',
          category: i.category || i.Catégorie || i.categorie || 'Autre',
          count: 0,
        })).filter(f => f.question)
        if (newFaqs.length === 0) { alert('Aucune FAQ valide trouvée'); return }
        if (!window.confirm(`Importer ${newFaqs.length} FAQ ?`)) return
        setFaqs(prev => [...prev, ...newFaqs])
        alert(`✅ ${newFaqs.length} FAQ importées !`)
      } catch(err) { alert('Erreur JSON: ' + err.message) }
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  // Import XLSX
  const importXLSX = async (e) => {
    const file = e.target.files[0]; if (!file) return
    try {
      const XLSX = await import('xlsx').then(m => m.default || m)
      const buffer = await file.arrayBuffer()
      const wb = XLSX.read(buffer)
      const ws = wb.Sheets[wb.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json(ws)
      const newFaqs = rows.filter(r => r.Question || r.question).map(r => ({
        id: Date.now().toString(36) + Math.random().toString(36).slice(2),
        question: r.Question || r.question || '',
        answer: r.Réponse || r.answer || r.Answer || r.reponse || '',
        category: r.Catégorie || r.category || r.Category || 'Autre',
        count: 0,
      })).filter(f => f.question)
      if (newFaqs.length === 0) { alert('Aucune FAQ valide'); return }
      if (!window.confirm(`Importer ${newFaqs.length} FAQ ?`)) return
      setFaqs(prev => [...prev, ...newFaqs])
      alert(`✅ ${newFaqs.length} FAQ importées !`)
    } catch(e) { alert('Erreur: ' + e.message) }
    e.target.value = ''
  }

  const save = async () => {
    setSaving(true)
    try { await axios.put(`${API_URL}/api/businesses/${businessId}/modules/faq`, { data: faqs }); onClose() }
    catch (e) { console.error(e) } finally { setSaving(false) }
  }

  const generateWithAI = async () => {
    setGenerating(true); setGenerateError('')
    try {
      const storedLang = localStorage.getItem('lang') || 'fr'
      const res = await axios.post(`${API_URL}/api/generate/faq/${businessId}`, { language: storedLang })
      const newFaqs = res.data.faqs
      setFaqs(prev => {
        const existing = prev.map(f => f.question.toLowerCase())
        const unique = newFaqs.filter(f => !existing.includes(f.question.toLowerCase()))
        return [...prev, ...unique]
      })
    } catch (e) {
      setGenerateError('Erreur génération. Réessayez.')
    } finally { setGenerating(false) }
  }

  const filteredFaqs = faqs.filter(f => {
    const matchCat = filterCat === 'Tous' || f.category === filterCat
    const matchSearch = !searchQ || f.question.toLowerCase().includes(searchQ.toLowerCase())
    return matchCat && matchSearch
  })

  const catCounts = FAQ_CATEGORIES.reduce((acc, cat) => { acc[cat] = faqs.filter(f => f.category === cat).length; return acc }, {})

  return (
    <Modal title="Module FAQ" moduleId="faq" icon={<MdQuestionAnswer size={20} className="text-white"/>} color="#3b82f6" onClose={onClose} isDark={isDark}>
      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { label: 'Questions', value: faqs.length, color: '#60a5fa', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.25)' },
          { label: 'Catégories', value: Object.keys(catCounts).filter(c => catCounts[c] > 0).length, color: '#34d399', bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.25)' },
          { label: 'Suggestions', value: suggestions.filter(s => !faqs.find(f => f.question === s.question)).length, color: '#a78bfa', bg: 'rgba(139,92,246,0.12)', border: 'rgba(139,92,246,0.25)' },
        ].map((s, i) => (
          <div key={i} className="rounded-xl p-3 text-center" style={{background:s.bg,border:`1px solid ${s.border}`}}>
            <p className="text-2xl font-bold" style={{color:s.color}}>{s.value}</p>
            <p className="text-xs" style={{color:th.textMuted}}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Génération IA */}
      <button onClick={generateWithAI} disabled={generating}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold mb-3 transition-all disabled:opacity-50"
        style={{background:'linear-gradient(135deg,#a855f7,#7c3aed)',color:'white'}}>
        {generating ? (
          <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>Génération en cours...</>
        ) : (
          <><MdAutoAwesome size={16}/>✨ Générer avec l'IA</>
        )}
      </button>
      {generateError && <p className="text-red-400 text-xs text-center mb-2">{generateError}</p>}


      {/* Import/Export — XLSX + JSON */}
      <div style={{display:"flex", gap:6, flexWrap:"wrap", marginBottom:16}}>
        <button onClick={exportXLSX} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold" style={{background:"rgba(16,185,129,0.12)",color:"#10b981",border:"1px solid rgba(16,185,129,0.25)"}}>
          <MdDownload size={13}/>Excel
        </button>
        <button onClick={exportJSON} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold" style={{background:"rgba(139,92,246,0.12)",color:"#a78bfa",border:"1px solid rgba(139,92,246,0.25)"}}>
          <MdDownload size={13}/>JSON
        </button>
        <label className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer" style={{background:"rgba(59,130,246,0.12)",color:"#60a5fa",border:"1px solid rgba(59,130,246,0.25)"}}>
          <MdUpload size={13}/>Import Excel
          <input type="file" accept=".xlsx,.xls" onChange={importXLSX} className="hidden"/>
        </label>
        <label className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer" style={{background:"rgba(245,158,11,0.12)",color:"#fbbf24",border:"1px solid rgba(245,158,11,0.25)"}}>
          <MdUpload size={13}/>Import JSON
          <input type="file" accept=".json" onChange={importJSON} className="hidden"/>
        </label>
      </div>


      {/* Tabs */}
      <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{background:th.inputBg}}>
        {[
          { id: 'list', label: ` Mes FAQ (${faqs.length})` },
          { id: 'add', label: ' Ajouter' },
          { id: 'suggest', label: ` Suggestions (${suggestions.filter(s => !faqs.find(f => f.question === s.question)).length})` },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
            style={activeTab === tab.id ? {background:'linear-gradient(135deg,#3b82f6,#1d4ed8)',color:'white'} : {color:th.textSub}}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Liste */}
      {activeTab === 'list' && (
        <div>
          <div className="flex gap-2 mb-3">
            <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Rechercher..."
              className="flex-1 px-3 py-2 rounded-xl text-xs focus:outline-none"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
              className="px-3 py-2 rounded-xl text-xs focus:outline-none"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}>
              <option value="Tous">Toutes</option>
              {[...new Set([...FAQ_CATEGORIES, ...faqs.map(f => f.category).filter(Boolean)])].map(c => <option key={c} value={c}>{c} ({catCounts[c]||0})</option>)}
            </select>
          </div>
          {loading ? <div className="text-center py-4" style={{color:th.textSub}}>Chargement...</div>
          : filteredFaqs.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm mb-2" style={{color:th.textSub}}>Aucune FAQ</p>
              <button onClick={() => setActiveTab('suggest')} className="text-blue-400 text-xs">Voir les suggestions →</button>
            </div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {filteredFaqs.map(f => {
                const cc = getCategoryColor(f.category || 'Autre')
                return (
                  <div key={f.id} className="px-4 py-3 rounded-xl" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`}}>
                    {editId === f.id ? (
                      <div className="space-y-2">
                        <input value={editData.question} onChange={e => setEditData({...editData,question:e.target.value})}
                          className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none" style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}}/>
                        <textarea value={editData.answer} onChange={e => setEditData({...editData,answer:e.target.value})} rows={2}
                          className="w-full px-3 py-2 rounded-lg text-sm resize-none focus:outline-none" style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}}/>
                        <select value={editData.category} onChange={e => setEditData({...editData,category:e.target.value})}
                          className="w-full px-3 py-2 rounded-lg text-xs focus:outline-none" style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}}>
                          {FAQ_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <div className="flex gap-2">
                          <button onClick={saveEdit} className="flex-1 py-1.5 rounded-lg text-xs text-white font-semibold flex items-center justify-center gap-1" style={{background:'linear-gradient(135deg,#10b981,#059669)'}}><MdSave size={13}/>OK</button>
                          <button onClick={()=>setEditId(null)} className="flex-1 py-1.5 rounded-lg text-xs" style={{background:th.inputBg,color:th.textSub}}>Annuler</button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="text-sm font-semibold flex-1 text-blue-400">{f.question}</p>
                          <div className="flex gap-1 flex-shrink-0">
                            <button onClick={()=>startEdit(f)} className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:text-blue-400" style={{color:th.textSub}}><MdEdit size={14}/></button>
                            <button onClick={()=>deleteFaq(f.id)} className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:text-red-400" style={{color:th.textSub}}><MdDelete size={14}/></button>
                          </div>
                        </div>
                        <p className="text-xs mb-2" style={{color:th.textSub}}>{f.answer}</p>
                        {/* Badge catégorie TOUJOURS visible */}
                        <span className="text-xs px-2.5 py-1 rounded-full font-medium"
                          style={{background:cc.bg, color:cc.text, border:`1px solid ${cc.border}`}}>
                          {f.category || 'Autre'}
                        </span>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab Ajouter */}
      {activeTab === 'add' && (
        <div className="space-y-3">
          <div>
            <label className="text-xs mb-2 block" style={{color:th.textSub}}>Catégorie</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {FAQ_CATEGORIES.map(cat => {
                const cc = getCategoryColor(cat)
                const isSelected = category === cat
                return (
                  <button key={cat} onClick={() => {setCategory(cat);setCustomCat('')}}
                    className="px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
                    style={{
                      background: isSelected ? cc.bg : th.inputBg,
                      color: isSelected ? cc.text : th.textSub,
                      border: `1px solid ${isSelected ? cc.border : th.inputBorder}`,
                    }}>
                    {cat}
                  </button>
                )
              })}
            </div>
            <input value={customCat} onChange={e=>{setCustomCat(e.target.value);setCategory(toTitleCase(e.target.value)||'Autre')}}
              placeholder="Ou saisir une catégorie personnalisée..."
              className="w-full px-3 py-2 rounded-xl text-xs focus:outline-none"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          </div>
          <StyledInput placeholder={`${ph.question||"Question du client..."}`} value={q} onChange={e=>setQ(e.target.value)} isDark={isDark}/>
          <StyledTA placeholder="Réponse de l'IA..." value={a} onChange={e=>setA(e.target.value)} rows={3} isDark={isDark}/>
          <button onClick={addFaq} disabled={!q||!a} className="w-full py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 flex items-center justify-center gap-2"
            style={{background:'linear-gradient(135deg,#3b82f6,#1d4ed8)'}}><MdAdd size={16}/>Ajouter</button>
        </div>
      )}

      {/* Tab Suggestions */}
      {activeTab === 'suggest' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs" style={{color:th.textSub}}>Recommandées pour <span style={{color:th.text,fontWeight:'600'}}>{business?.sector}</span></p>
            <button onClick={async () => {
              try {
                const storedLang = localStorage.getItem('lang') || 'fr'
                const res = await axios.post(`${API_URL}/api/generate/faq/${businessId}`, { language: storedLang, sector: business?.sector })
                if (res.data.faqs) {
                  const existing = faqs.map(f => f.question.toLowerCase())
                  const newFaqs = res.data.faqs.filter(f => !existing.includes(f.question.toLowerCase()))
                  setFaqs(prev => [...prev, ...newFaqs])
                }
              } catch(e) { console.error(e) }
            }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold"
              style={{background:'linear-gradient(135deg,#a855f7,#7c3aed)',color:'white'}}>
              <MdAutoAwesome size={13}/>✨ IA
            </button>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
            {suggestions.map((sug, i) => {
              const added = faqs.find(f => f.question === sug.question)
              const cc = getCategoryColor(sug.category)
              return (
                <div key={i} className="flex items-start gap-3 px-4 py-3 rounded-xl"
                  style={{background:added?'rgba(16,185,129,0.08)':th.inputBg, border:`1px solid ${added?'rgba(16,185,129,0.2)':th.inputBorder}`}}>
                  <div className="flex-1">
                    <p className="text-sm font-medium" style={{color:th.text}}>{sug.question}</p>
                    <p className="text-xs mt-0.5" style={{color:th.textSub}}>{sug.answer}</p>
                    <span className="text-xs px-2 py-0.5 rounded-full mt-1 inline-block font-medium"
                      style={{background:cc.bg, color:cc.text, border:`1px solid ${cc.border}`}}>
                      {sug.category}
                    </span>
                  </div>
                  <button onClick={()=>addSuggestion(sug)}
                    className="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center"
                    style={{background:added?'rgba(16,185,129,0.2)':'rgba(59,130,246,0.2)', color:added?'#34d399':'#60a5fa'}}>
                    {added?<MdCheck size={16}/>:<MdAdd size={16}/>}
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <button onClick={save} disabled={saving}
        className="w-full mt-4 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2"
        style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>
        {saving?'Sauvegarde...':<><MdCheck size={16}/>Sauvegarder {faqs.length} FAQ</>}
      </button>
    </Modal>
  )
}

// ─── MODULE RDV ───

const RDV_SECTORS = (sector = '') => {
  const s = sector.toLowerCase()
  if (s.includes('clinique') || s.includes('santé') || s.includes('médecin')) return {
    dureeDefaut: 30, label: 'Consultation', unite: 'patient',
    horaires: { debut: '08:00', fin: '17:00' },
    rappel: 'Rappel : Votre consultation est prévue le {date} à {heure}. Merci de vous présenter 10 min avant. '
  }
  if (s.includes('immo') || s.includes('btp')) return {
    dureeDefaut: 60, label: 'Visite', unite: 'client',
    horaires: { debut: '09:00', fin: '18:00' },
    rappel: 'Rappel : Votre visite est prévue le {date} à {heure}. Notre agent sera sur place. '
  }
  if (s.includes('restaurant') || s.includes('food')) return {
    dureeDefaut: 90, label: 'Réservation', unite: 'convive',
    horaires: { debut: '11:00', fin: '22:00' },
    rappel: 'Rappel : Votre table est réservée le {date} à {heure}. À bientôt ! ️'
  }
  return {
    dureeDefaut: 45, label: 'RDV', unite: 'client',
    horaires: { debut: '08:00', fin: '17:00' },
    rappel: 'Rappel : Votre RDV est prévu le {date} à {heure}. À bientôt !'
  }
}

const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre']

const RDVModal = ({ businessId, business, onClose, isDark }) => {
  const th = getTheme(isDark)
  const ph = getPlaceholders(business?.sector)
  const sectorInfo = RDV_SECTORS(business?.sector)
  const [activeView, setActiveView] = useState('calendar')
  const [currentDate, setCurrentDate] = useState(new Date())
  const [rdvList, setRdvList] = useState([])
  const [selectedDay, setSelectedDay] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [loading, setLoading] = useState(true) // eslint-disable-line no-unused-vars
  const [saving, setSaving] = useState(false)

  // Horaires config
  const [horaireDebut, setHoraireDebut] = useState(sectorInfo.horaires.debut)
  const [horaireFin, setHoraireFin] = useState(sectorInfo.horaires.fin)
  const [duree, setDuree] = useState(sectorInfo.dureeDefaut)
  const [joursActifs, setJoursActifs] = useState([0,1,2,3,4]) // lun-ven

  // Formulaire nouveau RDV
  const [newClient, setNewClient] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [newHeure, setNewHeure] = useState('09:00')
  const [newNote, setNewNote] = useState('')
  const [newStatut, setNewStatut] = useState('confirme')

  useEffect(() => {
    axios.get(`${API_URL}/api/businesses/${businessId}/modules`)
      .then(r => {
        const rdv = r.data.rdv || []
        // Convertir ancien format si nécessaire
        if (rdv.length > 0 && rdv[0].day !== undefined) {
          setRdvList([])
        } else {
          setRdvList(rdv)
        }
        const config = r.data.rdvConfig || {}
        if (config.debut) setHoraireDebut(config.debut)
        if (config.fin) setHoraireFin(config.fin)
        if (config.duree) setDuree(config.duree)
        if (config.jours) setJoursActifs(config.jours)
      })
      .catch(() => {}).finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true)
    try {
      await axios.put(`${API_URL}/api/businesses/${businessId}/modules/rdv`, { data: rdvList })
      await axios.put(`${API_URL}/api/businesses/${businessId}/modules/rdvConfig`, {
        data: { debut: horaireDebut, fin: horaireFin, duree, jours: joursActifs }
      })
      onClose()
    } catch (e) {
      console.error('RDV save:', e.message)
      alert('Erreur sauvegarde: ' + (e.response?.data?.error || e.message))
    } finally { setSaving(false) }
  }

  // Générer les jours du mois
  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const days = []
    // Padding début (lundi = 0)
    let startDow = firstDay.getDay() - 1
    if (startDow < 0) startDow = 6
    for (let i = 0; i < startDow; i++) days.push(null)
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, month, d))
    }
    return days
  }

  const dateKey = (date) => date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : ''

  const getRdvsForDay = (date) => {
    if (!date) return []
    return rdvList.filter(r => r.date === dateKey(date))
  }

  const addRdv = () => {
    if (!newClient || !newHeure || !selectedDay) return
    const rdv = {
      id: genId(),
      date: dateKey(selectedDay),
      heure: newHeure,
      client: newClient,
      phone: newPhone,
      note: newNote,
      statut: newStatut,
      label: sectorInfo.label,
    }
    setRdvList(prev => [...prev, rdv].sort((a,b) => a.date.localeCompare(b.date) || a.heure.localeCompare(b.heure)))
    setNewClient(''); setNewPhone(''); setNewHeure('09:00'); setNewNote('')
    setShowAddForm(false)
  }

  const deleteRdv = (id) => setRdvList(prev => prev.filter(r => r.id !== id))

  const updateStatut = (id, statut) => setRdvList(prev => prev.map(r => r.id === id ? {...r, statut} : r))

  const statutColors = {
    confirme: { bg:'rgba(16,185,129,0.15)', color:'#34d399', label:'Confirmé' },
    attente: { bg:'rgba(245,158,11,0.15)', color:'#fbbf24', label:'En attente' },
    annule: { bg:'rgba(239,68,68,0.15)', color:'#f87171', label:'Annulé' },
    termine: { bg:'rgba(107,114,128,0.15)', color:'#9ca3af', label:'Terminé' },
  }

  const days = getDaysInMonth(currentDate)
  const today = new Date()
  const todayKey = dateKey(today)

  // Générer créneaux horaires
  const genCreneaux = () => {
    const slots = []
    const [dh, dm] = horaireDebut.split(':').map(Number)
    const [fh, fm] = horaireFin.split(':').map(Number)
    let cur = dh * 60 + dm
    const end = fh * 60 + fm
    while (cur + duree <= end) {
      const h = String(Math.floor(cur/60)).padStart(2,'0')
      const m = String(cur%60).padStart(2,'0')
      slots.push(`${h}:${m}`)
      cur += duree
    }
    return slots
  }

  const upcomingRdvs = rdvList
    .filter(r => r.date >= dateKey(today))
    .sort((a,b) => a.date.localeCompare(b.date) || a.heure.localeCompare(b.heure))
    .slice(0, 10)

  return (
    <Modal moduleId="rdv" title={`Module RDV — ${sectorInfo.label}s`} icon={<MdCalendarToday size={20} className="text-white"/>} color="#8b5cf6" onClose={onClose} wide isDark={isDark}>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{background:th.inputBg}}>
        {[
          {id:'calendar', label:' Calendrier'},
          {id:'list', label:` Liste (${upcomingRdvs.length})`},
          {id:'config', label:'⚙️ Config'},
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveView(tab.id)}
            className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
            style={activeView===tab.id?{background:'linear-gradient(135deg,#8b5cf6,#6d28d9)',color:'white'}:{color:th.textSub}}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── VUE CALENDRIER ─── */}
      {activeView === 'calendar' && (
        <div>
          {/* Navigation mois */}
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth()-1, 1))}
              className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background:th.inputBg,color:th.text}}>
              ‹
            </button>
            <p className="text-sm font-bold" style={{color:th.text}}>
              {MONTHS_FR[currentDate.getMonth()]} {currentDate.getFullYear()}
            </p>
            <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth()+1, 1))}
              className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background:th.inputBg,color:th.text}}>
              ›
            </button>
          </div>

          {/* Jours de la semaine */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS_FR.map(d => (
              <div key={d} className="text-center text-xs font-semibold py-1" style={{color:th.textMuted}}>{d}</div>
            ))}
          </div>

          {/* Grille calendrier */}
          <div className="grid grid-cols-7 gap-0.5">
            {days.map((day, i) => {
              if (!day) return <div key={i}/>
              const key = dateKey(day)
              const rdvs = getRdvsForDay(day)
              const isToday = key === todayKey
              const isSelected = selectedDay && dateKey(selectedDay) === key
              const isPast = day < today && !isToday
              const dow = (day.getDay() + 6) % 7
              const isOff = !joursActifs.includes(dow)
              return (
                <button key={i} onClick={() => { setSelectedDay(day); setShowAddForm(false) }}
                  className="rounded-lg p-1 min-h-12 flex flex-col items-center transition-all relative"
                  style={{
                    background: isSelected ? 'rgba(139,92,246,0.2)' : isToday ? 'rgba(139,92,246,0.08)' : th.inputBg,
                    border: `1px solid ${isSelected ? '#8b5cf6' : isToday ? 'rgba(139,92,246,0.3)' : th.inputBorder}`,
                    opacity: isPast ? 0.4 : isOff ? 0.5 : 1,
                  }}>
                  <span className="text-xs font-semibold" style={{color: isToday ? '#a78bfa' : th.text}}>{day.getDate()}</span>
                  {rdvs.length > 0 && (
                    <div className="flex flex-col gap-0.5 w-full mt-0.5">
                      {rdvs.slice(0,2).map((r,ri) => (
                        <div key={ri} className="text-xs rounded px-0.5 truncate text-center"
                          style={{background:statutColors[r.statut]?.bg||'rgba(139,92,246,0.15)',color:statutColors[r.statut]?.color||'#a78bfa',fontSize:'9px'}}>
                          {r.heure}
                        </div>
                      ))}
                      {rdvs.length > 2 && <div className="text-xs text-center" style={{color:th.textMuted,fontSize:'9px'}}>+{rdvs.length-2}</div>}
                    </div>
                  )}
                </button>
              )
            })}
          </div>

          {/* Détail jour sélectionné */}
          {selectedDay && (
            <div className="mt-3 rounded-xl p-3" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`}}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold" style={{color:th.text}}>
                  {selectedDay.toLocaleDateString('fr-FR', {weekday:'long',day:'numeric',month:'long'})}
                </p>
                <button onClick={() => setShowAddForm(!showAddForm)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold text-white"
                  style={{background:'linear-gradient(135deg,#8b5cf6,#6d28d9)'}}>
                  <MdAdd size={13}/>RDV
                </button>
              </div>

              {showAddForm && (
                <div className="space-y-2 mb-3 p-3 rounded-xl" style={{background:th.card,border:`1px solid ${th.cardBorder}`}}>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Client</label>
                      <input value={newClient} onChange={e=>setNewClient(e.target.value)}
                        placeholder={ph.clientName||"Nom du client"}
                        className="w-full px-3 py-2 rounded-xl text-xs focus:outline-none"
                        style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
                    </div>
                    <div>
                      <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Téléphone</label>
                      <input value={newPhone} onChange={e=>setNewPhone(e.target.value)}
                        placeholder="+221 77..."
                        className="w-full px-3 py-2 rounded-xl text-xs focus:outline-none"
                        style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Heure</label>
                      <select value={newHeure} onChange={e=>setNewHeure(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl text-xs focus:outline-none"
                        style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}>
                        {(genCreneaux().length > 0 ? genCreneaux() : ['08:00','09:00','10:00','11:00','12:00','14:00','15:00','16:00','17:00']).map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Statut</label>
                      <select value={newStatut} onChange={e=>setNewStatut(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl text-xs focus:outline-none"
                        style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}>
                        {Object.entries(statutColors).map(([k,v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <input value={newNote} onChange={e=>setNewNote(e.target.value)}
                    placeholder="Note (optionnel)"
                    className="w-full px-3 py-2 rounded-xl text-xs focus:outline-none"
                    style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
                  <button onClick={addRdv} disabled={!newClient||!newHeure}
                    className="w-full py-2 rounded-xl text-xs font-semibold text-white disabled:opacity-40"
                    style={{background:'linear-gradient(135deg,#8b5cf6,#6d28d9)'}}>
                    Confirmer le RDV
                  </button>
                </div>
              )}

              {getRdvsForDay(selectedDay).length === 0 && !showAddForm && (
                <p className="text-xs text-center py-2" style={{color:th.textMuted}}>Aucun RDV ce jour</p>
              )}
              <div className="space-y-1.5">
                {getRdvsForDay(selectedDay).map(r => (
                  <div key={r.id} className="flex items-center justify-between px-3 py-2 rounded-xl"
                    style={{background:statutColors[r.statut]?.bg||'rgba(139,92,246,0.1)',border:`1px solid ${statutColors[r.statut]?.color||'#a78bfa'}33`}}>
                    <div>
                      <p className="text-xs font-bold" style={{color:th.text}}>{r.heure} — {r.client}</p>
                      {r.phone && <p className="text-xs" style={{color:th.textMuted}}>{r.phone}</p>}
                      {r.note && <p className="text-xs italic" style={{color:th.textSub}}>{r.note}</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      <select value={r.statut} onChange={e=>updateStatut(r.id, e.target.value)}
                        className="text-xs px-2 py-1 rounded-lg focus:outline-none"
                        style={{background:th.inputBg,color:statutColors[r.statut]?.color||'#a78bfa',border:'none'}}>
                        {Object.entries(statutColors).map(([k,v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                      <button onClick={() => deleteRdv(r.id)} className="w-6 h-6 rounded flex items-center justify-center" style={{color:th.textMuted}}>
                        <MdDelete size={13}/>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── VUE LISTE ─── */}
      {activeView === 'list' && (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {upcomingRdvs.length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm" style={{color:th.textSub}}>Aucun RDV à venir</p>
              <button onClick={() => setActiveView('calendar')} className="text-purple-400 text-xs mt-1">
                Ajouter depuis le calendrier →
              </button>
            </div>
          )}
          {upcomingRdvs.map(r => (
            <div key={r.id} className="flex items-center justify-between px-4 py-3 rounded-xl"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`}}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex flex-col items-center justify-center flex-shrink-0"
                  style={{background:'rgba(139,92,246,0.1)',border:'1px solid rgba(139,92,246,0.2)'}}>
                  <p className="text-xs font-bold leading-none" style={{color:'#a78bfa'}}>
                    {new Date(r.date).toLocaleDateString('fr-FR',{day:'2-digit'})}
                  </p>
                  <p className="text-xs leading-none" style={{color:th.textMuted,fontSize:'9px'}}>
                    {MONTHS_FR[new Date(r.date+'T00:00').getMonth()]?.slice(0,3)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{color:th.text}}>{r.client}</p>
                  <p className="text-xs" style={{color:th.textSub}}>{r.heure} • {r.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 rounded-full font-medium"
                  style={{background:statutColors[r.statut]?.bg,color:statutColors[r.statut]?.color}}>
                  {statutColors[r.statut]?.label}
                </span>
                <button onClick={() => deleteRdv(r.id)} className="w-6 h-6 rounded flex items-center justify-center" style={{color:th.textMuted}}>
                  <MdDelete size={13}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── VUE CONFIG ─── */}
      {activeView === 'config' && (
        <div className="space-y-4">
          <div className="px-4 py-3 rounded-xl" style={{background:'rgba(139,92,246,0.08)',border:'1px solid rgba(139,92,246,0.2)'}}>
            <p className="text-xs font-semibold mb-1" style={{color:'#a78bfa'}}>⚙️ Config pour : {business?.sector}</p>
            <p className="text-xs" style={{color:th.textSub}}>Durée par défaut : {sectorInfo.dureeDefaut} min • Type : {sectorInfo.label}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Heure ouverture</label>
              <input type="time" value={horaireDebut} onChange={e=>setHoraireDebut(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none"
                style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Heure fermeture</label>
              <input type="time" value={horaireFin} onChange={e=>setHoraireFin(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none"
                style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold mb-2 block" style={{color:th.textSub}}>
              Durée par RDV : <span style={{color:'#a78bfa'}}>{duree} min</span>
            </label>
            <input type="range" min="15" max="180" step="15" value={duree} onChange={e=>setDuree(parseInt(e.target.value))}
              className="w-full" style={{accentColor:'#8b5cf6'}}/>
            <div className="flex justify-between text-xs mt-1" style={{color:th.textMuted}}>
              <span>15 min</span><span>1h</span><span>3h</span>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold mb-2 block" style={{color:th.textSub}}>Jours ouvrables</label>
            <div className="flex gap-1.5">
              {DAYS_FR.map((d, i) => (
                <button key={i} onClick={() => {
                    if (joursActifs.includes(i)) {
                      setJoursActifs(joursActifs.filter(j => j !== i))
                    } else {
                      setJoursActifs([...joursActifs, i].sort())
                    }
                  }}
                  className="flex-1 py-2 rounded-xl text-xs font-semibold transition-all"
                  style={{
                    background: joursActifs.includes(i) ? 'rgba(139,92,246,0.2)' : th.inputBg,
                    color: joursActifs.includes(i) ? '#a78bfa' : th.textMuted,
                    border: `1px solid ${joursActifs.includes(i) ? 'rgba(139,92,246,0.4)' : th.inputBorder}`
                  }}>
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold mb-2 block" style={{color:th.textSub}}>Message rappel WhatsApp</label>
            <textarea value={sectorInfo.rappel} readOnly rows={3}
              className="w-full px-4 py-2.5 rounded-xl text-xs resize-none focus:outline-none opacity-60"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            <p className="text-xs mt-1" style={{color:th.textMuted}}>Envoyé automatiquement 24h avant chaque RDV</p>
          </div>
        </div>
      )}

      <button onClick={save} disabled={saving}
        className="w-full mt-4 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2"
        style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>
        {saving ? 'Sauvegarde...' : <><MdCheck size={16}/>Sauvegarder</>}
      </button>
    </Modal>
  )
}


// ─── MODULE CATALOGUE ───
// ─── MODULE CATALOGUE AVEC PHOTOS ───

// ── Adapter le Devis selon le secteur ──
const getDevisConfig = (sector = '') => {
  const s = sector.toLowerCase()
  if (s.includes('clinique') || s.includes('santé') || s.includes('médecin') || s.includes('pharma'))
    return { title: 'Tarifs & Actes Médicaux', itemLabel: 'acte', labelPlural: 'Actes médicaux', labelSelected: 'Sélectionnés', tabGrille: 'Tarifs', tabAdd: 'Ajouter acte', tabPDF: 'Générer facture' }
  if (s.includes('immo') || s.includes('btp') || s.includes('terrain'))
    return { title: 'Estimation Immobilière', itemLabel: 'prestation', labelPlural: 'Prestations', labelSelected: 'Sélectionnées', tabGrille: 'Grille', tabAdd: 'Ajouter', tabPDF: 'Générer estimation' }
  if (s.includes('restaurant') || s.includes('traiteur') || s.includes('food'))
    return { title: 'Bon de Commande', itemLabel: 'plat', labelPlural: 'Plats', labelSelected: 'Commandés', tabGrille: 'Menu', tabAdd: 'Ajouter plat', tabPDF: 'Générer bon' }
  if (s.includes('transport') || s.includes('logistique'))
    return { title: 'Grille Tarifaire', itemLabel: 'trajet', labelPlural: 'Trajets', labelSelected: 'Sélectionnés', tabGrille: 'Tarifs', tabAdd: 'Ajouter trajet', tabPDF: 'Générer devis' }
  if (s.includes('juridique') || s.includes('avocat') || s.includes('notaire'))
    return { title: 'Honoraires & Services', itemLabel: 'service', labelPlural: 'Services', labelSelected: 'Sélectionnés', tabGrille: 'Honoraires', tabAdd: 'Ajouter service', tabPDF: 'Générer convention' }
  if (s.includes('éducation') || s.includes('formation') || s.includes('école'))
    return { title: 'Frais de Formation', itemLabel: 'formation', labelPlural: 'Formations', labelSelected: 'Sélectionnées', tabGrille: 'Formations', tabAdd: 'Ajouter formation', tabPDF: 'Générer reçu' }
  if (s.includes('event') || s.includes('événement') || s.includes('mariage'))
    return { title: 'Devis Événementiel', itemLabel: 'prestation', labelPlural: 'Prestations', labelSelected: 'Sélectionnées', tabGrille: 'Prestations', tabAdd: 'Ajouter prestation', tabPDF: 'Générer devis' }
  return { title: 'Devis Automatique', itemLabel: 'prestation', labelPlural: 'Prestations', labelSelected: 'Sélectionnées', tabGrille: 'Grille', tabAdd: 'Ajouter', tabPDF: 'Générer PDF' }
}

// ── Adapter les Leads selon le secteur ──
const getLeadsConfig = (sector = '') => {
  const s = sector.toLowerCase()
  if (s.includes('clinique') || s.includes('santé') || s.includes('médecin') || s.includes('pharma'))
    return { title: 'Collecte Patients', itemLabel: 'patient', labelPlural: 'Patients', labelCollected: 'Patients collectés', labelWeek: 'Cette semaine', tabConfig: 'Configuration', tabLeads: 'Patients' }
  if (s.includes('immo') || s.includes('btp') || s.includes('terrain'))
    return { title: 'Collecte Prospects', itemLabel: 'prospect', labelPlural: 'Prospects', labelCollected: 'Prospects collectés', labelWeek: 'Cette semaine', tabConfig: 'Configuration', tabLeads: 'Prospects' }
  if (s.includes('restaurant') || s.includes('traiteur') || s.includes('food'))
    return { title: 'Collecte Clients', itemLabel: 'client', labelPlural: 'Clients', labelCollected: 'Clients collectés', labelWeek: 'Cette semaine', tabConfig: 'Configuration', tabLeads: 'Clients' }
  if (s.includes('éducation') || s.includes('formation') || s.includes('école'))
    return { title: 'Collecte Apprenants', itemLabel: 'apprenant', labelPlural: 'Apprenants', labelCollected: 'Apprenants collectés', labelWeek: 'Cette semaine', tabConfig: 'Configuration', tabLeads: 'Apprenants' }
  if (s.includes('juridique') || s.includes('avocat') || s.includes('notaire'))
    return { title: 'Collecte Clients', itemLabel: 'client', labelPlural: 'Clients', labelCollected: 'Clients collectés', labelWeek: 'Cette semaine', tabConfig: 'Configuration', tabLeads: 'Clients' }
  if (s.includes('tourisme') || s.includes('hôtel') || s.includes('voyage'))
    return { title: 'Collecte Voyageurs', itemLabel: 'voyageur', labelPlural: 'Voyageurs', labelCollected: 'Voyageurs collectés', labelWeek: 'Cette semaine', tabConfig: 'Configuration', tabLeads: 'Voyageurs' }
  return { title: 'Collecte de Leads', itemLabel: 'lead', labelPlural: 'Leads', labelCollected: 'Leads collectés', labelWeek: 'Cette semaine', tabConfig: 'Configuration', tabLeads: 'Leads' }
}

// ── Adapter le nom du Catalogue selon le secteur ──
const getCatalogConfig = (sector = '') => {
  const s = sector.toLowerCase()
  if (s.includes('clinique') || s.includes('santé') || s.includes('médecin') || s.includes('pharma'))
    return { title: 'Nos Prestations', itemLabel: 'prestation', labelPlural: 'Prestations', labelStock: 'Disponibles', labelRupture: 'Indisponibles', priceSuffix: 'FCFA', fields: { name: 'Nom de la prestation', price: 'Tarif (FCFA)', desc: 'Description', stock: 'Durée / Conditions' } }
  if (s.includes('immo') || s.includes('btp') || s.includes('terrain'))
    return { title: 'Nos Biens Disponibles', itemLabel: 'bien', labelPlural: 'Biens', labelStock: 'Disponibles', labelRupture: 'Vendus', priceSuffix: 'FCFA', fields: { name: 'Titre du bien', price: 'Prix (FCFA)', desc: 'Description', stock: 'Superficie / Localisation' } }
  if (s.includes('restaurant') || s.includes('traiteur') || s.includes('food') || s.includes('boulangerie'))
    return { title: 'Notre Menu', itemLabel: 'plat', labelPlural: 'Plats', labelStock: 'Disponibles', labelRupture: 'Épuisés', priceSuffix: 'FCFA', fields: { name: 'Nom du plat', price: 'Prix (FCFA)', desc: 'Ingrédients / Description', stock: 'Disponibilité' } }
  if (s.includes('tourisme') || s.includes('hôtel') || s.includes('hotel') || s.includes('voyage'))
    return { title: 'Nos Offres & Séjours', itemLabel: 'offre', labelPlural: 'Offres', labelStock: 'Disponibles', labelRupture: 'Complètes', priceSuffix: 'FCFA', fields: { name: 'Nom du séjour', price: 'Prix (FCFA)', desc: 'Inclus / Description', stock: 'Disponibilité / Places' } }
  if (s.includes('juridique') || s.includes('avocat') || s.includes('conseil') || s.includes('notaire'))
    return { title: 'Nos Services', itemLabel: 'service', labelPlural: 'Services', labelStock: 'Actifs', labelRupture: 'Inactifs', priceSuffix: 'FCFA', fields: { name: 'Type de service', price: 'Honoraires (FCFA)', desc: 'Description', stock: 'Délai' } }
  if (s.includes('transport') || s.includes('logistique') || s.includes('livraison'))
    return { title: 'Nos Tarifs de Transport', itemLabel: 'trajet', labelPlural: 'Trajets', labelStock: 'Disponibles', labelRupture: 'Indisponibles', priceSuffix: 'FCFA', fields: { name: 'Trajet / Service', price: 'Tarif (FCFA)', desc: 'Description', stock: 'Disponibilité' } }
  if (s.includes('event') || s.includes('événement') || s.includes('mariage'))
    return { title: 'Nos Prestations Événementielles', itemLabel: 'prestation', labelPlural: 'Prestations', labelStock: 'Disponibles', labelRupture: 'Indisponibles', priceSuffix: 'FCFA', fields: { name: 'Nom de la prestation', price: 'Prix (FCFA)', desc: 'Description', stock: 'Capacité / Conditions' } }
  if (s.includes('éducation') || s.includes('formation') || s.includes('école'))
    return { title: 'Nos Formations', itemLabel: 'formation', labelPlural: 'Formations', labelStock: 'Ouvertes', labelRupture: 'Fermées', priceSuffix: 'FCFA', fields: { name: 'Nom de la formation', price: 'Frais (FCFA)', desc: 'Programme / Description', stock: 'Durée / Places' } }
  // Default — commerce/boutique
  return { title: 'Catalogue Produits', itemLabel: 'produit', labelPlural: 'Produits', labelStock: 'En stock', labelRupture: 'Rupture', priceSuffix: 'FCFA', fields: { name: 'Nom du produit', price: 'Prix (FCFA)', desc: 'Description', stock: 'Stock disponible' } }
}

const CatalogModal = ({ businessId, business, onClose, isDark }) => {
  const th = getTheme(isDark)
  const ph = getPlaceholders(business?.sector)
  const catalogConfig = getCatalogConfig(business?.sector)
  const [products, setProducts] = useState([])
  const [name, setName] = useState('')
  const [price, setPrice] = useState('')
  const [desc, setDesc] = useState('')
  const [stock, setStock] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [publicId, setPublicId] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [editId, setEditId] = useState(null)
  const [editData, setEditData] = useState({})
  const [searchDevis, setSearchDevis] = useState('') // eslint-disable-line no-unused-vars
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('list')
  const [searchP, setSearchP] = useState('')
  const fileInputRef = useRef(null)

  useEffect(() => {
    axios.get(`${API_URL}/api/businesses/${businessId}/modules`)
      .then(r => setProducts(r.data.catalog || []))
      .catch(() => {}).finally(() => setLoading(false))
  }, [businessId])

  // Upload image vers Cloudinary via backend
  const handleImageUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      alert('Image trop lourde. Maximum 5MB.')
      return
    }
    setUploading(true)
    setUploadProgress(0)
    try {
      const formData = new FormData()
      formData.append('image', file)
      const res = await axios.post(
        `${API_URL}/api/upload/catalog/${businessId}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            setUploadProgress(Math.round((e.loaded * 100) / e.total))
          }
        }
      )
      setImageUrl(res.data.url)
      setPublicId(res.data.publicId)
      console.log(' Image uploadée:', res.data.url)
    } catch (e) {
      console.error('Upload error:', e)
      alert('Erreur upload. Réessayez.')
    } finally { setUploading(false) }
  }

  const add = () => {
    if (!name || !price) return
    const newProduct = {
      id: genId(),
      name: name.trim(),
      price: price.trim(),
      description: desc.trim(),
      stock: stock.trim(),
      imageUrl: imageUrl || '',
      publicId: publicId || '',
      inStock: true
    }
    console.log('Ajout produit avec imageUrl:', newProduct.imageUrl)
    setProducts(prev => [...prev, newProduct])
    setName(''); setPrice(''); setDesc(''); setStock('')
    setImageUrl(''); setPublicId('')
    setActiveTab('list')
  }

  const del = (id) => setProducts(products.filter(p => p.id !== id))
  const toggleStock = (id) => setProducts(products.map(p => p.id === id ? {...p, inStock: !p.inStock} : p))
  const startEdit = (p) => { setEditId(p.id); setEditData({name:p.name, price:p.price, description:p.description||'', stock:p.stock||'', imageUrl:p.imageUrl||''}) }
  const saveEdit = () => { setProducts(products.map(p => p.id === editId ? {...p,...editData} : p)); setEditId(null) }
  const save = async () => {
    setSaving(true)
    try { await axios.put(`${API_URL}/api/businesses/${businessId}/modules/catalog`, { data: products }); onClose() }
    catch (e) { console.error(e) } finally { setSaving(false) }
  }

  const filtered = products.filter(p => !searchP || p.name.toLowerCase().includes(searchP.toLowerCase()))
  const inStockCount = products.filter(p => p.inStock !== false).length

  // Import JSON (Sari Compta, WooCommerce, etc.)
  const importCatalogJSON = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    try {
      const text = await file.text()
      const data = JSON.parse(text)
      // Supporter plusieurs formats JSON
      let items = []
      if (Array.isArray(data)) items = data
      else if (data.products) items = data.products
      else if (data.items) items = data.items
      else if (data.catalogue) items = data.catalogue
      else if (data.data) items = data.data
      else { alert('Format JSON non reconnu. Consultez le modèle.'); return }

      const newProds = items.filter(i => i).map(i => ({
        id: Date.now().toString(36) + Math.random().toString(36).slice(2),
        name: (i.name || i.nom || i.designation || i.libelle || i.article || '').toString().trim(),
        price: (i.price || i.prix || i.tarif || i.montant || '0').toString().split(' ').join(''),
        description: (i.description || i.desc || i.details || '').toString().trim(),
        stock: (i.stock || i.quantite || i.qty || '').toString(),
        inStock: i.inStock !== false && i.actif !== false && i.active !== false,
        imageUrl: i.imageUrl || i.image || i.photo || '',
      })).filter(p => p.name)

      if (newProds.length === 0) { alert(`Aucun ${catalogConfig.itemLabel || 'produit'} valide trouvé dans le fichier JSON`); return }
      if (!window.confirm(`Importer ${newProds.length} ${catalogConfig.itemLabel || 'produit'}(s) depuis JSON ?`)) return
      setProducts(prev => [...prev, ...newProds])
      alert(`✅ ${newProds.length} ${catalogConfig.itemLabel || 'produit'}(s) importés !`)
    } catch (err) {
      alert('Erreur JSON: ' + err.message + '\n\nVérifiez que le fichier est un JSON valide.')
    }
    e.target.value = ''
  }

  // Télécharger un modèle Excel adapté au secteur
  const downloadTemplate = async () => {
    try {
      const XLSX = await import('xlsx').then(m => m.default || m)
      const config = getCatalogConfig(business?.sector)
      const headers = [config.fields.name, `${config.fields.price} (FCFA)`, config.fields.desc, config.fields.stock, 'En stock (Oui/Non)', 'URL Image']
      const example = [
        [`Exemple ${config.itemLabel}`, '15000', 'Description exemple', '10', 'Oui', ''],
        [`Autre ${config.itemLabel}`, '25000', 'Autre description', '5', 'Oui', ''],
      ]
      const ws = XLSX.utils.aoa_to_sheet([headers, ...example])
      ws['!cols'] = [{wch:35},{wch:15},{wch:40},{wch:20},{wch:15},{wch:30}]
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, config.title)
      XLSX.writeFile(wb, `modele-${config.itemLabel}-${business?.name || 'catalogue'}.xlsx`)
    } catch(e) { alert('Erreur: ' + e.message) }
  }

  const exportCatalogXLSX = async () => {
    if (products.length === 0) { alert(`Aucun ${catalogConfig.itemLabel || 'produit'} à exporter`); return }
    try {
      const XLSX = await import('xlsx').then(m => m.default || m)
      const headers = ['Nom', 'Prix (FCFA)', 'Description', 'Stock', 'En stock', 'Stock minimum']
      const rows = products.map(p => [p.name||'', p.price||'', p.description||'', p.stock||0, p.inStock!==false?'Oui':'Non', p.stockMin||0])
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
      ws['!cols'] = [{wch:30},{wch:15},{wch:40},{wch:10},{wch:10},{wch:15}]
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Catalogue')
      XLSX.writeFile(wb, 'catalogue-' + Date.now() + '.xlsx')
    } catch(e) { alert('Erreur export: ' + e.message) }
  }

  const importCatalogXLSX = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    try {
      const XLSX = await import('xlsx').then(m => m.default || m)
      const buffer = await file.arrayBuffer()
      const wb = XLSX.read(buffer)
      const ws = wb.Sheets[wb.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1 })
      if (rows.length < 2) { alert('Fichier vide'); return }
      const newProds = rows.slice(1).filter(r => r[0]).map(r => ({
        id: Date.now().toString(36) + Math.random().toString(36).slice(2),
        name: (r[0]||'').toString().trim(),
        price: (r[1]||'0').toString().replace(/\s/g,''),
        description: (r[2]||'').toString().trim(),
        stock: parseInt(r[3])||0,
        inStock: (r[4]||'').toString().toLowerCase() !== 'non',
        stockMin: parseInt(r[5])||0,
        imageUrl: '',
      }))
      if (newProds.length === 0) { alert(`Aucun ${catalogConfig.itemLabel || 'produit'} valide`); return }
      if (!window.confirm(`Importer ${newProds.length} ${catalogConfig.itemLabel || 'produit'}(s) ?`)) return
      const merged = [...products, ...newProds]
      setProducts(merged)
      await axios.put(API_URL + '/api/businesses/' + businessId + '/modules', { catalog: merged })
      alert(`✅ ${newProds.length} ${catalogConfig.itemLabel || 'produit'}(s) importés !`)
    } catch(e) { alert('Erreur import: ' + e.message) }
    e.target.value = ''
  }


  return (
    <Modal title={`Module ${catalogConfig.title}`} moduleId="catalog" icon={<MdShoppingCart size={20} className="text-white"/>} color="#f59e0b" onClose={onClose} wide isDark={isDark}>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { label: catalogConfig.labelPlural || 'Produits', value: products.length, color:'#fbbf24', bg:'rgba(245,158,11,0.12)', border:'rgba(245,158,11,0.25)' },
          { label: catalogConfig.labelStock || 'En stock', value: inStockCount, color:'#34d399', bg:'rgba(16,185,129,0.12)', border:'rgba(16,185,129,0.25)' },
          { label: catalogConfig.labelRupture || 'Rupture', value: products.length - inStockCount, color:'#f87171', bg:'rgba(239,68,68,0.12)', border:'rgba(239,68,68,0.25)' },
        ].map((s,i) => (
          <div key={i} className="rounded-xl p-3 text-center" style={{background:s.bg,border:`1px solid ${s.border}`}}>
            <p className="text-xl font-bold" style={{color:s.color}}>{s.value}</p>
            <p className="text-xs" style={{color:th.textMuted}}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{background:th.inputBg}}>
        <div className="flex gap-2 mb-3">
          <button onClick={exportCatalogXLSX}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold"
            style={{background:'rgba(16,185,129,0.15)',color:'#10b981',border:'1px solid rgba(16,185,129,0.2)'}}>
             Exporter Excel
          </button>
          <label className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer"
            style={{background:'rgba(59,130,246,0.15)',color:'#3b82f6',border:'1px solid rgba(59,130,246,0.2)'}}>
            Importer Excel
            <input type="file" accept=".xlsx,.xls,.csv" onChange={importCatalogXLSX} className="hidden"/>
          </label>
          <label className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer"
            style={{background:'rgba(139,92,246,0.15)',color:'#a78bfa',border:'1px solid rgba(139,92,246,0.2)'}}>
            Importer JSON
            <input type="file" accept=".json" onChange={importCatalogJSON} className="hidden"/>
          </label>
          <button onClick={downloadTemplate}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold"
            style={{background:'rgba(16,185,129,0.15)',color:'#10b981',border:'1px solid rgba(16,185,129,0.2)'}}>
            Modèle Excel
          </button>
          {/* Tabs dans le même flex */}
          {[
            {id:'list', label:`${catalogConfig.labelPlural || catalogConfig.title} (${products.length})`},
            {id:'add', label:`+ Ajouter ${catalogConfig.itemLabel || 'produit'}`},
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className="rounded-lg text-xs font-bold transition-all whitespace-nowrap"
              style={{
                padding:'7px 14px',
                background: activeTab===tab.id ? 'linear-gradient(135deg,#f59e0b,#d97706)' : 'transparent',
                color: activeTab===tab.id ? 'white' : th.textSub,
                border: activeTab===tab.id ? '1px solid transparent' : `1px solid ${th.cardBorder}`,
              }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Liste */}
      {activeTab === 'list' && (
        <div>
          <input value={searchP} onChange={e => setSearchP(e.target.value)}
            placeholder={`Rechercher un${catalogConfig.itemLabel ? " "+catalogConfig.itemLabel : " produit"}...`}
            className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none mb-3"
            style={{background:th.inputBg, border:`1px solid ${th.inputBorder}`, color:th.text}}/>

          {loading ? <div className="text-center py-4" style={{color:th.textSub}}>Chargement...</div>
          : filtered.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm mb-2" style={{color:th.textSub}}>Aucun {catalogConfig.itemLabel || "produit"}</p>
              <button onClick={() => setActiveTab('add')} className="text-yellow-400 text-xs">Ajouter un {catalogConfig.itemLabel || "produit"} →</button>
            </div>
          ) : (
            <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
              {filtered.map(p => (
                <div key={p.id} className="rounded-xl overflow-hidden" style={{border:`1px solid ${th.inputBorder}`}}>
                  {editId === p.id ? (
                    <div className="p-4 space-y-2" style={{background:th.inputBg}}>
                      <div className="flex gap-2">
                        <input value={editData.name} onChange={e=>setEditData({...editData,name:e.target.value})}
                          className="flex-1 px-3 py-2 rounded-lg text-sm focus:outline-none" style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}} placeholder="Nom"/>
                        <input value={editData.price} onChange={e=>setEditData({...editData,price:e.target.value})}
                          className="w-28 px-3 py-2 rounded-lg text-sm focus:outline-none" style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}} placeholder="Prix FCFA"/>
                      </div>
                      <input value={editData.description} onChange={e=>setEditData({...editData,description:e.target.value})}
                        className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none" style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}} placeholder={ph.description||"Description"}/>
                      <input value={editData.stock} onChange={e=>setEditData({...editData,stock:e.target.value})}
                        className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none" style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}} placeholder="Stock (ex: 15 unités)"/>
                      <input value={editData.stockMin||''} onChange={e=>setEditData({...editData,stockMin:e.target.value})}
                        className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none" style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}} placeholder=" Seuil d'alerte (ex: 5 unités)"/>
                      {/* Aperçu image en édition */}
                      {editData.imageUrl && (
                        <div className="relative w-full h-28 rounded-xl overflow-hidden">
                          <img src={editData.imageUrl} alt={catalogConfig.itemLabel || 'produit'} className="w-full h-full object-cover"/>
                          <button onClick={()=>setEditData({...editData,imageUrl:'',publicId:''})}
                            className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs"
                            style={{background:'rgba(239,68,68,0.8)'}}>✕</button>
                        </div>
                      )}
                      <input value={editData.imageUrl} onChange={e=>setEditData({...editData,imageUrl:e.target.value})}
                        className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none" style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}} placeholder="URL image (ou uploadez une nouvelle)"/>
                      <div className="flex gap-2">
                        <button onClick={saveEdit} className="flex-1 py-1.5 rounded-lg text-xs text-white font-semibold" style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>Sauvegarder</button>
                        <button onClick={()=>setEditId(null)} className="flex-1 py-1.5 rounded-lg text-xs" style={{background:th.inputBg,color:th.textSub}}>Annuler</button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4" style={{background:th.inputBg}}>
                      {/* Image si présente */}
                      {p.imageUrl && (
                        <div className="w-full h-32 rounded-xl overflow-hidden mb-3">
                          <img src={p.imageUrl} alt={p.name} className="w-full h-full object-cover"/>
                        </div>
                      )}
                      {/* Infos — liste verticale */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="text-sm font-bold" style={{color:th.text}}>{p.name}</p>
                          <span className="text-sm font-bold flex-shrink-0 px-2 py-0.5 rounded-lg" style={{color:'#fbbf24',background:'rgba(251,191,36,0.1)'}}>{Number(p.price).toLocaleString('fr-FR')} FCFA</span>
                        </div>
                        {p.description && <p className="text-xs mb-1" style={{color:th.textSub,lineHeight:'1.5'}}>{p.description}</p>}
                        {p.stock && <p className="text-xs" style={{color:th.textMuted}}>📦 {p.stock}</p>}
                        <div className="flex items-center gap-2 mt-1.5">
                          {/* Badge stock */}
                          <button onClick={() => toggleStock(p.id)}
                            className="text-xs px-2 py-0.5 rounded-full font-medium transition-all"
                            style={{
                              background: p.inStock!==false ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)',
                              color: p.inStock!==false ? '#34d399' : '#f87171'
                            }}>
                            {p.inStock!==false ? '✓ En stock' : '✗ Rupture'}
                          </button>
                          {p.stock && <span className="text-xs" style={{color:th.textMuted}}>{p.stock}</span>}
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => startEdit(p)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                          style={{background:'rgba(59,130,246,0.1)',color:'#60a5fa',border:'1px solid rgba(59,130,246,0.2)'}}>
                          <MdEdit size={13}/> Modifier
                        </button>
                        <button onClick={() => del(p.id)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                          style={{background:'rgba(239,68,68,0.1)',color:'#f87171',border:'1px solid rgba(239,68,68,0.2)'}}>
                          <MdDelete size={13}/> Supprimer
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab Ajouter */}
      {activeTab === 'add' && (
        <div className="space-y-3">
          {/* Zone upload photo */}
          <div>
            <label className="text-sm font-semibold mb-2 block" style={{color:th.text}}>Photo du {catalogConfig.itemLabel || 'produit'}</label>
            <div
              onClick={() => !uploading && fileInputRef.current?.click()}
              className="w-full h-36 rounded-xl flex flex-col items-center justify-center cursor-pointer transition-all relative overflow-hidden"
              style={{border:`2px dashed ${imageUrl?'rgba(16,185,129,0.5)':th.inputBorder}`, background:imageUrl?'transparent':th.inputBg}}>
              {imageUrl ? (
                <>
                  <img src={imageUrl} alt="preview" className="w-full h-full object-cover"/>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                    style={{background:'rgba(0,0,0,0.5)'}}>
                    <p className="text-white text-xs font-semibold">Changer la photo</p>
                  </div>
                  <button onClick={(e)=>{e.stopPropagation();setImageUrl('')}}
                    className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-white"
                    style={{background:'rgba(239,68,68,0.8)'}}>
                    <MdClose size={14}/>
                  </button>
                </>
              ) : uploading ? (
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full border-4 border-yellow-400 border-t-transparent animate-spin mx-auto mb-2"/>
                  <p className="text-sm font-semibold" style={{color:'#fbbf24'}}>{uploadProgress}%</p>
                  <p className="text-xs" style={{color:th.textSub}}>Upload en cours...</p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2" style={{background:'rgba(245,158,11,0.15)'}}>
                    <MdUpload size={24} style={{color:'#fbbf24'}}/>
                  </div>
                  <p className="text-sm font-semibold" style={{color:th.text}}>Ajouter une photo</p>
                  <p className="text-xs" style={{color:th.textSub}}>JPG, PNG, WebP — max 5MB</p>
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden"/>
          </div>

          {/* Champs {catalogConfig.itemLabel || 'produit'} */}
          <div className="flex gap-2">
            <input value={name} onChange={e=>setName(toTitleCase(e.target.value))} placeholder={`${catalogConfig.fields.name} *`}
              className="flex-1 px-4 py-2.5 rounded-xl text-sm focus:outline-none" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            <input value={formatNumber(price)} onChange={e=>setPrice(parseNumber(e.target.value))}
              placeholder="Prix FCFA *" inputMode="numeric"
              className="w-28 px-4 py-2.5 rounded-xl text-sm focus:outline-none" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          </div>
          <input value={desc} onChange={e=>setDesc(toSentenceCase(e.target.value))} placeholder={catalogConfig.fields.desc}
            className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          <input value={formatNumber(stock)} onChange={e=>setStock(parseNumber(e.target.value))}
            placeholder={catalogConfig.fields.stock} inputMode="numeric"
            className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>

          <button onClick={add} disabled={!name||!price||uploading}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 flex items-center justify-center gap-2"
            style={{background:'linear-gradient(135deg,#f59e0b,#d97706)'}}>
            <MdAdd size={16}/>Ajouter au catalogue
          </button>
        </div>
      )}

      <button onClick={save} disabled={saving}
        className="w-full mt-4 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2"
        style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>
        {saving?'Sauvegarde...':<><MdCheck size={16}/>Sauvegarder ({products.length} {catalogConfig.itemLabel || 'produit'}s)</> }
      </button>
    </Modal>
  )
}


// ─── MODULE LEADS ───
// ─── MODULE LEADS AMÉLIORÉ ───
const QUESTION_TYPES = [
  { id: 'text', label: 'Texte libre', icon: '' },
  { id: 'choice', label: 'Choix multiple', icon: '☑️' },
  { id: 'yesno', label: 'Oui / Non', icon: '' },
  { id: 'phone', label: 'Numéro de téléphone', icon: '' },
  { id: 'number', label: 'Nombre', icon: '' },
]

const LeadsModal = ({ businessId, business, onClose, isDark }) => {
  const th = getTheme(isDark)
  const leadsConfig = getLeadsConfig(business?.sector)
  const [questions, setQuestions] = useState([])
  const [leads, setLeads] = useState([])
  const [newQ, setNewQ] = useState('')
  const [searchLeads, setSearchLeads] = useState('')
  const [newType, setNewType] = useState('text')
  const [newChoices, setNewChoices] = useState('')
  const [editId, setEditId] = useState(null)
  const [editData, setEditData] = useState({})
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('questions')
  const [dragOver, setDragOver] = useState(null)
  const [dragItem, setDragItem] = useState(null)

  useEffect(() => {
    axios.get(`${API_URL}/api/businesses/${businessId}/modules`)
      .then(r => {
        setQuestions(r.data.leads || [])
        setLeads(r.data.collectedLeads || [])
      })
      .catch(() => {}).finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const [searchLead, setSearchLead] = useState('')
  const [generatingLeads, setGeneratingLeads] = useState(false)

  const add = () => {
    if (!newQ) return
    const q = {
      id: genId(),
      question: toSentenceCase(newQ),
      type: newType,
      choices: newType === 'choice' ? newChoices.split(',').map(c => c.trim()).filter(Boolean) : [],
      required: true,
    }
    setQuestions(prev => [...prev, q])
    setNewQ(''); setNewChoices(''); setNewType('text')
  }

  const generateLeadsWithAI = async () => {
    setGeneratingLeads(true)
    try {
      const storedLang = localStorage.getItem('lang') || 'fr'
      const res = await axios.post(`${API_URL}/api/generate/faq/${businessId}`, {
        language: storedLang,
        type: 'leads',
        sector: business?.sector || ''
      })
      // Convertir en questions leads
      const generated = (res.data.faqs || []).slice(0,6).map(f => ({
        id: genId(),
        question: f.question,
        type: 'text',
        choices: [],
        required: true,
        generated: true
      }))
      setQuestions(prev => {
        const existing = prev.map(q => q.question.toLowerCase())
        return [...prev, ...generated.filter(g => !existing.includes(g.question.toLowerCase()))]
      })
    } catch (e) { console.error('Erreur génération leads:', e) }
    finally { setGeneratingLeads(false) }
  }

  const del = id => setQuestions(questions.filter(q => q.id !== id))
  const saveEdit = () => { setQuestions(questions.map(q => q.id === editId ? {...q,...editData} : q)); setEditId(null) }

  // Drag & Drop pour réordonner
  const handleDragStart = (id) => setDragItem(id)
  const handleDragOver = (e, id) => { e.preventDefault(); setDragOver(id) }
  const handleDrop = (targetId) => {
    if (!dragItem || dragItem === targetId) { setDragOver(null); return }
    const from = questions.findIndex(q => q.id === dragItem)
    const to = questions.findIndex(q => q.id === targetId)
    const updated = [...questions]
    const [moved] = updated.splice(from, 1)
    updated.splice(to, 0, moved)
    setQuestions(updated)
    setDragItem(null); setDragOver(null)
  }

  // Export XLSX
  const exportXLSX = async () => {
    if (leads.length === 0) { alert('Aucun lead à exporter'); return }
    const XLSX = await import('xlsx').then(m => m.default || m)
    const headers = ['Date', 'Téléphone', ...questions.map(q => q.question)]
    const rows = leads.map(lead => [
      new Date(lead.createdAt).toLocaleDateString('fr-FR'),
      lead.phone,
      ...questions.map(q => lead.answers?.[q.id] || '')
    ])
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
    // Style header
    const range = XLSX.utils.decode_range(ws['!ref'])
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSX.utils.encode_cell({r:0, c:C})
      if (!ws[addr]) continue
      ws[addr].s = { font: { bold: true }, fill: { fgColor: { rgb: '3B82F6' } } }
    }
    ws['!cols'] = [{ wch: 12 }, { wch: 18 }, ...questions.map(() => ({ wch: 30 }))]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Leads')
    XLSX.writeFile(wb, `leads-${business?.name || 'export'}-${new Date().toLocaleDateString('fr-FR').replace(/\//g,'-')}.xlsx`)
  }

  const save = async () => {
    setSaving(true)
    try {
      await axios.put(`${API_URL}/api/businesses/${businessId}/modules/leads`, { data: questions })
      onClose()
    } catch (e) { console.error(e) } finally { setSaving(false) }
  }

  const typeColors = {
    text: { bg: 'rgba(59,130,246,0.15)', color: '#60a5fa' },
    choice: { bg: 'rgba(139,92,246,0.15)', color: '#a78bfa' },
    yesno: { bg: 'rgba(16,185,129,0.15)', color: '#34d399' },
    phone: { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24' },
    number: { bg: 'rgba(239,68,68,0.15)', color: '#f87171' },
  }

  return (
    <Modal title={`Module ${leadsConfig.title}`} moduleId="leads" icon={<MdPersonSearch size={20} className="text-white"/>} color="#06b6d4" onClose={onClose} wide isDark={isDark}>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { label: 'Questions', value: questions.length, color:'#22d3ee', bg:'rgba(6,182,212,0.12)', border:'rgba(6,182,212,0.25)' },
          { label: leadsConfig.labelCollected, value: leads.length, color:'#34d399', bg:'rgba(16,185,129,0.12)', border:'rgba(16,185,129,0.25)' },
          { label: leadsConfig.labelWeek, value: leads.filter(l => new Date(l.createdAt) > new Date(Date.now()-7*24*60*60*1000)).length, color:'#a78bfa', bg:'rgba(139,92,246,0.12)', border:'rgba(139,92,246,0.25)' },
        ].map((s,i) => (
          <div key={i} className="rounded-xl p-3 text-center" style={{background:s.bg,border:`1px solid ${s.border}`}}>
            <p className="text-xl font-bold" style={{color:s.color}}>{s.value}</p>
            <p className="text-xs" style={{color:th.textMuted}}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{background:th.inputBg}}>
        {[
          {id:'questions', label:`❓ Questions (${questions.length})`},
          {id:'add', label:' Ajouter'},
          {id:'leads', label:`${leadsConfig.labelPlural} (${leads.length})`},
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
            style={activeTab===tab.id?{background:'linear-gradient(135deg,#06b6d4,#0891b2)',color:'white'}:{color:th.textSub}}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Questions */}
      {activeTab === 'questions' && (
        <div>
          <div className="flex gap-2 mb-3">
            <input value={searchLead} onChange={e=>setSearchLead(e.target.value)}
              placeholder="Rechercher une question..."
              className="flex-1 px-3 py-2 rounded-xl text-xs focus:outline-none"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            <button onClick={generateLeadsWithAI} disabled={generatingLeads}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold disabled:opacity-50 flex-shrink-0"
              style={{background:'linear-gradient(135deg,#a855f7,#7c3aed)',color:'white'}}>
              {generatingLeads ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"/> : <MdAutoAwesome size={13}/>}
              IA
            </button>
          </div>
          <p className="text-xs mb-2" style={{color:th.textSub}}> Glissez-déposez pour réordonner</p>
          {loading ? <div className="text-center py-4" style={{color:th.textSub}}>Chargement...</div>
          : questions.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm mb-2" style={{color:th.textSub}}>Aucune question configurée</p>
              <button onClick={() => setActiveTab('add')} className="text-cyan-400 text-xs">Ajouter une question →</button>
            </div>
          ) : (
            <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
              {questions.filter(q => !searchLead || q.question.toLowerCase().includes(searchLead.toLowerCase())).map((q, i) => {
                const tc = typeColors[q.type] || typeColors.text
                const typeInfo = QUESTION_TYPES.find(t => t.id === q.type)
                return (
                  <div key={q.id}
                    draggable
                    onDragStart={() => handleDragStart(q.id)}
                    onDragOver={e => handleDragOver(e, q.id)}
                    onDrop={() => handleDrop(q.id)}
                    className="rounded-xl transition-all cursor-grab active:cursor-grabbing"
                    style={{
                      border: `1px solid ${dragOver===q.id?'rgba(6,182,212,0.5)':th.inputBorder}`,
                      background: dragOver===q.id?'rgba(6,182,212,0.08)':th.inputBg,
                      transform: dragOver===q.id?'scale(1.01)':'scale(1)'
                    }}>
                    {editId === q.id ? (
                      <div className="p-3 space-y-2">
                        <input value={editData.question} onChange={e=>setEditData({...editData,question:e.target.value})}
                          className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
                          style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}}/>
                        {editData.type === 'choice' && (
                          <input value={editData.choices?.join(', ')} onChange={e=>setEditData({...editData,choices:e.target.value.split(',').map(c=>c.trim())})}
                            placeholder="Option 1, Option 2, Option 3"
                            className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
                            style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}}/>
                        )}
                        <div className="flex gap-2">
                          <button onClick={saveEdit} className="flex-1 py-1.5 rounded-lg text-xs text-white font-semibold" style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>OK</button>
                          <button onClick={()=>setEditId(null)} className="flex-1 py-1.5 rounded-lg text-xs" style={{background:th.inputBg,color:th.textSub}}>Annuler</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-3">
                        <span className="text-slate-400 text-xs flex-shrink-0 cursor-grab">⠿</span>
                        <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{background:'rgba(6,182,212,0.2)',color:'#22d3ee'}}>{i+1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate" style={{color:th.text}}>{q.question}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs px-2 py-0.5 rounded-full" style={{background:tc.bg,color:tc.color}}>
                              {typeInfo?.icon} {typeInfo?.label}
                            </span>
                            {q.type === 'choice' && q.choices?.length > 0 && (
                              <span className="text-xs" style={{color:th.textMuted}}>{q.choices.join(' · ')}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <button onClick={()=>{setEditId(q.id);setEditData({question:q.question,type:q.type,choices:q.choices||[]})}}
                            className="w-7 h-7 rounded-lg flex items-center justify-center hover:text-blue-400 transition-all" style={{color:th.textSub}}><MdEdit size={14}/></button>
                          <button onClick={()=>del(q.id)}
                            className="w-7 h-7 rounded-lg flex items-center justify-center hover:text-red-400 transition-all" style={{color:th.textSub}}><MdDelete size={14}/></button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab Ajouter */}
      {activeTab === 'add' && (
        <div className="space-y-4">
          {/* Bouton IA */}
          <button onClick={async () => {
            try {
              const storedLang = localStorage.getItem('lang') || 'fr'
              const res = await axios.post(`${API_URL}/api/generate/faq/${businessId}`, {
                language: storedLang, type: 'leads', sector: business?.sector || ''
              })
              if (res.data.faqs?.[0]) { setNewQ(res.data.faqs[0].question); setNewType('text') }
            } catch(e) { console.error(e) }
          }}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
            style={{background:'linear-gradient(135deg,#a855f7,#7c3aed)'}}>
            <MdAutoAwesome size={16}/>✨ Générer une question avec l'IA
          </button>
          <div>
            <label className="text-sm font-semibold mb-2 block" style={{color:th.text}}>Type de question</label>
            <div className="grid grid-cols-5 gap-2">
              {QUESTION_TYPES.map(t => (
                <button key={t.id} onClick={() => setNewType(t.id)}
                  className="flex flex-col items-center gap-1 py-2.5 rounded-xl transition-all border-2 text-center"
                  style={{
                    background: newType===t.id?'rgba(6,182,212,0.15)':th.inputBg,
                    borderColor: newType===t.id?'#06b6d4':th.inputBorder,
                    color: newType===t.id?'#22d3ee':th.textSub
                  }}>
                  <span style={{fontSize:'18px'}}>{t.icon}</span>
                  <span className="text-xs font-medium leading-tight">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold mb-1 block" style={{color:th.text}}>Question</label>
            <input value={newQ} onChange={e=>setNewQ(e.target.value)}
              placeholder={
                newType==='text' ? "Ex: Quel est votre besoin principal ?" :
                newType==='choice' ? "Ex: Quel est votre budget ?" :
                newType==='yesno' ? "Ex: Avez-vous déjà un financement ?" :
                newType==='phone' ? "Ex: Quel est votre numéro de téléphone ?" :
                "Ex: Combien de personnes êtes-vous ?"
              }
              className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          </div>

          {newType === 'choice' && (
            <div>
              <label className="text-sm font-semibold mb-1 block" style={{color:th.text}}>Options (séparées par des virgules)</label>
              <input value={newChoices} onChange={e=>setNewChoices(e.target.value)}
                placeholder="Ex: Moins de 50M FCFA, 50M-100M FCFA, Plus de 100M FCFA"
                className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none"
                style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
              {newChoices && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {newChoices.split(',').map((c,i) => c.trim() && (
                    <span key={i} className="text-xs px-2.5 py-1 rounded-full" style={{background:'rgba(139,92,246,0.2)',color:'#a78bfa'}}>
                      {c.trim()}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {newType === 'yesno' && (
            <div className="flex gap-3">
              {['Oui ', 'Non '].map(opt => (
                <div key={opt} className="flex-1 py-2 rounded-xl text-sm text-center font-medium" style={{background:'rgba(16,185,129,0.1)',color:'#34d399',border:'1px solid rgba(16,185,129,0.3)'}}>
                  {opt}
                </div>
              ))}
            </div>
          )}

          <button onClick={add} disabled={!newQ}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 flex items-center justify-center gap-2"
            style={{background:'linear-gradient(135deg,#06b6d4,#0891b2)'}}>
            <MdAdd size={16}/>Ajouter cette question
          </button>
        </div>
      )}

      {/* Tab Leads collectés */}
      {activeTab === 'leads' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold" style={{color:th.text}}>Prospects collectés</p>
            <button onClick={exportXLSX} disabled={leads.length===0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all disabled:opacity-40"
              style={{background:'rgba(16,185,129,0.15)',color:'#34d399',border:'1px solid rgba(16,185,129,0.3)'}}>
              <MdDownload size={14}/>Exporter XLSX
            </button>
          </div>
          <input value={searchLeads} onChange={e=>setSearchLeads(e.target.value)}
            placeholder="Rechercher un lead..."
            className="w-full px-3 py-2 rounded-xl text-xs focus:outline-none mb-3"
            style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          {leads.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{background:th.inputBg}}>
                <MdPersonSearch size={24} style={{color:th.textMuted}}/>
              </div>
              <p className="text-sm" style={{color:th.textSub}}>Aucun lead collecté pour l'instant</p>
              <p className="text-xs mt-1" style={{color:th.textMuted}}>Les prospects WhatsApp apparaîtront ici</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
              {leads.map((lead, i) => (
                <div key={i} className="px-4 py-3 rounded-xl" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`}}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold" style={{color:th.text}}>{lead.phone}</p>
                    <span className="text-xs" style={{color:th.textMuted}}>{new Date(lead.createdAt).toLocaleDateString('fr-FR')}</span>
                  </div>
                  {Object.entries(lead.answers || {}).slice(0,2).map(([qId, ans]) => {
                    const q = questions.find(q => q.id === qId)
                    return q ? (
                      <p key={qId} className="text-xs mt-1" style={{color:th.textSub}}>
                        <span style={{color:th.text}}>{q.question.slice(0,30)}...</span> → {ans}
                      </p>
                    ) : null
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <button onClick={save} disabled={saving}
        className="w-full mt-4 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2"
        style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>
        {saving?'Sauvegarde...':<><MdCheck size={16}/>Sauvegarder ({questions.length} questions)</>}
      </button>
    </Modal>
  )
}


// ─── MODULE DEVIS ───
// ─── MODULE DEVIS AUTOMATIQUE ───
const getDevisCategories = (sector = '') => {
  const s = (sector || '').toLowerCase()
  if (s.includes('btp') || s.includes('construction')) return ['Main d\'œuvre','Matériaux','Équipements','Transport','Sous-traitance','Autre']
  if (s.includes('immo')) return ['Commission','Frais dossier','Diagnostics','Notaire','Gestion','Autre']
  if (s.includes('clinique') || s.includes('santé')) return ['Consultation','Analyses','Chirurgie','Médicaments','Équipements','Autre']
  if (s.includes('tourisme') || s.includes('hôtel') || s.includes('hotel') || s.includes('voyage') || s.includes('agence')) return ['Séjour','Circuit','Transfert','Hébergement','Vol','Guide','Excursion','Autre']
  if (s.includes('restaurant') || s.includes('food') || s.includes('traiteur')) return ['Menu','Traiteur','Livraison','Location salle','Boissons','Personnel','Autre']
  if (s.includes('info') || s.includes('tech') || s.includes('digital') || s.includes('web')) return ['Développement','Design','Hébergement','Maintenance','Formation','Conseil','Autre']
  if (s.includes('transport') || s.includes('logistique')) return ['Transport','Location véhicule','Livraison','Manutention','Stockage','Autre']
  if (s.includes('event') || s.includes('événement') || s.includes('organisation')) return ['Organisation','Décoration','Traiteur','Sono/Lumière','Sécurité','Location','Autre']
  return ['Services','Prestation','Conseil','Formation','Abonnement','Autre']
}
const DEVIS_CATEGORIES = getDevisCategories('')

const DevisModal = ({ businessId, business, onClose, isDark }) => {
  const th = getTheme(isDark)
  // eslint-disable-next-line no-unused-vars
  const ph = getPlaceholders(business?.sector)
  const devisConfig = getDevisConfig(business?.sector)
  const [items, setItems] = useState([])
  const [companyInfo, setCompanyInfo] = useState({})
  const [activeTab, setActiveTab] = useState('grille')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [searchDevis, setSearchDevis] = useState('') // eslint-disable-line no-unused-vars

  // Formulaire ajout
  const [critere, setCritere] = useState('')
  const [prix, setPrix] = useState('')
  const [prixMax, setPrixMax] = useState('')
  const [categorie, setCategorie] = useState('Services')
  const [conditions, setConditions] = useState('')

  // Formulaire devis client
  const [clientName, setClientName] = useState('')
  const [clientPhone, setClientPhone] = useState('')
  const [clientCountry, setClientCountry] = useState(availableCountries[0])
  const [clientAddress, setClientAddress] = useState('')
  const [selectedItems, setSelectedItems] = useState([])
  const [devisNote, setDevisNote] = useState('')
  const [tva, setTva] = useState(false)
  const [remise, setRemise] = useState(0)
  const [editId, setEditId] = useState(null)
  const [editData, setEditData] = useState({})

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/businesses/${businessId}/modules`),
      axios.get(`${API_URL}/api/businesses/${businessId}/company`),
    ]).then(([modulesRes, companyRes]) => {
      setItems(modulesRes.data.devis || [])
      setCompanyInfo(companyRes.data || {})
    }).catch(() => {}).finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const add = () => {
    if (!critere || !prix) return
    setItems(prev => [...prev, { id: genId(), critere, prix, prixMax, categorie, conditions }])
    setCritere(''); setPrix(''); setPrixMax(''); setConditions('')
    setActiveTab('grille')
  }

  const del = id => setItems(items.filter(i => i.id !== id))
  const saveEdit = () => { setItems(items.map(i => i.id === editId ? {...i,...editData} : i)); setEditId(null) }

  const toggleItem = (item) => {
    if (selectedItems.find(s => s.id === item.id)) {
      setSelectedItems(selectedItems.filter(s => s.id !== item.id))
    } else {
      setSelectedItems([...selectedItems, {...item, qty: 1}])
    }
  }

  const updateQty = (id, qty) => {
    setSelectedItems(selectedItems.map(s => s.id === id ? {...s, qty: parseInt(qty)||1} : s))
  }

  // Calculs
  const subtotal = selectedItems.reduce((acc, item) => acc + (parseFloat(item.prix.replace(/\s/g,'')) * item.qty), 0)
  const remiseAmount = subtotal * (remise / 100)
  const afterRemise = subtotal - remiseAmount
  const tvaAmount = tva ? afterRemise * 0.18 : 0
  const total = afterRemise + tvaAmount

  const formatPrice = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n)) + ' FCFA'

  // Générer numéro devis
  const devisNumber = `DEV-${new Date().getFullYear()}-${String(Math.floor(Math.random()*9000)+1000).padStart(4,'0')}`

  // Générer PDF professionnel
  const generatePDF = async () => {
    if (selectedItems.length === 0) { alert('Sélectionnez au moins une prestation'); return }
    if (!clientName) { alert('Renseignez le nom du client'); return }
    setGenerating(true)
    try {
      // jsPDF v4 — import compatible toutes versions
      let jsPDF
      let autoTableFn = null
      try {
        const mod = await import('jspdf')
        // v4: export default, v3: named export
        jsPDF = mod.jsPDF || mod.default?.jsPDF || mod.default
        if (typeof jsPDF !== 'function') throw new Error('jsPDF introuvable')
        // autotable optionnel
        try { 
          const atMod = await import('jspdf-autotable')
          autoTableFn = atMod.default || atMod.autoTable
        } catch(e) { console.log('autotable:', e.message) }
        if (!autoTableFn) autoTableFn = (d, opts) => console.log('autoTable non disponible')
      } catch(importErr) {
        console.error('Import jsPDF:', importErr)
        alert('Erreur chargement PDF: ' + importErr.message)
        setGenerating(false)
        return
      }
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
      const brandColor = companyInfo.brandColor || '#3b82f6'
      const rgb = (hex) => {
        const r = parseInt(hex.slice(1,3),16)
        const g = parseInt(hex.slice(3,5),16)
        const b = parseInt(hex.slice(5,7),16)
        return [r,g,b]
      }
      const [r,g,b] = rgb(brandColor)


      // Formater numéro de téléphone
      const formatPhone = (phone) => {
        if (!phone) return ''
        const digits = phone.replace(/\D/g, '')
        // Format sénégalais: +221 XX XXX XX XX
        if (digits.startsWith('221') && digits.length === 12) {
          return '+221 ' + digits.slice(3,5) + ' ' + digits.slice(5,8) + ' ' + digits.slice(8,10) + ' ' + digits.slice(10,12)
        }
        if (digits.length === 9) {
          return digits.slice(0,2) + ' ' + digits.slice(2,5) + ' ' + digits.slice(5,7) + ' ' + digits.slice(7,9)
        }
        return phone
      }

      // ─── ENTÊTE ───
      // Bande colorée en haut
      doc.setFillColor(r,g,b)
      doc.rect(0, 0, 210, 35, 'F')

      // Logo si disponible
      if (companyInfo.logoUrl) {
        try {
          const img = new Image()
          img.crossOrigin = 'anonymous'
          img.src = companyInfo.logoUrl
          await new Promise(resolve => { img.onload = resolve; img.onerror = resolve })
          doc.addImage(img, 'PNG', 10, 5, 40, 25, '', 'FAST')
        } catch (e) { console.log('Logo non chargé') }
      }

      // Nom entreprise dans l'entête
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(18)
      doc.setFont('helvetica', 'bold')
      const companyName = companyInfo.legalName || business?.name || 'Entreprise'
      doc.text(companyName, companyInfo.logoUrl ? 60 : 10, 15)

      if (companyInfo.legalForm) {
        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        doc.text(companyInfo.legalForm, companyInfo.logoUrl ? 60 : 10, 22)
      }

      // Numéro devis en haut droite
      doc.setFontSize(22)
      doc.setFont('helvetica', 'bold')
      doc.text('DEVIS', 200, 15, { align: 'right' })
      doc.setFontSize(11)
      doc.setFont('helvetica', 'normal')
      doc.text(devisNumber, 200, 23, { align: 'right' })
      doc.text(`Date: ${new Date().toLocaleDateString('fr-FR')}`, 200, 29, { align: 'right' })

      // ─── INFOS ENTREPRISE + CLIENT ───
      doc.setTextColor(50, 50, 50)
      let y = 45

      // Bloc entreprise (gauche)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(r,g,b)
      doc.text('DE :', 10, y)
      doc.setTextColor(50, 50, 50)
      doc.setFont('helvetica', 'normal')
      y += 5
      if (companyInfo.address) { doc.text(companyInfo.address, 10, y); y += 4 }
      if (companyInfo.phone1) { doc.text(`Tél: ${formatPhone(companyInfo.phone1)}`, 10, y); y += 4 }
      if (companyInfo.phone2) { doc.text(`Tél: ${formatPhone(companyInfo.phone2)}`, 10, y); y += 4 }
      if (companyInfo.email) { doc.text(`Email: ${companyInfo.email}`, 10, y); y += 4 }
      if (companyInfo.website) { doc.text(companyInfo.website, 10, y); y += 4 }

      // Bloc client (droite)
      let yc = 45
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(r,g,b)
      doc.text('POUR :', 120, yc)
      doc.setTextColor(50, 50, 50)
      doc.setFont('helvetica', 'bold')
      yc += 5
      doc.text(clientName, 120, yc); yc += 4
      doc.setFont('helvetica', 'normal')
      if (clientPhone) { doc.text(`Tél: ${formatPhone(clientPhone)}`, 120, yc); yc += 4 }
      if (clientAddress) { doc.text(clientAddress, 120, yc); yc += 4 }

      y = Math.max(y, yc) + 8

      // ─── TABLEAU PRESTATIONS ───
      const tableData = selectedItems.map((item, i2) => {
        const prix = parseFloat((item.prix||'0').replace(/\s/g,'')) || 0
        const total = prix * (item.qty || 1)
        return [
          i2 + 1,
          item.description || item.critere || '',
          item.qty || 1,
          prix.toLocaleString('fr-FR') + ' FCFA',
          total.toLocaleString('fr-FR') + ' FCFA',
        ]
      })

      autoTableFn(doc, {
        startY: y,
        head: [['#', 'Désignation', 'Qté', 'Prix unitaire', 'Total']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [r,g,b], textColor: [255,255,255], fontStyle: 'bold', fontSize: 10 },
        bodyStyles: { fontSize: 9, textColor: [50,50,50] },
        alternateRowStyles: { fillColor: [248,249,250] },
        columnStyles: {
          0: { cellWidth: 8, halign: 'center' },
          1: { cellWidth: 80 },
          2: { cellWidth: 12, halign: 'center' },
          3: { cellWidth: 42, halign: 'right' },
          4: { cellWidth: 42, halign: 'right' },
        },
        tableWidth: 184,
        margin: { left: 13, right: 13 },
      })

      y = doc.lastAutoTable.finalY + 5

      // ─── TOTAUX ───
      const totalsX = 120
      const totalsW = 80
      doc.setFontSize(10)

      if (remise > 0) {
        doc.setFont('helvetica', 'normal')
        doc.text(`Sous-total:`, totalsX, y)
        doc.text(formatPrice(subtotal), 200, y, { align: 'right' })
        y += 6
        doc.setTextColor(239, 68, 68)
        doc.text(`Remise (${remise}%):`, totalsX, y)
        doc.text(`- ${formatPrice(remiseAmount)}`, 200, y, { align: 'right' })
        doc.setTextColor(50, 50, 50)
        y += 6
      }

      if (tva) {
        doc.text(`HT:`, totalsX, y)
        doc.text(formatPrice(afterRemise), 200, y, { align: 'right' })
        y += 6
        doc.text(`TVA (18%):`, totalsX, y)
        doc.text(formatPrice(tvaAmount), 200, y, { align: 'right' })
        y += 6
      }

      // Total final
      doc.setFillColor(r,g,b)
      doc.rect(totalsX - 5, y - 4, totalsW + 5, 10, 'F')
      doc.setTextColor(255,255,255)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(11)
      doc.text(`TOTAL TTC:`, totalsX, y + 3)
      doc.text(formatPrice(total), 200, y + 3, { align: 'right' })
      doc.setTextColor(50,50,50)
      y += 15

      // Note
      if (devisNote) {
        doc.setFont('helvetica', 'italic')
        doc.setFontSize(9)
        doc.setTextColor(100,100,100)
        doc.text(`Note: ${devisNote}`, 10, y)
        y += 8
      }

      // Conditions de paiement
      if (companyInfo.paymentConditions) {
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.setTextColor(100,100,100)
        doc.text(`Conditions: ${companyInfo.paymentConditions}`, 10, y)
        y += 8
      }

      // Validité
      const validite = new Date()
      validite.setDate(validite.getDate() + 30)
      doc.setFontSize(8)
      doc.setTextColor(150,150,150)
      doc.text(`Ce devis est valable jusqu'au ${validite.toLocaleDateString('fr-FR')}`, 10, y)

      // ─── PIED DE PAGE ───
      const pageH = doc.internal.pageSize.height
      doc.setFillColor(r,g,b)
      doc.rect(0, pageH - 20, 210, 20, 'F')
      doc.setTextColor(255,255,255)
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      const footer = companyInfo.footerText || `${companyName}${companyInfo.ninea ? ' | NINEA: '+companyInfo.ninea : ''}${companyInfo.rc ? ' | RC: '+companyInfo.rc : ''}`
      doc.text(footer, 105, pageH - 10, { align: 'center', maxWidth: 190 })

      // Sauvegarder
      const fileName = `Devis-${devisNumber}-${clientName.replace(/\s+/g,'-')}.pdf`
      doc.save(fileName)
    } catch (e) {
      console.error('Erreur PDF:', e)
      alert('Erreur génération PDF. Réessayez.')
    } finally { setGenerating(false) }
  }

  // Export XLSX grille tarifaire
  const exportXLSX = async () => {
    if (items.length === 0) { alert('Aucun tarif à exporter'); return }
    const XLSX = await import('xlsx').then(m => m.default || m)
    const ws = XLSX.utils.aoa_to_sheet([
      ['Désignation', 'Catégorie', 'Prix min (FCFA)', 'Prix max (FCFA)', 'Conditions'],
      ...items.map(i => [i.critere, i.categorie||'', i.prix, i.prixMax||'', i.conditions||''])
    ])
    ws['!cols'] = [{wch:40},{wch:15},{wch:20},{wch:20},{wch:40}]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Grille tarifaire')
    XLSX.writeFile(wb, `grille-tarifaire-${business?.name||'export'}.xlsx`)
  }

  const save = async () => {
    setSaving(true)
    try { await axios.put(`${API_URL}/api/businesses/${businessId}/modules/devis`, { data: items }); onClose() }
    catch (e) { console.error(e) } finally { setSaving(false) }
  }

  const catColors = {
    'Services':'rgba(59,130,246,0.15)', 'Produits':'rgba(245,158,11,0.15)',
    'Travaux':'rgba(239,68,68,0.15)', 'Consultation':'rgba(139,92,246,0.15)',
    'Location':'rgba(16,185,129,0.15)', 'Autre':'rgba(107,114,128,0.15)'
  }
  const catTextColors = {
    'Services':'#60a5fa', 'Produits':'#fbbf24', 'Travaux':'#f87171',
    'Consultation':'#a78bfa', 'Location':'#34d399', 'Autre':'#9ca3af'
  }

  return (
    <Modal title={`Module ${devisConfig.title}`} moduleId="devis" icon={<MdRequestQuote size={20} className="text-white"/>} color="#8b5cf6" onClose={onClose} wide isDark={isDark}>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { label: devisConfig.labelPlural, value: items.length, color:'#a78bfa', bg:'rgba(139,92,246,0.12)', border:'rgba(139,92,246,0.25)' },
          { label: devisConfig.labelSelected, value: selectedItems.length, color:'#34d399', bg:'rgba(16,185,129,0.12)', border:'rgba(16,185,129,0.25)' },
          { label: 'Total devis', value: selectedItems.length > 0 ? formatPrice(total) : '—', color:'#fbbf24', bg:'rgba(245,158,11,0.12)', border:'rgba(245,158,11,0.25)' },
        ].map((s,i) => (
          <div key={i} className="rounded-xl p-3 text-center" style={{background:s.bg,border:`1px solid ${s.border}`}}>
            <p className="text-lg font-bold truncate" style={{color:s.color}}>{s.value}</p>
            <p className="text-xs" style={{color:th.textMuted}}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{background:th.inputBg}}>
        {[
          {id:'grille', label:`${devisConfig.tabGrille} (${items.length})`},
          {id:'add', label:devisConfig.tabAdd},
          {id:'generer', label:devisConfig.tabPDF},
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
            style={activeTab===tab.id?{background:'linear-gradient(135deg,#8b5cf6,#6d28d9)',color:'white'}:{color:th.textSub}}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Grille */}
      {activeTab === 'grille' && (
        <div>
          <div className="flex gap-2 mb-3">
            <input value={searchDevis||''} onChange={e=>setSearchDevis&&setSearchDevis(e.target.value)}
              placeholder="Rechercher une prestation..."
              className="flex-1 px-3 py-2 rounded-xl text-xs focus:outline-none"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            <button onClick={exportXLSX} disabled={items.length===0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold disabled:opacity-40"
              style={{background:'rgba(16,185,129,0.15)',color:'#34d399',border:'1px solid rgba(16,185,129,0.3)'}}>
              <MdDownload size={14}/>Exporter XLSX
            </button>
          </div>
          {loading ? <div className="text-center py-4" style={{color:th.textSub}}>Chargement...</div>
          : items.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm mb-2" style={{color:th.textSub}}>Aucune prestation configurée</p>
              <button onClick={() => setActiveTab('add')} className="text-purple-400 text-xs">Ajouter une prestation →</button>
            </div>
          ) : (
            <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
              {items.filter(i => !searchDevis || i.critere.toLowerCase().includes(searchDevis.toLowerCase())).map(item => (
                <div key={item.id} className="rounded-xl" style={{border:`1px solid ${th.inputBorder}`,background:th.inputBg}}>
                  {editId === item.id ? (
                    <div className="p-3 space-y-2">
                      <input value={editData.critere} onChange={e=>setEditData({...editData,critere:e.target.value})}
                        className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none" style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}} placeholder={"Ex: Prestation"||"Désignation"}/>
                      <div className="flex gap-2">
                        <input value={editData.prix} onChange={e=>setEditData({...editData,prix:e.target.value})}
                          className="flex-1 px-3 py-2 rounded-lg text-sm focus:outline-none" style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}} placeholder="Prix min"/>
                        <input value={editData.prixMax} onChange={e=>setEditData({...editData,prixMax:e.target.value})}
                          className="flex-1 px-3 py-2 rounded-lg text-sm focus:outline-none" style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}} placeholder="Prix max (opt.)"/>
                      </div>
                      <input value={editData.conditions} onChange={e=>setEditData({...editData,conditions:e.target.value})}
                        className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none" style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}} placeholder="Conditions"/>
                      <div className="flex gap-2">
                        <button onClick={saveEdit} className="flex-1 py-1.5 rounded-lg text-xs text-white" style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>Sauvegarder</button>
                        <button onClick={()=>setEditId(null)} className="flex-1 py-1.5 rounded-lg text-xs" style={{background:th.inputBg,color:th.textSub}}>Annuler</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{background:catColors[item.categorie||'Autre'],color:catTextColors[item.categorie||'Autre']}}>
                            {item.categorie || 'Autre'}
                          </span>
                        </div>
                        <p className="text-sm font-medium truncate" style={{color:th.text}}>{item.critere}</p>
                        <p className="text-xs font-bold" style={{color:'#a78bfa'}}>
                          {item.prix} FCFA {item.prixMax ? `→ ${item.prixMax} FCFA` : ''}
                        </p>
                        {item.conditions && <p className="text-xs truncate" style={{color:th.textMuted}}>{item.conditions}</p>}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={()=>{setEditId(item.id);setEditData({critere:item.critere,prix:item.prix,prixMax:item.prixMax||'',categorie:item.categorie||'Services',conditions:item.conditions||''})}}
                          className="w-7 h-7 rounded-lg flex items-center justify-center hover:text-blue-400" style={{color:th.textSub}}><MdEdit size={14}/></button>
                        <button onClick={()=>del(item.id)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center hover:text-red-400" style={{color:th.textSub}}><MdDelete size={14}/></button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab Ajouter */}
      {activeTab === 'add' && (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold mb-2 block" style={{color:th.textSub}}>Catégorie</label>
            <div className="flex flex-wrap gap-2">
              {DEVIS_CATEGORIES.map(cat => (
                <button key={cat} onClick={() => setCategorie(cat)}
                  className="px-3 py-1.5 rounded-xl text-xs font-medium transition-all border-2"
                  style={{
                    background: categorie===cat ? catColors[cat] : th.inputBg,
                    color: categorie===cat ? catTextColors[cat] : th.textSub,
                    borderColor: categorie===cat ? catTextColors[cat] : th.inputBorder,
                  }}>
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Désignation de la prestation</label>
            <input value={critere} onChange={e=>setCritere(e.target.value)} placeholder={"Ex: Prestation"||"Ex: Prestation de votre secteur"}
              className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Prix (FCFA) *</label>
            <input value={formatNumber(prix)} onChange={e=>setPrix(parseNumber(e.target.value))}
              placeholder={"Ex: 50 000"||"Ex: 50 000"} inputMode="numeric"
              className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Conditions / Description</label>
            <input value={conditions} onChange={e=>setConditions(e.target.value)} placeholder={"Ex: Conditions"||"Ex: Inclut 2 visites sur site"}
              className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          </div>
          <button onClick={async () => {
            try {
              const storedLang = localStorage.getItem('lang') || 'fr'
              const res = await axios.post(`${API_URL}/api/generate/devis`, {
                sector: business?.sector || '',
                businessName: business?.name || '',
                language: storedLang
              })
              if (res.data.items) {
                setItems(prev => {
                  const existing = prev.map(i => i.critere.toLowerCase())
                  const newOnes = res.data.items.filter(i => !existing.includes(i.critere.toLowerCase()))
                  return [...prev, ...newOnes.map(i => ({id: genId(), ...i}))]
                })
                setActiveTab('grille')
              }
            } catch(e) { console.error(e) }
          }}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 mb-2"
            style={{background:'linear-gradient(135deg,#a855f7,#7c3aed)'}}>
            <MdAutoAwesome size={16}/>✨ Générer des prestations avec l'IA
          </button>
          <button onClick={add} disabled={!critere||!prix}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 flex items-center justify-center gap-2"
            style={{background:'linear-gradient(135deg,#8b5cf6,#6d28d9)'}}>
            <MdAdd size={16}/>Ajouter à la grille tarifaire
          </button>
        </div>
      )}

      {/* Tab Générer PDF */}
      {activeTab === 'generer' && (
        <div className="space-y-4">
          {/* Infos client */}
          <div className="rounded-xl p-4" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`}}>
            <p className="text-sm font-semibold mb-3" style={{color:th.text}}>Informations client</p>
            <div className="space-y-2">
              <input value={clientName} onChange={e=>setClientName(toTitleCase(e.target.value))} placeholder="Nom complet du client *"
                className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none" style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}}/>
              <div className="flex gap-2">
                <div className="flex-1">
                  <PhoneInput
                    value={clientPhone}
                    onChange={e=>setClientPhone(e.target.value.replace(/\D/g,''))}
                    selectedCountry={clientCountry}
                    setSelectedCountry={setClientCountry}
                    onCountryChange={c=>{setClientCountry(c);setClientPhone('')}}
                    error=""
                    phonePreviewLabel="Numéro"
                  />
                </div>
                <input value={clientAddress}
                  onChange={e=>setClientAddress(toTitleCase(e.target.value))}
                  placeholder="Ex: Ouest Foire, Dakar"
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm focus:outline-none" style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}}/>
              </div>
            </div>
          </div>

          {/* Sélection prestations */}
          <div>
            <p className="text-sm font-semibold mb-2" style={{color:th.text}}>Sélectionner les prestations</p>
            {items.length === 0 ? (
              <p className="text-xs text-center py-3" style={{color:th.textSub}}>Aucune prestation — ajoutez-en dans la grille</p>
            ) : (
              <div className="space-y-2 max-h-36 overflow-y-auto pr-1">
                {items.map(item => {
                  const sel = selectedItems.find(s => s.id === item.id)
                  return (
                    <div key={item.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all"
                      style={{background:sel?'rgba(139,92,246,0.1)':th.inputBg, border:`1px solid ${sel?'rgba(139,92,246,0.3)':th.inputBorder}`}}
                      onClick={() => toggleItem(item)}>
                      <div className={`w-5 h-5 rounded-lg flex items-center justify-center flex-shrink-0 transition-all`}
                        style={{background:sel?'#8b5cf6':'transparent',border:`2px solid ${sel?'#8b5cf6':th.textMuted}`}}>
                        {sel && <MdCheck size={12} className="text-white"/>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate" style={{color:th.text}}>{item.critere}</p>
                        <p className="text-xs" style={{color:'#a78bfa'}}>{item.prix} FCFA</p>
                      </div>
                      {sel && (
                        <div className="flex items-center gap-1 flex-shrink-0" onClick={e=>e.stopPropagation()}>
                          <button onClick={()=>updateQty(item.id,Math.max(1,sel.qty-1))} className="w-6 h-6 rounded-lg flex items-center justify-center text-white text-xs" style={{background:'rgba(139,92,246,0.3)'}}>-</button>
                          <span className="text-sm font-bold w-6 text-center" style={{color:th.text}}>{sel.qty}</span>
                          <button onClick={()=>updateQty(item.id,sel.qty+1)} className="w-6 h-6 rounded-lg flex items-center justify-center text-white text-xs" style={{background:'rgba(139,92,246,0.3)'}}>+</button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Options TVA / Remise */}
          <div className="flex gap-3">
            <div className="flex items-center gap-2 flex-1 px-3 py-2.5 rounded-xl cursor-pointer"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`}} onClick={()=>setTva(!tva)}>
              <div className={`w-5 h-5 rounded-lg flex items-center justify-center`}
                style={{background:tva?'#8b5cf6':'transparent',border:`2px solid ${tva?'#8b5cf6':th.textMuted}`}}>
                {tva && <MdCheck size={12} className="text-white"/>}
              </div>
              <span className="text-sm" style={{color:th.text}}>TVA 18%</span>
            </div>
            <div className="flex items-center gap-2 flex-1 px-3 py-2.5 rounded-xl" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`}}>
              <span className="text-sm flex-shrink-0" style={{color:th.text}}>Remise</span>
              <input type="number" min="0" max="100" value={remise} onChange={e=>setRemise(parseInt(e.target.value)||0)}
                className="flex-1 px-2 py-1 rounded-lg text-sm text-center focus:outline-none w-12"
                style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}}/>
              <span className="text-sm" style={{color:th.textSub}}>%</span>
            </div>
          </div>

          {/* Note */}
          <input value={devisNote} onChange={e=>setDevisNote(e.target.value)} placeholder="Note ou remarque (optionnel)"
            className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>

          {/* Récap total */}
          {selectedItems.length > 0 && (
            <div className="rounded-xl p-4" style={{background:'rgba(139,92,246,0.08)',border:'1px solid rgba(139,92,246,0.2)'}}>
              <p className="text-xs font-semibold mb-2" style={{color:'#a78bfa'}}>Récapitulatif</p>
              {remise > 0 && <div className="flex justify-between text-xs mb-1"><span style={{color:th.textSub}}>Sous-total</span><span style={{color:th.text}}>{formatPrice(subtotal)}</span></div>}
              {remise > 0 && <div className="flex justify-between text-xs mb-1"><span style={{color:'#f87171'}}>Remise ({remise}%)</span><span style={{color:'#f87171'}}>- {formatPrice(remiseAmount)}</span></div>}
              {tva && <div className="flex justify-between text-xs mb-1"><span style={{color:th.textSub}}>HT</span><span style={{color:th.text}}>{formatPrice(afterRemise)}</span></div>}
              {tva && <div className="flex justify-between text-xs mb-1"><span style={{color:th.textSub}}>TVA 18%</span><span style={{color:th.text}}>{formatPrice(tvaAmount)}</span></div>}
              <div className="flex justify-between text-sm font-bold mt-2 pt-2" style={{borderTop:`1px solid rgba(139,92,246,0.3)`}}>
                <span style={{color:'#a78bfa'}}>Total TTC</span>
                <span style={{color:'#a78bfa'}}>{formatPrice(total)}</span>
              </div>
            </div>
          )}

          {/* Bouton générer PDF */}
          <button onClick={generatePDF} disabled={generating||selectedItems.length===0||!clientName}
            className="w-full py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2"
            style={{background:'linear-gradient(135deg,#8b5cf6,#6d28d9)'}}>
            {generating ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>Génération PDF...</>
            ) : (
              <><MdRequestQuote size={16}/>Générer le devis PDF</>
            )}
          </button>
          <p className="text-xs text-center" style={{color:th.textMuted}}>
            Le PDF inclut votre logo, NINEA, RC et pied de page configurés
          </p>
        </div>
      )}

      <button onClick={save} disabled={saving}
        className="w-full mt-4 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2"
        style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>
        {saving?'Sauvegarde...':<><MdCheck size={16}/>Sauvegarder la grille tarifaire</>}
      </button>
    </Modal>
  )
}


// ─── MODULE SUIVI ───
// ─── MODULE SUIVI COMMANDE INTELLIGENT ───

// Statuts par secteur
const STATUTS_BY_SECTOR = {
  immobilier: [
    { id: 'prospect', label: 'Prospect', color: '#94a3b8' },
    { id: 'visite', label: 'Visite planifiée', color: '#60a5fa' },
    { id: 'offre', label: 'Offre faite', color: '#f59e0b' },
    { id: 'compromis', label: 'Compromis signé', color: '#a78bfa' },
    { id: 'acte', label: 'Acte notarié', color: '#34d399' },
    { id: 'finalise', label: 'Finalisé', color: '#10b981' },
  ],
  restaurant: [
    { id: 'recue', label: 'Reçue', color: '#94a3b8' },
    { id: 'preparation', label: 'En préparation', color: '#f59e0b' },
    { id: 'prete', label: 'Prête', color: '#a78bfa' },
    { id: 'livraison', label: 'En livraison', color: '#60a5fa' },
    { id: 'livree', label: 'Livrée', color: '#34d399' },
    { id: 'annulee', label: 'Annulée', color: '#f87171' },
  ],
  boutique: [
    { id: 'recue', label: 'Reçue', color: '#94a3b8' },
    { id: 'confirmee', label: 'Confirmée', color: '#60a5fa' },
    { id: 'preparation', label: 'En préparation', color: '#f59e0b' },
    { id: 'livraison', label: 'En livraison', color: '#a78bfa' },
    { id: 'livree', label: 'Livrée', color: '#34d399' },
    { id: 'retournee', label: 'Retournée', color: '#f87171' },
  ],
  clinique: [
    { id: 'rdv', label: 'RDV pris', color: '#94a3b8' },
    { id: 'consulte', label: 'Consulté', color: '#60a5fa' },
    { id: 'traitement', label: 'Traitement en cours', color: '#f59e0b' },
    { id: 'suivi', label: 'En suivi', color: '#a78bfa' },
    { id: 'gueri', label: 'Guéri', color: '#34d399' },
  ],
  services: [
    { id: 'devis', label: 'Devis envoyé', color: '#94a3b8' },
    { id: 'accepte', label: 'Accepté', color: '#60a5fa' },
    { id: 'encours', label: 'En cours', color: '#f59e0b' },
    { id: 'livre', label: 'Livré', color: '#a78bfa' },
    { id: 'facture', label: 'Facturé', color: '#34d399' },
    { id: 'paye', label: 'Payé', color: '#10b981' },
  ],
  default: [
    { id: 'attente', label: 'En attente', color: '#94a3b8' },
    { id: 'encours', label: 'En cours', color: '#f59e0b' },
    { id: 'livraison', label: 'En livraison', color: '#60a5fa' },
    { id: 'livre', label: 'Livré', color: '#34d399' },
    { id: 'annule', label: 'Annulé', color: '#f87171' },
  ]
}

const getStatutsBySector = (sector = '') => {
  const s = sector.toLowerCase()
  if (s.includes('immo') || s.includes('btp') || s.includes('terrain')) return STATUTS_BY_SECTOR.immobilier
  if (s.includes('restaurant') || s.includes('traiteur') || s.includes('food')) return STATUTS_BY_SECTOR.restaurant
  if (s.includes('boutique') || s.includes('commerce') || s.includes('vente')) return STATUTS_BY_SECTOR.boutique
  if (s.includes('clinique') || s.includes('santé') || s.includes('médecin')) return STATUTS_BY_SECTOR.clinique
  if (s.includes('service') || s.includes('consult') || s.includes('agence')) return STATUTS_BY_SECTOR.services
  return STATUTS_BY_SECTOR.default
}

const genOrderNum = () => `CMD-${new Date().getFullYear()}-${String(Math.floor(Math.random()*9000)+1000)}`

const SuiviModal = ({ businessId, business, onClose, isDark }) => {
  const th = getTheme(isDark)
  const [orders, setOrders] = useState([])
  const [statuts, setStatuts] = useState([])
  const [customStatut, setCustomStatut] = useState('')
  const [activeTab, setActiveTab] = useState('commandes')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [searchOrder, setSearchOrder] = useState('')
  const [filterStatut, setFilterStatut] = useState('tous')

  // Formulaire nouvelle commande
  const [clientName, setClientName] = useState('')
  const [clientPhone, setClientPhone] = useState('')
  const [clientCountry, setClientCountry] = useState(availableCountries[0])
  const [description, setDescription] = useState('')
  const [montant, setMontant] = useState('')
  const [statutDefault, setStatutDefault] = useState('')

  useEffect(() => {
    const sectorStatuts = getStatutsBySector(business?.sector || '')
    axios.get(`${API_URL}/api/businesses/${businessId}/modules`)
      .then(r => {
        setOrders(r.data.orders || [])
        // Charger statuts personnalisés ou utiliser ceux du secteur
        setStatuts(r.data.customStatuts?.length > 0 ? r.data.customStatuts : sectorStatuts)
        setStatutDefault(sectorStatuts[0]?.id || 'attente')
      })
      .catch(() => { setStatuts(sectorStatuts) })
      .finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Générer statuts avec IA
  const generateStatutsWithAI = async () => {
    setGenerating(true)
    try {
      const storedLang = localStorage.getItem('lang') || 'fr'
      const res = await axios.post(`${API_URL}/api/generate/statuts`, {
        sector: business?.sector || '',
        language: storedLang
      })
      if (res.data.statuts) setStatuts(res.data.statuts)
    } catch (e) {
      // Fallback sur les statuts du secteur
      setStatuts(getStatutsBySector(business?.sector || ''))
      console.error('Erreur génération statuts:', e)
    } finally { setGenerating(false) }
  }

  const addStatut = () => {
    if (!customStatut.trim()) return
    const colors = ['#60a5fa','#f59e0b','#a78bfa','#34d399','#f87171','#22d3ee','#fb923c']
    const newStatut = {
      id: genId(),
      label: toTitleCase(customStatut),
      color: colors[statuts.length % colors.length]
    }
    setStatuts([...statuts, newStatut])
    setCustomStatut('')
  }

  const removeStatut = (id) => setStatuts(statuts.filter(s => s.id !== id))

  const addOrder = () => {
    if (!clientName) return
    const newOrder = {
      id: genId(),
      numero: genOrderNum(),
      client: toTitleCase(clientName),
      phone: clientPhone,
      description: toSentenceCase(description),
      montant: parseNumber(montant),
      status: statutDefault || statuts[0]?.id || 'attente',
      statusLabel: statuts.find(s => s.id === statutDefault)?.label || statuts[0]?.label || 'En attente',
      createdAt: new Date().toISOString(),
      history: [{ status: statutDefault, label: statuts.find(s=>s.id===statutDefault)?.label, date: new Date().toISOString() }]
    }
    setOrders(prev => [newOrder, ...prev])
    setClientName(''); setClientPhone(''); setDescription(''); setMontant('')
    setActiveTab('commandes')
  }

  const updateStatus = (orderId, newStatusId) => {
    const statutInfo = statuts.find(s => s.id === newStatusId)
    setOrders(orders.map(o => {
      if (o.id !== orderId) return o
      return {
        ...o,
        status: newStatusId,
        statusLabel: statutInfo?.label || newStatusId,
        history: [...(o.history || []), {
          status: newStatusId,
          label: statutInfo?.label,
          date: new Date().toISOString()
        }]
      }
    }))
  }

  const deleteOrder = (id) => setOrders(orders.filter(o => o.id !== id))

  // Export XLSX
  const exportXLSX = async () => {
    if (orders.length === 0) { alert('Aucune commande à exporter'); return }
    const XLSX = await import('xlsx').then(m => m.default || m)
    const ws = XLSX.utils.aoa_to_sheet([
      ['N° Commande', 'Client', 'Téléphone', 'Description', 'Montant (FCFA)', 'Statut', 'Date'],
      ...orders.map(o => [
        o.numero || o.id,
        o.client,
        o.phone || '',
        o.description || '',
        o.montant || '',
        o.statusLabel || o.status,
        new Date(o.createdAt || Date.now()).toLocaleDateString('fr-FR')
      ])
    ])
    ws['!cols'] = [{wch:16},{wch:25},{wch:18},{wch:35},{wch:15},{wch:20},{wch:12}]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Commandes')
    XLSX.writeFile(wb, `commandes-${business?.name||'export'}-${new Date().toLocaleDateString('fr-FR').replace(/\//g,'-')}.xlsx`)
  }

  const save = async () => {
    setSaving(true)
    try {
      await Promise.all([
        axios.put(`${API_URL}/api/businesses/${businessId}/modules/orders`, { data: orders }),
        axios.put(`${API_URL}/api/businesses/${businessId}/modules/customStatuts`, { data: statuts }),
      ])
      onClose()
    } catch (e) { console.error(e) } finally { setSaving(false) }
  }

  const filteredOrders = orders.filter(o => {
    const matchSearch = !searchOrder || o.client.toLowerCase().includes(searchOrder.toLowerCase()) || (o.numero||'').includes(searchOrder)
    const matchStatut = filterStatut === 'tous' || o.status === filterStatut
    return matchSearch && matchStatut
  })

  const getStatutInfo = (statusId) => statuts.find(s => s.id === statusId) || { label: statusId, color: '#94a3b8' }

  // Stats
  const statsByStatus = statuts.reduce((acc, s) => {
    acc[s.id] = orders.filter(o => o.status === s.id).length
    return acc
  }, {})

  return (
    <Modal title="Module Suivi Commande" moduleId="suivi" icon={<MdLocalShipping size={20} className="text-white"/>} color="#ec4899" onClose={onClose} wide isDark={isDark}>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { label: 'Total', value: orders.length, color:'#ec4899', bg:'rgba(236,72,153,0.12)', border:'rgba(236,72,153,0.25)' },
          { label: 'En cours', value: orders.filter(o=>!['livre','livree','finalise','paye','annule','annulee'].includes(o.status)).length, color:'#f59e0b', bg:'rgba(245,158,11,0.12)', border:'rgba(245,158,11,0.25)' },
          { label: 'Finalisés', value: orders.filter(o=>['livre','livree','finalise','paye'].includes(o.status)).length, color:'#34d399', bg:'rgba(16,185,129,0.12)', border:'rgba(16,185,129,0.25)' },
          { label: 'Annulés', value: orders.filter(o=>['annule','annulee'].includes(o.status)).length, color:'#f87171', bg:'rgba(239,68,68,0.12)', border:'rgba(239,68,68,0.25)' },
        ].map((s,i) => (
          <div key={i} className="rounded-xl p-2 text-center" style={{background:s.bg,border:`1px solid ${s.border}`}}>
            <p className="text-lg font-bold" style={{color:s.color}}>{s.value}</p>
            <p className="text-xs" style={{color:th.textMuted}}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{background:th.inputBg}}>
        {[
          {id:'commandes', label:` Commandes (${orders.length})`},
          {id:'add', label:' Ajouter'},
          {id:'statuts', label:`⚙️ Statuts (${statuts.length})`},
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
            style={activeTab===tab.id?{background:'linear-gradient(135deg,#ec4899,#be185d)',color:'white'}:{color:th.textSub}}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Commandes */}
      {activeTab === 'commandes' && (
        <div>
          <div className="flex gap-2 mb-3">
            <input value={searchOrder} onChange={e=>setSearchOrder(e.target.value)}
              placeholder="Rechercher client ou N° commande..."
              className="flex-1 px-3 py-2 rounded-xl text-xs focus:outline-none"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            <select value={filterStatut} onChange={e=>setFilterStatut(e.target.value)}
              className="px-3 py-2 rounded-xl text-xs focus:outline-none"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}>
              <option value="tous">Tous</option>
              {statuts.map(s => <option key={s.id} value={s.id}>{s.label} ({statsByStatus[s.id]||0})</option>)}
            </select>
            <button onClick={exportXLSX} disabled={orders.length===0}
              className="flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-semibold disabled:opacity-40 flex-shrink-0"
              style={{background:'rgba(16,185,129,0.15)',color:'#34d399',border:'1px solid rgba(16,185,129,0.3)'}}>
              <MdDownload size={13}/>XLSX
            </button>
          </div>

          {loading ? <div className="text-center py-4" style={{color:th.textSub}}>Chargement...</div>
          : filteredOrders.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm mb-2" style={{color:th.textSub}}>Aucune commande</p>
              <button onClick={() => setActiveTab('add')} className="text-pink-400 text-xs">Ajouter une commande →</button>
            </div>
          ) : (
            <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
              {filteredOrders.map(o => {
                const statutInfo = getStatutInfo(o.status) // eslint-disable-line no-unused-vars
                return (
                  <div key={o.id} className="rounded-xl overflow-hidden" style={{border:`1px solid ${th.inputBorder}`}}>
                    <div className="px-4 py-3" style={{background:th.inputBg}}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="text-sm font-bold truncate" style={{color:th.text}}>{o.client}</p>
                            {o.numero && <span className="text-xs px-1.5 py-0.5 rounded-md flex-shrink-0" style={{background:th.card,color:th.textMuted}}>{o.numero}</span>}
                          </div>
                          {o.description && <p className="text-xs truncate" style={{color:th.textSub}}>{o.description}</p>}
                          {o.montant && <p className="text-xs font-semibold" style={{color:'#fbbf24'}}>{formatNumber(String(o.montant))} FCFA</p>}
                        </div>
                        <button onClick={() => deleteOrder(o.id)}
                          className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 hover:text-red-400 transition-all"
                          style={{color:th.textMuted}}><MdDelete size={13}/></button>
                      </div>

                      {/* Statuts cliquables */}
                      <div className="flex flex-wrap gap-1.5">
                        {statuts.map(s => (
                          <button key={s.id} onClick={() => updateStatus(o.id, s.id)}
                            className="text-xs px-2 py-1 rounded-lg transition-all font-medium"
                            style={{
                              background: o.status===s.id ? s.color+'33' : th.card,
                              color: o.status===s.id ? s.color : th.textMuted,
                              border: `1px solid ${o.status===s.id ? s.color+'66' : 'transparent'}`,
                              transform: o.status===s.id ? 'scale(1.05)' : 'scale(1)'
                            }}>
                            {o.status===s.id && '● '}{s.label}
                          </button>
                        ))}
                      </div>

                      {/* Date */}
                      <p className="text-xs mt-2" style={{color:th.textMuted}}>
                        {new Date(o.createdAt||Date.now()).toLocaleDateString('fr-FR', {day:'2-digit',month:'short',year:'numeric'})}
                        {o.phone && ` · ${o.phone}`}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab Ajouter */}
      {activeTab === 'add' && (
        <div className="space-y-3">
          {(() => { const ph = getPlaceholders(business?.sector); return (
          <>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Nom du client *</label>
              <input value={clientName} onChange={e=>setClientName(toTitleCase(e.target.value))}
                placeholder={ph.clientName}
                className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none"
                style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            </div>
            <div className="flex-1">
              <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Téléphone</label>
              <PhoneInput
                value={clientPhone}
                onChange={e=>setClientPhone(e.target.value.replace(/\D/g,''))}
                selectedCountry={clientCountry}
                setSelectedCountry={setClientCountry}
                onCountryChange={c=>{setClientCountry(c);setClientPhone('')}}
                error=""
                phonePreviewLabel="Numéro"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Description</label>
            <input value={description} onChange={e=>setDescription(toSentenceCase(e.target.value))}
              placeholder={ph.description}
              className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Montant (FCFA)</label>
              <input value={formatNumber(montant)} onChange={e=>setMontant(parseNumber(e.target.value))}
                placeholder={ph.montant} inputMode="numeric"
                className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none"
                style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            </div>
            <div className="flex-1">
              <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Statut initial</label>
              <select value={statutDefault} onChange={e=>setStatutDefault(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none"
                style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}>
                {statuts.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <button onClick={addOrder} disabled={!clientName}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 flex items-center justify-center gap-2"
            style={{background:'linear-gradient(135deg,#ec4899,#be185d)'}}>
            <MdAdd size={16}/>Ajouter la commande
          </button>
          </> )})()}
        </div>
      )}

      {/* Tab Statuts */}
      {activeTab === 'statuts' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold" style={{color:th.text}}>Statuts de suivi</p>
            <button onClick={generateStatutsWithAI} disabled={generating}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold disabled:opacity-50"
              style={{background:'linear-gradient(135deg,#a855f7,#7c3aed)',color:'white'}}>
              {generating ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"/> : <MdAutoAwesome size={13}/>}
              IA
            </button>
          </div>

          <p className="text-xs" style={{color:th.textSub}}>
            Statuts détectés pour votre secteur : <span style={{color:th.text,fontWeight:'600'}}>{business?.sector}</span>
          </p>

          {/* Liste statuts */}
          <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
            {statuts.map((s, i) => (
              <div key={s.id} className="flex items-center gap-3 px-4 py-2.5 rounded-xl"
                style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`}}>
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{background:s.color}}/>
                <span className="flex-1 text-sm font-medium" style={{color:th.text}}>{s.label}</span>
                <span className="text-xs px-2 py-0.5 rounded-full" style={{background:s.color+'22',color:s.color}}>
                  {statsByStatus[s.id]||0}
                </span>
                <button onClick={() => removeStatut(s.id)}
                  className="w-6 h-6 rounded flex items-center justify-center hover:text-red-400 transition-all"
                  style={{color:th.textMuted}}><MdDelete size={13}/></button>
              </div>
            ))}
          </div>

          {/* Ajouter statut personnalisé */}
          <div className="flex gap-2">
            <input value={customStatut} onChange={e=>setCustomStatut(e.target.value)}
              placeholder="Ajouter un statut personnalisé..."
              onKeyDown={e=>e.key==='Enter'&&addStatut()}
              className="flex-1 px-4 py-2.5 rounded-xl text-sm focus:outline-none"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            <button onClick={addStatut} disabled={!customStatut}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white disabled:opacity-40"
              style={{background:'linear-gradient(135deg,#ec4899,#be185d)'}}>
              <MdAdd size={20}/>
            </button>
          </div>

          {/* Suggestions secteur */}
          <div className="rounded-xl p-3" style={{background:'rgba(236,72,153,0.08)',border:'1px solid rgba(236,72,153,0.2)'}}>
            <p className="text-xs font-semibold mb-2" style={{color:'#f9a8d4'}}> Suggestions pour votre secteur</p>
            <div className="flex flex-wrap gap-1.5">
              {getStatutsBySector(business?.sector||'').filter(s => !statuts.find(st => st.label === s.label)).map(s => (
                <button key={s.id} onClick={() => setStatuts(prev => [...prev, {...s, id: genId()}])}
                  className="text-xs px-2.5 py-1 rounded-full transition-all hover:opacity-80"
                  style={{background:s.color+'22',color:s.color,border:`1px solid ${s.color}44`}}>
                  + {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <button onClick={save} disabled={saving}
        className="w-full mt-4 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2"
        style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>
        {saving?'Sauvegarde...':<><MdCheck size={16}/>Sauvegarder le suivi</>}
      </button>
    </Modal>
  )
}


const getSkillCategories = (sector = '') => {
  const s = (sector || '').toLowerCase()
  if (s.includes('immo') || s.includes('btp')) return ['Prospection','Négociation','Juridique','Technique','Gestion','Communication','Autre']
  if (s.includes('clinique') || s.includes('santé')) return ['Diagnostic','Soins','Urgences','Communication','Administration','Hygiène','Autre']
  if (s.includes('tourisme') || s.includes('hôtel') || s.includes('hotel') || s.includes('voyage') || s.includes('agence')) return ['Accueil','Réservations','Circuits','Visa & Formalités','Langues','Service client','Autre']
  if (s.includes('restaurant') || s.includes('food') || s.includes('traiteur')) return ['Cuisine','Service','Hygiène','Commandes','Livraison','Gestion','Autre']
  if (s.includes('info') || s.includes('tech') || s.includes('digital')) return ['Développement','Design','Support','Sécurité','Formation','Gestion projet','Autre']
  if (s.includes('event') || s.includes('événement')) return ['Organisation','Décoration','Coordination','Fournisseurs','Communication','Gestion','Autre']
  return ['Communication','Négociation','Vente','Support','Technique','Gestion','Autre']
}

// ─── MODULE COMPÉTENCES ───
// ─── MODULE COMPÉTENCE LIBRE ───

const SKILLS_CATEGORIES = (sector) => getSkillCategories(sector)

const SKILLS_BY_SECTOR = {
  immobilier: [
    { text: 'Toujours proposer une visite après chaque demande de prix.', category: 'Commercial', priority: true },
    { text: 'Ne jamais divulguer le prix de réserve d\'un bien sans accord du propriétaire.', category: 'Juridique', priority: true },
    { text: 'Mentionner systématiquement les options de financement disponibles.', category: 'Commercial', priority: false },
    { text: 'Répondre à toute demande dans un délai maximum de 2 heures.', category: 'Service client', priority: true },
    { text: 'Toujours confirmer les visites 24h à l\'avance par WhatsApp.', category: 'Communication', priority: false },
  ],
  restaurant: [
    { text: 'Ne jamais promettre une livraison en moins de 30 minutes aux heures de pointe.', category: 'Livraison', priority: true },
    { text: 'Toujours mentionner les allergènes sur demande.', category: 'Juridique', priority: true },
    { text: 'Proposer le menu du jour en premier avant de montrer la carte complète.', category: 'Commercial', priority: false },
    { text: 'Confirmer chaque commande avec un récapitulatif avant validation.', category: 'Service client', priority: true },
  ],
  boutique: [
    { text: 'Vérifier la disponibilité du stock avant de confirmer une commande.', category: 'Commercial', priority: true },
    { text: 'Les retours sont acceptés sous 7 jours avec le reçu d\'achat.', category: 'Juridique', priority: true },
    { text: 'Proposer la livraison à domicile pour toute commande supérieure à 20 000 FCFA.', category: 'Livraison', priority: false },
    { text: 'Informer le client dès que sa commande est prête.', category: 'Service client', priority: false },
  ],
  clinique: [
    { text: 'Ne jamais donner de diagnostic médical par WhatsApp.', category: 'Juridique', priority: true },
    { text: 'Toujours orienter vers une consultation physique pour les urgences.', category: 'Service client', priority: true },
    { text: 'Respecter la confidentialité des informations médicales des patients.', category: 'Juridique', priority: true },
    { text: 'Rappeler les rendez-vous 1 heure avant par WhatsApp.', category: 'Communication', priority: false },
  ],
  services: [
    { text: 'Envoyer un devis dans les 24h suivant toute demande.', category: 'Commercial', priority: true },
    { text: 'Ne jamais commencer un travail sans acompte signé.', category: 'Juridique', priority: true },
    { text: 'Tenir le client informé de l\'avancement toutes les 48h.', category: 'Communication', priority: false },
    { text: 'Proposer une garantie satisfaction sur tous les services.', category: 'Commercial', priority: false },
  ],
  default: [
    { text: 'Répondre à tous les messages dans un délai de 2 heures maximum.', category: 'Service client', priority: true },
    { text: 'Toujours saluer le client par son prénom s\'il est connu.', category: 'Communication', priority: false },
    { text: 'Ne jamais promettre ce qui ne peut pas être tenu.', category: 'Commercial', priority: true },
    { text: 'Proposer Wave ou Orange Money comme moyens de paiement.', category: 'Commercial', priority: false },
  ]
}

const getSkillsBySector = (sector = '') => {
  const s = sector.toLowerCase()
  if (s.includes('immo') || s.includes('btp') || s.includes('terrain')) return SKILLS_BY_SECTOR.immobilier
  if (s.includes('restaurant') || s.includes('traiteur') || s.includes('food')) return SKILLS_BY_SECTOR.restaurant
  if (s.includes('boutique') || s.includes('commerce') || s.includes('vente')) return SKILLS_BY_SECTOR.boutique
  if (s.includes('clinique') || s.includes('santé') || s.includes('médecin')) return SKILLS_BY_SECTOR.clinique
  if (s.includes('service') || s.includes('consult') || s.includes('agence')) return SKILLS_BY_SECTOR.services
  return SKILLS_BY_SECTOR.default
}

const catColors = {
  'Commercial': { bg: 'rgba(59,130,246,0.15)', text: '#60a5fa', border: 'rgba(59,130,246,0.3)' },
  'Service client': { bg: 'rgba(16,185,129,0.15)', text: '#34d399', border: 'rgba(16,185,129,0.3)' },
  'Livraison': { bg: 'rgba(245,158,11,0.15)', text: '#fbbf24', border: 'rgba(245,158,11,0.3)' },
  'Juridique': { bg: 'rgba(239,68,68,0.15)', text: '#f87171', border: 'rgba(239,68,68,0.3)' },
  'Communication': { bg: 'rgba(139,92,246,0.15)', text: '#a78bfa', border: 'rgba(139,92,246,0.3)' },
  'Autre': { bg: 'rgba(107,114,128,0.15)', text: '#9ca3af', border: 'rgba(107,114,128,0.3)' },
}

const SkillsModal = ({ businessId, business, onClose, isDark }) => {
  const th = getTheme(isDark)
  const ph = getPlaceholders(business?.sector)
  const [skills, setSkills] = useState([])
  const [newText, setNewText] = useState('')
  const [newCategory, setNewCategory] = useState('Autre')
  const [newPriority, setNewPriority] = useState(false)
  const [editId, setEditId] = useState(null)
  const [editData, setEditData] = useState({})
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [activeTab, setActiveTab] = useState('list')
  const [searchSkill, setSearchSkill] = useState('')
  const [filterCat, setFilterCat] = useState('Tous')

  const suggestions = getSkillsBySector(business?.sector || '')

  useEffect(() => {
    axios.get(`${API_URL}/api/businesses/${businessId}/modules`)
      .then(r => setSkills(r.data.skills || []))
      .catch(() => {}).finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const add = () => {
    if (!newText.trim()) return
    setSkills(prev => [...prev, {
      id: genId(),
      text: toSentenceCase(newText),
      category: newCategory,
      priority: newPriority,
      active: true,
    }])
    setNewText(''); setNewPriority(false)
    setActiveTab('list')
  }

  const addSuggestion = (sug) => {
    if (skills.find(s => s.text === sug.text)) {
      setSkills(skills.filter(s => s.text !== sug.text))
    } else {
      setSkills(prev => [...prev, { id: genId(), ...sug, active: true }])
    }
  }

  const toggleActive = (id) => setSkills(skills.map(s => s.id === id ? {...s, active: !s.active} : s))
  const togglePriority = (id) => setSkills(skills.map(s => s.id === id ? {...s, priority: !s.priority} : s))
  const del = (id) => setSkills(skills.filter(s => s.id !== id))
  const saveEdit = () => { setSkills(skills.map(s => s.id === editId ? {...s, ...editData} : s)); setEditId(null) }

  const generateWithAI = async () => {
    setGenerating(true)
    try {
      const storedLang = localStorage.getItem('lang') || 'fr'
      const res = await axios.post(`${API_URL}/api/generate/skills`, {
        sector: business?.sector || '',
        language: storedLang,
        businessName: business?.name || ''
      })
      if (res.data.skills) {
        setSkills(prev => {
          const existing = prev.map(s => s.text.toLowerCase())
          const newOnes = res.data.skills.filter(s => !existing.includes(s.text.toLowerCase()))
          return [...prev, ...newOnes.map(s => ({ id: genId(), ...s, active: true }))]
        })
      }
    } catch (e) {
      console.error('Erreur génération compétences:', e)
    } finally { setGenerating(false) }
  }


  // Export JSON
  const exportJSON = () => {
    if (skills.length === 0) { alert('Aucun élément à exporter'); return }
    const blob = new Blob([JSON.stringify(skills.map(s => ({ text: s.text, category: s.category, priority: s.priority })), null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `skills-${Date.now()}.json`; a.click()
    URL.revokeObjectURL(url)
  }

  // Import JSON
  const importJSON = (e) => {
    const file = e.target.files[0]; if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result)
        const items = Array.isArray(data) ? data : (data.items || data.data || [])
        const newItems = items.filter(i => i).map(i => ({ id: Date.now().toString(36)+Math.random().toString(36).slice(2), text: i.text||i.skill||i.rule||'', category: i.category||'Autre', priority: !!i.priority, active: i.active !== false }))
        if (newItems.length === 0) { alert('Aucun élément valide'); return }
        if (!window.confirm(`Importer ${newItems.length} élément(s) ?`)) return
        setSkills(prev => [...prev, ...newItems])
        alert(`✅ ${newItems.length} élément(s) importés !`)
      } catch(err) { alert('Erreur JSON: ' + err.message) }
    }
    reader.readAsText(file); e.target.value = ''
  }

  const save = async () => {
    setSaving(true)
    try {
      await axios.put(`${API_URL}/api/businesses/${businessId}/modules/skills`, { data: skills })
      onClose()
    } catch (e) { console.error(e) } finally { setSaving(false) }
  }

  const filtered = skills.filter(s => {
    const matchSearch = !searchSkill || s.text.toLowerCase().includes(searchSkill.toLowerCase())
    const matchCat = filterCat === 'Tous' || s.category === filterCat
    return matchSearch && matchCat
  })

  const activeCount = skills.filter(s => s.active !== false).length
  const priorityCount = skills.filter(s => s.priority).length

  return (
    <Modal title="Module Compétence Libre" moduleId="skills" icon={<MdPsychology size={20} className="text-white"/>} color="#f97316" onClose={onClose} wide isDark={isDark}>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { label: 'Total règles', value: skills.length, color:'#fb923c', bg:'rgba(249,115,22,0.12)', border:'rgba(249,115,22,0.25)' },
          { label: 'Actives', value: activeCount, color:'#34d399', bg:'rgba(16,185,129,0.12)', border:'rgba(16,185,129,0.25)' },
          { label: 'Prioritaires', value: priorityCount, color:'#fbbf24', bg:'rgba(245,158,11,0.12)', border:'rgba(245,158,11,0.25)' },
        ].map((s,i) => (
          <div key={i} className="rounded-xl p-3 text-center" style={{background:s.bg,border:`1px solid ${s.border}`}}>
            <p className="text-xl font-bold" style={{color:s.color}}>{s.value}</p>
            <p className="text-xs" style={{color:th.textMuted}}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{background:th.inputBg}}>
        {[
          {id:'list', label:` Règles (${skills.length})`},
          {id:'add', label:' Ajouter'},
          {id:'suggest', label:` Suggestions (${suggestions.filter(s=>!skills.find(sk=>sk.text===s.text)).length})`},
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
            style={activeTab===tab.id?{background:'linear-gradient(135deg,#f97316,#ea580c)',color:'white'}:{color:th.textSub}}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Liste */}
      {activeTab === 'list' && (
        <div>
          <div style={{display:"flex", gap:6, marginBottom:12, flexWrap:"wrap"}}>
            <button onClick={exportJSON} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold" style={{background:"rgba(139,92,246,0.12)",color:"#a78bfa",border:"1px solid rgba(139,92,246,0.25)"}}>
              <MdDownload size={13}/>Export JSON
            </button>
            <label className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer" style={{background:"rgba(245,158,11,0.12)",color:"#fbbf24",border:"1px solid rgba(245,158,11,0.25)"}}>
              <MdUpload size={13}/>Import JSON
              <input type="file" accept=".json" onChange={importJSON} className="hidden"/>
            </label>
          </div>
          <div className="flex gap-2 mb-3">
            <input value={searchSkill} onChange={e=>setSearchSkill(e.target.value)}
              placeholder="Rechercher une règle..."
              className="flex-1 px-3 py-2 rounded-xl text-xs focus:outline-none"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            <select value={filterCat} onChange={e=>setFilterCat(e.target.value)}
              className="px-3 py-2 rounded-xl text-xs focus:outline-none"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}>
              <option value="Tous">Toutes</option>
              {SKILLS_CATEGORIES(business?.sector).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={generateWithAI} disabled={generating}
              className="flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-semibold disabled:opacity-50 flex-shrink-0"
              style={{background:'linear-gradient(135deg,#a855f7,#7c3aed)',color:'white'}}>
              {generating ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"/> : <MdAutoAwesome size={13}/>}
              IA
            </button>
          </div>

          {loading ? <div className="text-center py-4" style={{color:th.textSub}}>Chargement...</div>
          : filtered.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm mb-2" style={{color:th.textSub}}>Aucune règle configurée</p>
              <button onClick={() => setActiveTab('suggest')} className="text-orange-400 text-xs">Voir les suggestions →</button>
            </div>
          ) : (
            <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
              {filtered.map(s => {
                const cc = catColors[s.category] || catColors['Autre']
                return (
                  <div key={s.id} className="rounded-xl overflow-hidden transition-all"
                    style={{border:`1px solid ${s.active!==false?th.inputBorder:'rgba(107,114,128,0.15)'}`, opacity:s.active!==false?1:0.5}}>
                    {editId === s.id ? (
                      <div className="p-3 space-y-2" style={{background:th.inputBg}}>
                        <textarea value={editData.text} onChange={e=>setEditData({...editData,text:e.target.value})} rows={2}
                          className="w-full px-3 py-2 rounded-lg text-sm resize-none focus:outline-none"
                          style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}}/>
                        <select value={editData.category} onChange={e=>setEditData({...editData,category:e.target.value})}
                          className="w-full px-3 py-2 rounded-lg text-xs focus:outline-none"
                          style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.text}}>
                          {SKILLS_CATEGORIES(business?.sector).map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <div className="flex gap-2">
                          <button onClick={saveEdit} className="flex-1 py-1.5 rounded-lg text-xs text-white font-semibold" style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>Sauvegarder</button>
                          <button onClick={()=>setEditId(null)} className="flex-1 py-1.5 rounded-lg text-xs" style={{background:th.inputBg,color:th.textSub}}>Annuler</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3 p-3" style={{background:th.inputBg}}>
                        {/* Toggle active */}
                        <button onClick={() => toggleActive(s.id)}
                          className={`w-9 h-5 rounded-full relative flex-shrink-0 mt-0.5 transition-colors ${s.active!==false?'bg-orange-500':'bg-slate-400'}`}>
                          <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${s.active!==false?'left-4':'left-0.5'}`}/>
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm leading-relaxed" style={{color:th.text}}>{s.text}</p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                              style={{background:cc.bg,color:cc.text,border:`1px solid ${cc.border}`}}>
                              {s.category}
                            </span>
                            {s.priority && <span className="text-xs">⭐ Prioritaire</span>}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 flex-shrink-0">
                          <button onClick={() => togglePriority(s.id)}
                            className="w-6 h-6 rounded flex items-center justify-center transition-all"
                            style={{color:s.priority?'#fbbf24':th.textMuted}} title="Prioritaire">
                            ⭐
                          </button>
                          <button onClick={() => {setEditId(s.id);setEditData({text:s.text,category:s.category||'Autre'})}}
                            className="w-6 h-6 rounded flex items-center justify-center hover:text-blue-400 transition-all"
                            style={{color:th.textMuted}}><MdEdit size={13}/></button>
                          <button onClick={() => del(s.id)}
                            className="w-6 h-6 rounded flex items-center justify-center hover:text-red-400 transition-all"
                            style={{color:th.textMuted}}><MdDelete size={13}/></button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Tab Ajouter */}
      {activeTab === 'add' && (
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold mb-2 block" style={{color:th.textSub}}>Catégorie</label>
            <div className="flex flex-wrap gap-2">
              {SKILLS_CATEGORIES(business?.sector).map(cat => {
                const cc = catColors[cat] || catColors['Autre']
                return (
                  <button key={cat} onClick={() => setNewCategory(cat)}
                    className="px-3 py-1.5 rounded-xl text-xs font-medium transition-all border-2"
                    style={{
                      background: newCategory===cat ? cc.bg : th.inputBg,
                      color: newCategory===cat ? cc.text : th.textSub,
                      borderColor: newCategory===cat ? cc.border : th.inputBorder,
                    }}>
                    {cat}
                  </button>
                )
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Règle ou instruction</label>
            <textarea value={newText} onChange={e=>setNewText(e.target.value)}
              placeholder={ph.skill||"Ex: Toujours confirmer le rendez-vous 24h à l'avance par WhatsApp."}
              rows={3} className="w-full px-4 py-2.5 rounded-xl text-sm resize-none focus:outline-none focus:ring-1 focus:ring-orange-500"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          </div>

          <div className="flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer"
            style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`}}
            onClick={() => setNewPriority(!newPriority)}>
            <div className={`w-5 h-5 rounded-lg flex items-center justify-center transition-all`}
              style={{background:newPriority?'#f59e0b':'transparent',border:`2px solid ${newPriority?'#f59e0b':th.textMuted}`}}>
              {newPriority && <span style={{fontSize:'11px'}}>⭐</span>}
            </div>
            <div>
              <p className="text-sm font-medium" style={{color:th.text}}>Marquer comme prioritaire</p>
              <p className="text-xs" style={{color:th.textSub}}>L'IA respectera cette règle en priorité absolue</p>
            </div>
          </div>

          <button onClick={add} disabled={!newText.trim()}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 flex items-center justify-center gap-2"
            style={{background:'linear-gradient(135deg,#f97316,#ea580c)'}}>
            <MdAdd size={16}/>Ajouter cette règle
          </button>
        </div>
      )}

      {/* Tab Suggestions */}
      {activeTab === 'suggest' && (
        <div>
          <p className="text-xs mb-3" style={{color:th.textSub}}>
            Règles recommandées pour votre secteur : <span style={{color:th.text,fontWeight:'600'}}>{business?.sector}</span>
          </p>
          <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
            {suggestions.map((sug, i) => {
              const added = skills.find(s => s.text === sug.text)
              const cc = catColors[sug.category] || catColors['Autre']
              return (
                <div key={i} className="flex items-start gap-3 px-4 py-3 rounded-xl transition-all"
                  style={{background:added?'rgba(249,115,22,0.08)':th.inputBg, border:`1px solid ${added?'rgba(249,115,22,0.2)':th.inputBorder}`}}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm leading-relaxed" style={{color:th.text}}>{sug.text}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{background:cc.bg,color:cc.text,border:`1px solid ${cc.border}`}}>
                        {sug.category}
                      </span>
                      {sug.priority && <span className="text-xs text-yellow-400">⭐</span>}
                    </div>
                  </div>
                  <button onClick={() => addSuggestion(sug)}
                    className="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center transition-all"
                    style={{background:added?'rgba(249,115,22,0.2)':'rgba(249,115,22,0.15)', color:added?'#fb923c':'#fb923c'}}>
                    {added ? <MdCheck size={16}/> : <MdAdd size={16}/>}
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <button onClick={save} disabled={saving}
        className="w-full mt-4 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2"
        style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>
        {saving?'Sauvegarde...':<><MdCheck size={16}/>Sauvegarder {activeCount} règles actives</>}
      </button>
    </Modal>
  )
}


// ─── MODULE PERSONNALITÉ ───
// ─── MODULE PERSONNALITÉ ───

const TONES_BY_SECTOR = (sector = '') => {
  const s = sector.toLowerCase()
  if (s.includes('clinique') || s.includes('santé') || s.includes('médecin') || s.includes('pharma')) return {
    default: 'expert',
    tones: [
      { id: 'expert', label: 'Expert', icon: '', desc: 'Précis, rassurant, médical' },
      { id: 'professionnel', label: 'Professionnel', icon: '', desc: 'Sérieux et courtois' },
      { id: 'chaleureux', label: 'Chaleureux', icon: '', desc: 'Empathique et bienveillant' },
    ]
  }
  if (s.includes('immo') || s.includes('btp') || s.includes('terrain')) return {
    default: 'professionnel',
    tones: [
      { id: 'professionnel', label: 'Professionnel', icon: '', desc: 'Sérieux et fiable' },
      { id: 'expert', label: 'Expert', icon: '', desc: 'Autoritaire et précis' },
      { id: 'chaleureux', label: 'Chaleureux', icon: '', desc: 'Proche et accessible' },
      { id: 'tantine', label: 'Tantine', icon: '‍', desc: 'Comme un proche de confiance' },
    ]
  }
  if (s.includes('restaurant') || s.includes('traiteur') || s.includes('food')) return {
    default: 'chaleureux',
    tones: [
      { id: 'chaleureux', label: 'Chaleureux', icon: '️', desc: 'Accueillant et convivial' },
      { id: 'humoristique', label: 'Humoristique', icon: '', desc: 'Léger et sympathique' },
      { id: 'tantine', label: 'Tantine', icon: '‍', desc: 'Comme une cuisinière de maison' },
      { id: 'professionnel', label: 'Professionnel', icon: '', desc: 'Formel si besoin' },
    ]
  }
  if (s.includes('boutique') || s.includes('commerce') || s.includes('vente')) return {
    default: 'chaleureux',
    tones: [
      { id: 'chaleureux', label: 'Chaleureux', icon: '️', desc: 'Accueillant et commerçant' },
      { id: 'humoristique', label: 'Humoristique', icon: '', desc: 'Léger et sympathique' },
      { id: 'tantine', label: 'Tantine', icon: '‍', desc: 'Comme une marchande de confiance' },
      { id: 'professionnel', label: 'Professionnel', icon: '', desc: 'Sérieux et fiable' },
    ]
  }
  // Default — tous secteurs
  return {
    default: 'professionnel',
    tones: [
      { id: 'professionnel', label: 'Professionnel', icon: '', desc: 'Sérieux et courtois' },
      { id: 'chaleureux', label: 'Chaleureux', icon: '', desc: 'Amical et bienveillant' },
      { id: 'expert', label: 'Expert', icon: '', desc: 'Autoritaire et précis' },
      { id: 'humoristique', label: 'Humoristique', icon: '', desc: 'Léger avec une touche d\'humour' },
      { id: 'tantine', label: 'Tantine', icon: '‍', desc: 'Proche comme une tantine de confiance' },
    ]
  }
}

const STYLES = [
  { id: 'formel', label: 'Formel', icon: '', desc: 'Vouvoiement, langage soutenu' },
  { id: 'semi-formel', label: 'Semi-formel', icon: '', desc: 'Équilibre entre formel et décontracté' },
  { id: 'décontracté', label: 'Décontracté', icon: '', desc: 'Tutoiement, langage naturel' },
]

const LANGUAGES = [
  { id: 'auto', label: 'Auto', icon: '', desc: 'Détecte la langue du client' },
  { id: 'fr', label: 'Français', icon: '', desc: 'Toujours en français' },
  { id: 'en', label: 'English', icon: '', desc: 'Always in English' },
  { id: 'wo', label: 'Wolof', icon: '', desc: 'Toujours en wolof' },
]

// Instructions suggérées par secteur
const SUGGESTED_INSTRUCTIONS = (sector = '') => {
  const s = sector.toLowerCase()
  if (s.includes('clinique') || s.includes('santé') || s.includes('médecin')) return [
    "Ne jamais donner de diagnostic médical par WhatsApp.",
    "Toujours orienter vers une consultation physique pour les urgences.",
    "Utiliser des termes simples, éviter le jargon médical.",
    "Exprimer de l'empathie face à la douleur ou l'inquiétude du patient.",
  ]
  if (s.includes('immo') || s.includes('btp')) return [
    "Toujours proposer une visite après chaque demande de prix.",
    "Mentionner les options de financement disponibles.",
    "Répondre avec des chiffres précis en FCFA.",
    "Mettre en avant la sécurité et la qualité des biens.",
  ]
  if (s.includes('restaurant') || s.includes('food')) return [
    "Toujours décrire les plats avec appétit et passion.",
    "Proposer les plats du jour en priorité.",
    "Mentionner les allergènes sur demande.",
    "Suggérer des accompagnements ou boissons.",
  ]
  return [
    "Toujours finir par une question pour maintenir la conversation.",
    "Proposer Wave ou Orange Money pour les paiements.",
    "Ne jamais promettre ce qui ne peut être tenu.",
    "Saluer le client par son prénom s'il est connu.",
  ]
}

// Aperçu WhatsApp selon le secteur
const PREVIEW_BY_SECTOR = (sector = '', tone = 'professionnel', lang = 'auto') => {
  const s = sector.toLowerCase()
  const toneMap = {
    professionnel: { greeting: 'Bonjour', close: 'N\'hésitez pas à nous contacter pour toute question.' },
    chaleureux: { greeting: 'Bonjour et bienvenue ', close: 'On est là pour vous ! ' },
    expert: { greeting: 'Bonjour', close: 'Je reste à votre disposition pour tout renseignement.' },
    humoristique: { greeting: 'Salut ', close: 'On va vous régaler, promis ! ' },
    tantine: { greeting: 'Bienvenue chez nous ! ', close: 'On s\'occupe de vous comme de la famille ! ❤️' },
  }
  const t = toneMap[tone] || toneMap.professionnel
  if (s.includes('clinique') || s.includes('santé')) return `${t.greeting} ! Je suis l'assistant de notre clinique. Comment puis-je vous aider ? Avez-vous besoin d'un rendez-vous ou d'informations sur nos services ? ${t.close}`
  if (s.includes('immo')) return `${t.greeting} ! Je suis l'assistant immobilier de notre agence. Vous recherchez un bien à acheter, louer ou mettre en gestion ? ${t.close}`
  if (s.includes('restaurant')) return `${t.greeting} ! Je suis l'assistant de notre restaurant. Vous souhaitez consulter notre menu, passer une commande ou réserver une table ? ${t.close}`
  return `${t.greeting} ! Je suis votre assistant WhatsApp. Comment puis-je vous aider aujourd'hui ? ${t.close}`
}

const PersonalityModal = ({ businessId, business, onClose, isDark }) => {
  const th = getTheme(isDark)
  const ph = getPlaceholders(business?.sector)
  const sectorTones = TONES_BY_SECTOR(business?.sector)
  const suggestions = SUGGESTED_INSTRUCTIONS(business?.sector)

  const [tone, setTone] = useState(sectorTones.default)
  const [style, setStyle] = useState('formel')
  const [language, setLanguage] = useState('auto')
  const [customInstructions, setCustomInstructions] = useState('')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    axios.get(`${API_URL}/api/businesses/${businessId}/modules`)
      .then(r => {
        const p = r.data.personality || {}
        setTone(p.tone || sectorTones.default)
        setStyle(p.style || 'formel')
        setLanguage(p.language || 'auto')
        setCustomInstructions(p.customInstructions || '')
      })
      .catch(() => {}).finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const addSuggestion = (text) => {
    const existing = customInstructions.trim()
    if (existing.includes(text)) {
      setCustomInstructions(existing.replace(text, '').replace(/\n\n/g, '\n').trim())
    } else {
      setCustomInstructions(existing ? `${existing}\n${text}` : text)
    }
  }

  const save = async () => {
    setSaving(true)
    try {
      await axios.put(`${API_URL}/api/businesses/${businessId}/personality`, {
        tone, style, language, customInstructions
      })
      onClose()
    } catch (e) { console.error(e) } finally { setSaving(false) }
  }

  const preview = PREVIEW_BY_SECTOR(business?.sector, tone, language)

  if (loading) return (
    <Modal title="Module Personnalité" moduleId="personality" icon={<MdPsychology size={20} className="text-white"/>} color="#ec4899" onClose={onClose} wide isDark={isDark}>
      <div className="text-center py-8" style={{color:th.textSub}}>Chargement...</div>
    </Modal>
  )

  return (
    <Modal title="Module Personnalité" moduleId="personality" icon={<MdPsychology size={20} className="text-white"/>} color="#ec4899" onClose={onClose} wide isDark={isDark}>

      {/* Secteur détecté */}
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl mb-4" style={{background:'rgba(236,72,153,0.08)',border:'1px solid rgba(236,72,153,0.2)'}}>
        <span style={{fontSize:'18px'}}></span>
        <div>
          <p className="text-xs font-semibold" style={{color:'#f9a8d4'}}>Personnalité adaptée à votre secteur</p>
          <p className="text-xs" style={{color:th.textSub}}>{business?.sector || 'Secteur non défini'}</p>
        </div>
      </div>

      <div className="space-y-5">

        {/* Ton */}
        <div>
          <label className="text-sm font-bold mb-3 block" style={{color:th.text}}> Ton de l'agent</label>
          <div className="grid grid-cols-2 gap-2">
            {sectorTones.tones.map(t => (
              <button key={t.id} onClick={() => setTone(t.id)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all border-2 text-left"
                style={{
                  background: tone===t.id ? 'rgba(236,72,153,0.12)' : th.inputBg,
                  borderColor: tone===t.id ? '#ec4899' : th.inputBorder,
                }}>
                <span style={{fontSize:'22px'}}>{t.icon}</span>
                <div>
                  <p className="text-sm font-semibold" style={{color: tone===t.id ? '#f9a8d4' : th.text}}>{t.label}</p>
                  <p className="text-xs" style={{color:th.textMuted}}>{t.desc}</p>
                </div>
                {tone===t.id && <MdCheck size={16} className="ml-auto flex-shrink-0" style={{color:'#ec4899'}}/>}
              </button>
            ))}
          </div>
        </div>

        {/* Style */}
        <div>
          <label className="text-sm font-bold mb-3 block" style={{color:th.text}}>✍️ Style de communication</label>
          <div className="grid grid-cols-3 gap-2">
            {STYLES.map(s => (
              <button key={s.id} onClick={() => setStyle(s.id)}
                className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl transition-all border-2 text-center"
                style={{
                  background: style===s.id ? 'rgba(99,102,241,0.12)' : th.inputBg,
                  borderColor: style===s.id ? '#6366f1' : th.inputBorder,
                }}>
                <span style={{fontSize:'20px'}}>{s.icon}</span>
                <p className="text-xs font-semibold" style={{color: style===s.id ? '#818cf8' : th.text}}>{s.label}</p>
                <p className="text-xs leading-tight" style={{color:th.textMuted}}>{s.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Langue */}
        <div>
          <label className="text-sm font-bold mb-3 block" style={{color:th.text}}> Langue de réponse</label>
          <div className="grid grid-cols-4 gap-2">
            {LANGUAGES.map(l => (
              <button key={l.id} onClick={() => setLanguage(l.id)}
                className="flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl transition-all border-2 text-center"
                style={{
                  background: language===l.id ? 'rgba(16,185,129,0.12)' : th.inputBg,
                  borderColor: language===l.id ? '#10b981' : th.inputBorder,
                }}>
                <span style={{fontSize:'18px'}}>{l.icon}</span>
                <p className="text-xs font-semibold" style={{color: language===l.id ? '#34d399' : th.text}}>{l.label}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Instructions personnalisées */}
        <div>
          <label className="text-sm font-bold mb-2 block" style={{color:th.text}}>⚙️ Instructions supplémentaires</label>

          {/* Suggestions par secteur */}
          <div className="flex flex-wrap gap-1.5 mb-2">
            {suggestions.map((s, i) => {
              const added = customInstructions.includes(s)
              return (
                <button key={i} onClick={() => addSuggestion(s)}
                  className="text-xs px-2.5 py-1 rounded-lg transition-all"
                  style={{
                    background: added ? 'rgba(236,72,153,0.15)' : th.inputBg,
                    color: added ? '#f9a8d4' : th.textSub,
                    border: `1px solid ${added ? 'rgba(236,72,153,0.3)' : th.inputBorder}`
                  }}>
                  {added ? '✓ ' : '+ '}{s.slice(0, 35)}{s.length > 35 ? '...' : ''}
                </button>
              )
            })}
          </div>

          <textarea
            value={customInstructions}
            onChange={e => setCustomInstructions(e.target.value)}
            placeholder={ph.skill||"Ex: Toujours mentionner notre numéro WhatsApp \u00e0 la fin de chaque réponse."}
            rows={3}
            className="w-full px-4 py-2.5 rounded-xl text-sm resize-none focus:outline-none focus:ring-1 focus:ring-pink-500"
            style={{background:th.inputBg, border:`1px solid ${th.inputBorder}`, color:th.text}}
          />
        </div>

        {/* Aperçu WhatsApp */}
        <div>
          <label className="text-sm font-bold mb-2 block" style={{color:th.text}}> Aperçu WhatsApp</label>
          <div className="rounded-2xl overflow-hidden" style={{background:'#e5ddd5'}}>
            <div className="px-3 py-2" style={{background:'#075e54'}}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{background:'rgba(255,255,255,0.2)'}}>
                  <MdSmartToy size={18} className="text-white"/>
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">{business?.name || 'Votre entreprise'}</p>
                  <p className="text-green-200 text-xs">Agent IA • En ligne</p>
                </div>
              </div>
            </div>
            <div className="p-3">
              <div className="max-w-xs ml-auto">
                <div className="bg-white rounded-2xl rounded-tr-sm px-4 py-3 shadow-sm">
                  <p className="text-gray-800 text-sm leading-relaxed">{preview}</p>
                  <p className="text-gray-400 text-xs mt-1 text-right">09:00 ✓✓</p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <button onClick={save} disabled={saving}
        className="w-full mt-5 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2"
        style={{background:'linear-gradient(135deg,#ec4899,#be185d)'}}>
        {saving ? 'Sauvegarde...' : <><MdCheck size={16}/>Sauvegarder la personnalité</>}
      </button>
    </Modal>
  )
}





// ─── PAIEMENTS EN ATTENTE MODAL ───
const PaiementsModal = ({ businessId, business, onClose, isDark }) => {
  const th = getTheme(isDark)
  const [payments, setPayments] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [confirming, setConfirming] = React.useState(null)

  const loadPayments = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/businesses/${businessId}/payments/pending`)
      setPayments(res.data || [])
    } catch(e) { console.error(e) }
    finally { setLoading(false) }
  }, [businessId])

  React.useEffect(() => { loadPayments() }, [businessId, loadPayments])

  const handleConfirm = async (paymentId, valid) => {
    setConfirming(paymentId)
    try {
      await axios.post(`${API_URL}/api/businesses/${businessId}/payments/${paymentId}/confirm`, { valid })
      await loadPayments()
    } catch(e) { alert('Erreur: ' + e.message) }
    finally { setConfirming(null) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.6)'}}>
      <div className="w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden" style={{background:th.card}}>
        <div className="flex items-center justify-between p-5" style={{borderBottom:`1px solid ${th.inputBorder}`}}>
          <div>
            <h2 className="font-bold text-lg" style={{color:th.text}}> Paiements à vérifier</h2>
            <p className="text-sm" style={{color:th.textMuted}}>Validez ou rejetez les paiements déclarés</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center" style={{background:th.inputBorder,color:th.textSub}}>✕</button>
        </div>
        <div className="p-5 max-h-96 overflow-y-auto">
          {loading ? (
            <p className="text-center py-8" style={{color:th.textMuted}}>Chargement...</p>
          ) : payments.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-4xl mb-3"></p>
              <p className="font-semibold" style={{color:th.text}}>Aucun paiement en attente</p>
              <p className="text-sm" style={{color:th.textMuted}}>Tous les paiements ont été traités</p>
            </div>
          ) : payments.map((p, i) => (
            <div key={i} className="rounded-xl p-4 mb-3" style={{background:th.inputBorder,border:`1px solid ${th.border}`}}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-bold" style={{color:th.text}}>{p.clientName}</p>
                  <p className="text-sm" style={{color:th.textMuted}}>{p.clientPhone}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-lg" style={{color:'#10b981'}}>{parseInt(p.amount||0).toLocaleString('fr-FR')} FCFA</p>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{background:'rgba(59,130,246,0.2)',color:'#3b82f6'}}>{p.method}</span>
                </div>
              </div>
              {p.senderName && <p className="text-sm mb-1" style={{color:th.textSub}}> Expéditeur: <strong>{p.senderName}</strong></p>}
              {p.time && <p className="text-sm mb-1" style={{color:th.textSub}}> Heure: {p.time}</p>}
              {p.orderDetails && <p className="text-sm mb-3" style={{color:th.textSub}}> Commande: {p.orderDetails}</p>}
              <div className="flex gap-2">
                <button onClick={() => handleConfirm(p.id, true)} disabled={confirming === p.id}
                  className="flex-1 py-2 rounded-xl font-bold text-sm text-white"
                  style={{background:'linear-gradient(135deg,#10b981,#059669)',opacity:confirming===p.id?0.5:1}}>
                   Valider — Paiement reçu
                </button>
                <button onClick={() => handleConfirm(p.id, false)} disabled={confirming === p.id}
                  className="flex-1 py-2 rounded-xl font-bold text-sm"
                  style={{background:'rgba(239,68,68,0.15)',color:'#f87171',opacity:confirming===p.id?0.5:1}}>
                   Rejeter
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="p-4" style={{borderTop:`1px solid ${th.border}`}}>
          <button onClick={loadPayments} className="w-full py-2 rounded-xl text-sm font-semibold" style={{background:th.inputBorder,color:th.textSub}}>
             Actualiser
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── WIDGET WHATSAPP MODAL ───
const WidgetModal = ({ business, onClose, isDark, universalNumber }) => {
  const th = getTheme(isDark)
  const [copied, setCopied] = React.useState(false)
  const [position, setPosition] = React.useState('bottom-right')
  const [color, setColor] = React.useState('#25d366')
  const [message, setMessage] = React.useState('Bonjour ! Je souhaite avoir des informations.')

  const businessPhone = (business?.phone || '').replace(/\D/g, '')
  // Numéro universel dynamique depuis config admin
  const uniNumber = (universalNumber || '221788270202').replace(/\D/g, '')
  const waLink = 'https://wa.me/' + uniNumber + '?text=' + businessPhone

  const widgetCode = `<!-- Widget WhatsApp — ${business?.name} -->
<style>
.wa-widget{position:fixed;${position.includes('right')?'right:20px':'left:20px'};${position.includes('bottom')?'bottom:20px':'top:20px'};z-index:9999;display:flex;flex-direction:column;align-items:${position.includes('right')?'flex-end':'flex-start'}}
.wa-btn{width:60px;height:60px;border-radius:50%;background:${color};display:flex;align-items:center;justify-content:center;cursor:pointer;box-shadow:0 4px 20px rgba(0,0,0,0.25);transition:transform .2s}
.wa-btn:hover{transform:scale(1.1)}
.wa-bubble{background:white;border-radius:12px;padding:12px 16px;margin-bottom:10px;box-shadow:0 4px 20px rgba(0,0,0,0.15);font-family:sans-serif;font-size:14px;max-width:220px;display:none}
.wa-widget:hover .wa-bubble{display:block}
</style>
<div class="wa-widget">
  <div class="wa-bubble"> Bonjour ! Comment puis-je vous aider ?</div>
  <a href="${waLink}" target="_blank" class="wa-btn">
    <svg width="32" height="32" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
  </a>
</div>
<!-- Fin Widget WhatsApp -->`

  const handleCopy = () => {
    navigator.clipboard.writeText(widgetCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.6)'}}>
      <div className="w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden" style={{background: isDark ? '#1e1b4b' : '#ffffff'}}>
        <div className="flex items-center justify-between p-5" style={{borderBottom:`1px solid ${th.inputBorder}`}}>
          <div>
            <h2 className="font-bold text-lg" style={{color:th.text}}>Widget WhatsApp</h2>
            <p className="text-sm" style={{color:th.textMuted}}>Copiez ce code sur votre site web</p>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <ModuleHelpBtn moduleId="widget" isDark={isDark}/>
            <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center" style={{background:th.inputBorder,color:th.textSub}}>✕</button>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs font-semibold mb-2 block" style={{color:th.textSub}}>Position</label>
              <select value={position} onChange={e => setPosition(e.target.value)}
                className="w-full p-2 rounded-lg text-sm" style={{background:th.inputBorder,color:th.text,border:'none'}}>
                <option value="bottom-right">Bas droite</option>
                <option value="bottom-left">Bas gauche</option>
                <option value="top-right">Haut droite</option>
                <option value="top-left">Haut gauche</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold mb-2 block" style={{color:th.textSub}}>Couleur</label>
              <div className="flex gap-2">
                {['#25d366','#1d4ed8','#f59e0b','#ef4444','#8b5cf6'].map(c => (
                  <button key={c} onClick={() => setColor(c)}
                    className="w-8 h-8 rounded-full flex-shrink-0"
                    style={{background:c, border: color===c ? '3px solid white' : '3px solid transparent', outline: color===c ? `2px solid ${c}` : 'none'}}/>
                ))}
              </div>
            </div>
          </div>
          <div className="mb-4">
            <label className="text-xs font-semibold mb-2 block" style={{color:th.textSub}}>Message d'accueil</label>
            <input value={message} onChange={e => setMessage(e.target.value)}
              className="w-full p-2 rounded-lg text-sm" style={{background:th.inputBorder,color:th.text,border:'none'}}/>
          </div>
          <div className="rounded-xl p-4 mb-4 overflow-x-auto" style={{background:'#0f172a'}}>
            <pre className="text-xs text-green-400 whitespace-pre-wrap">{widgetCode.slice(0, 200)}...</pre>
          </div>
          <div className="flex gap-3">
            <button onClick={handleCopy} className="flex-1 py-3 rounded-xl font-bold text-sm"
              style={{background: copied ? '#10b981' : '#25d366', color:'white'}}>
              {copied ? ' Copié !' : ' Copier le code'}
            </button>
            <a href={waLink} target="_blank" rel="noreferrer"
              className="px-4 py-3 rounded-xl font-bold text-sm"
              style={{background:th.inputBorder,color:th.text}}>
              ️ Tester
            </a>
          </div>
          <p className="text-xs text-center mt-3" style={{color:th.textMuted}}>
            Collez ce code dans le &lt;body&gt; de votre site web juste avant &lt;/body&gt;
          </p>
        </div>
      </div>
    </div>
  )
}

// ─── QR CODE MODAL ───
// eslint-disable-next-line no-unused-vars
const QRCodeModal = ({ business, onClose, isDark, universalNumber }) => {
  const th = getTheme(isDark)
  const [copied, setCopied] = React.useState(false)
  const [qrSize, setQrSize] = React.useState(200)

  // Numéro universel LeBinaire — depuis config admin ou fallback
  const uniNumber = (universalNumber || '221788270202').replace(/\D/g, '')

  // Code d'activation = numéro de l'entrepreneur (toujours unique en DB)
  const businessPhone = (business?.phone || '').replace(/\D/g, '')
  const activationCode = businessPhone

  // ── IMPORTANT: on encode le message pour que WhatsApp l'envoie AUTOMATIQUEMENT
  // L'URL wa.me avec ?text= pré-remplit ET envoie automatiquement sur mobile
  // Sur desktop ça pré-remplit seulement — mais 95% des clients sont sur mobile
  const waLink = `https://wa.me/${uniNumber}?text=${activationCode}`

  // Générer QR via API gratuite (pas de lib nécessaire)
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${qrSize}x${qrSize}&data=${encodeURIComponent(waLink)}&color=000000&bgcolor=ffffff&margin=10&format=png`

  const handleCopy = () => {
    navigator.clipboard.writeText(waLink)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDownload = async () => {
    try {
      const res = await fetch(qrUrl)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `qrcode-${business?.name?.toLowerCase().replace(/\s/g, '-') || 'business'}.png`
      a.click()
      URL.revokeObjectURL(url)
    } catch(e) {
      window.open(qrUrl, '_blank')
    }
  }

  const handlePrint = () => {
    const win = window.open('', '_blank')
    win.document.write(`
      <html><head><title>QR Code — ${business?.name}</title>
      <style>
        body { margin: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; font-family: sans-serif; background: white; }
        .card { text-align: center; padding: 40px; border: 2px solid #e5e7eb; border-radius: 16px; max-width: 320px; }
        h2 { margin: 0 0 4px; font-size: 20px; }
        p { color: #6b7280; font-size: 13px; margin: 0 0 20px; }
        img { border-radius: 8px; }
        .footer { margin-top: 16px; font-size: 12px; color: #9ca3af; }
      </style></head>
      <body>
        <div class="card">
          <h2>${business?.name}</h2>
          <p>Scannez pour nous contacter sur WhatsApp</p>
          <img src="${qrUrl}" width="200" height="200"/>
          <div class="footer"> ${business?.phone}</div>
        </div>
      </body></html>
    `)
    win.document.close()
    win.print()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.6)'}}>
      <div className="w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden" style={{background:th.card}}>
        {/* Header */}
        <div className="flex items-center justify-between p-5" style={{borderBottom:`1px solid ${th.inputBorder}`}}>
          <div>
            <h2 className="font-bold text-lg" style={{color:th.text}}>QR Code WhatsApp</h2>
            <p className="text-sm" style={{color:th.textMuted}}>Lien d'activation Agent IA de {business?.name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center" style={{background:th.inputBorder,color:th.textSub}}>✕</button>
        </div>

        {/* QR Code */}
        <div className="flex flex-col items-center p-6 gap-4">
          <div className="rounded-2xl p-4 shadow-inner" style={{background:'white',border:'3px solid #25d366'}}>
            <img src={qrUrl} alt="QR Code WhatsApp" width={qrSize} height={qrSize} style={{display:'block',borderRadius:8}}/>
          </div>

          {/* Taille */}
          <div className="flex items-center gap-3 w-full">
            <span className="text-sm" style={{color:th.textMuted}}>Taille:</span>
            {[150,200,300].map(s => (
              <button key={s} onClick={() => setQrSize(s)}
                className="px-3 py-1 rounded-lg text-sm font-semibold"
                style={{background: qrSize===s ? '#25d366' : th.inputBorder, color: qrSize===s ? 'white' : th.textSub}}>
                {s}px
              </button>
            ))}
          </div>

          {/* Lien */}
          <div className="w-full rounded-xl p-3 flex items-center gap-2" style={{background:th.inputBorder}}>
            <span className="text-xs flex-1 truncate" style={{color:th.textMuted}}>{waLink}</span>
            <button onClick={handleCopy} className="text-xs font-bold px-3 py-1 rounded-lg flex-shrink-0"
              style={{background: copied ? '#10b981' : '#25d366', color:'white'}}>
              {copied ? ' Copié' : ' Copier'}
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-3 w-full">
            <button onClick={handleDownload} className="flex-1 py-2.5 rounded-xl font-bold text-sm"
              style={{background:'linear-gradient(135deg,#25d366,#128c7e)',color:'white'}}>
               Télécharger
            </button>
            <button onClick={handlePrint} className="flex-1 py-2.5 rounded-xl font-bold text-sm"
              style={{background:th.inputBorder,color:th.text}}>
              ️ Imprimer
            </button>
          </div>

          <div className="rounded-xl p-3" style={{background:'rgba(37,211,102,0.08)',border:'1px solid rgba(37,211,102,0.2)'}}>
            <p className="text-xs font-semibold mb-1" style={{color:'#25d366'}}>💡 Comment ça marche ?</p>
            <p className="text-xs" style={{color:th.textSub}}>
              1️⃣ Votre client scanne ce QR Code avec son téléphone<br/>
              2️⃣ WhatsApp s'ouvre avec le message déjà prêt<br/>
              3️⃣ Il appuie simplement sur <strong>Envoyer ▶</strong> — sans rien taper<br/>
              4️⃣ L'Agent IA de <strong>{business?.name}</strong> lui répond instantanément<br/>
              ⚠️ Dites à vos clients de <strong>ne pas modifier</strong> le message avant d'envoyer
            </p>
          </div>
          <p className="text-xs text-center mt-2" style={{color:th.textMuted}}>
            Imprimez sur vos cartes de visite, affiches et devanture
          </p>
        </div>
      </div>
    </div>
  )
}

// ─── PLAYGROUND CONNECTÉ AU VRAI MOTEUR IA ───
const PlaygroundModal = ({ businessId, businessName, onClose, isDark }) => {
  const th = getTheme(isDark)
  const [msgs,setMsgs]=useState([{from:'ia',text:`Bonjour ! Je suis l'assistant IA de ${businessName}. Comment puis-je vous aider ? `}])
  const [input,setInput]=useState(''); const [typing,setTyping]=useState(false)
  const [recording,setRecording]=useState(false); const [recordTime,setRecordTime]=useState(0)
  const [history,setHistory]=useState([])
  const bottomRef=useRef(null); const timerRef=useRef(null)

  useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:'smooth'}) },[msgs,typing])

  const send = async (text) => {
    if(!text.trim()) return
    const userMsg = {role:'user', content:text}
    setMsgs(m=>[...m,{from:'user',text}]); setInput(''); setTyping(true)
    try {
      const res = await axios.post(`${API_URL}/api/businesses/${businessId}/playground`, {
        message: text,
        history: history
      })
      const aiText = res.data.response
      setMsgs(m=>[...m,{from:'ia',text:aiText}])
      setHistory(prev=>[...prev, userMsg, {role:'assistant',content:aiText}])
    } catch {
      setMsgs(m=>[...m,{from:'ia',text:'Désolé, une erreur est survenue. Réessayez.'}])
    } finally { setTyping(false) }
  }

  const reset = () => {
    setMsgs([{from:'ia',text:`Bonjour ! Je suis l'assistant IA de ${businessName}. Comment puis-je vous aider ? `}])
    setHistory([])
  }

  const startRecord=()=>{ setRecording(true); setRecordTime(0); timerRef.current=setInterval(()=>setRecordTime(t=>t+1),1000) }
  const stopRecord=()=>{ clearInterval(timerRef.current); setRecording(false); setMsgs(m=>[...m,{from:'user',text:' Message vocal (transcription bientôt disponible)',audio:true}]) }

  return (
    <Modal title="Playground — Tester l'Agent IA" icon={<MdPlayArrow size={20} className="text-white"/>} color="#10b981" onClose={onClose} wide isDark={isDark}>
      <div style={{height:'420px'}} className="flex flex-col">
        <div className="flex items-center justify-between px-3 py-2 rounded-xl mb-4 flex-shrink-0" style={{background:'rgba(16,185,129,0.1)',border:'1px solid rgba(16,185,129,0.25)'}}>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"/>
            <span className="text-xs font-medium text-green-300">Agent IA actif</span>
          </div>
          <button onClick={reset} className="flex items-center gap-1 text-xs text-slate-400 hover:text-white transition-colors">
            <MdRefresh size={14}/>Reset
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-3 mb-4 pr-1">
          {msgs.map((m,i)=>(
            <div key={i} className={`flex ${m.from==='user'?'justify-end':'justify-start'}`}>
              {m.from==='ia'&&(
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mr-2 mt-0.5" style={{background:'linear-gradient(135deg,#3b82f6,#1d4ed8)'}}>
                  <MdSmartToy size={14} className="text-white"/>
                </div>
              )}
              <div className={`max-w-xs px-4 py-3 rounded-2xl text-sm leading-relaxed`}
                style={{
                  background: m.from==='user' ? 'linear-gradient(135deg,#3b82f6,#1d4ed8)' : isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.06)',
                  color: m.from==='user' ? 'white' : th.text,
                  borderRadius: m.from==='user'?'18px 18px 4px 18px':'18px 18px 18px 4px'
                }}>
                {m.text}
              </div>
            </div>
          ))}
          {typing&&(
            <div className="flex justify-start">
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mr-2" style={{background:'linear-gradient(135deg,#3b82f6,#1d4ed8)'}}><MdSmartToy size={14} className="text-white"/></div>
              <div className="px-4 py-3 rounded-2xl flex gap-1 items-center" style={{background:isDark?'rgba(255,255,255,0.09)':'rgba(0,0,0,0.06)'}}>
                {[0,1,2].map(i=><div key={i} className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay:`${i*0.15}s`}}/>)}
              </div>
            </div>
          )}
          <div ref={bottomRef}/>
        </div>

        <div className="flex-shrink-0">
          {recording&&(
            <div className="flex items-center gap-3 mb-3 px-4 py-3 rounded-xl" style={{background:'rgba(239,68,68,0.15)',border:'1px solid rgba(239,68,68,0.3)'}}>
              <div className="flex gap-0.5">{[1,2,3,4,5,4,3,2,1].map((h,i)=><div key={i} className="w-1 bg-red-400 rounded-full animate-pulse" style={{height:`${h*6}px`}}/>)}</div>
              <span className="text-red-300 text-sm">{recordTime}s</span>
              <button onClick={stopRecord} className="ml-auto px-3 py-1 rounded-lg text-xs text-white" style={{background:'rgba(239,68,68,0.4)'}}>Envoyer</button>
            </div>
          )}
          <div className="flex gap-2">
            <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==='Enter'&&send(input)}
              placeholder="Testez votre agent IA en temps réel..."
              className="flex-1 px-4 py-3 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            <button onMouseDown={startRecord} onMouseUp={stopRecord}
              className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all ${recording?'scale-110':''}`}
              style={{background:recording?'linear-gradient(135deg,#ef4444,#dc2626)':th.inputBg, border:`1px solid ${th.inputBorder}`}}>
              <MdMic size={20} style={{color:recording?'white':th.textSub}}/>
            </button>
            <button onClick={()=>send(input)} disabled={typing}
              className="w-12 h-12 rounded-xl flex items-center justify-center text-white disabled:opacity-50"
              style={{background:'linear-gradient(135deg,#3b82f6,#1d4ed8)'}}><MdSend size={20}/></button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

// ─── PARAMÈTRES ENTREPRISE ───
// ─── PARAMÈTRES ENTREPRISE ───
const LEGAL_FORMS = ['SARL', 'SA', 'SAS', 'GIE', 'Entreprise individuelle', 'Association', 'Autre']

const SettingsModal = ({ businessId, business, onClose, isDark }) => {
  const th = getTheme(isDark)
  const fileInputRef = useRef(null)
  const [activeTab, setActiveTab] = useState('identite')
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [phone1Country, setPhone1Country] = useState(availableCountries[0])
  const [phone2Country, setPhone2Country] = useState(availableCountries[0])
  const [showDangerZone, setShowDangerZone] = useState(false)
  const [pinCode, setPinCode] = useState('')
  const [pinAction, setPinAction] = useState(null) // 'delete' | 'reset'
  const [pinError, setPinError] = useState('')
  const [pinLoading, setPinLoading] = useState(false)
  const [info, setInfo] = useState({
    logoUrl: '', logoPublicId: '', brandColor: '#3b82f6',
    legalName: '', ninea: '', rc: '', legalForm: '',
    address: '', phone1: '', phone2: '', email: '', website: '',
    footerText: '', paymentConditions: 'Paiement à 30 jours. Wave / Orange Money / Espèces acceptés.',
  })
  // Infos principales modifiables (nom, secteur, téléphone)
  // Parser le secteur existant pour pré-remplir SectorSelect
  const _rawSector = business?.sector || ''
  const _secParts = _rawSector.split(' — ')
  const _initSec = _secParts[0] || ''

  const [mainInfo, setMainInfo] = useState({
    name: business?.name || '',
    sector: _rawSector,
    sectorMain: _initSec,
    phone: (() => {
      const raw = (business?.phone || '').replace(/\D/g, '')
      if (raw.startsWith('221')) return raw.slice(3)
      if (raw.startsWith('00221')) return raw.slice(5)
      return raw
    })(),
  })
  const [savingMain, setSavingMain] = useState(false)
  const [mainPhoneCountry, setMainPhoneCountry] = useState(availableCountries[0])
  // Secteurs + subcategories pour tab général
  // Charger secteurs depuis API admin
  const [SECTORS_SN, setSECTORS_SN] = React.useState({})
  React.useEffect(() => {
    const cc = business?.country || 'SN'
    axios.get(`${API_URL}/api/sectors?country=${cc}`)
      .then(r => {
        const map = {}
        ;(r.data || []).forEach(s => { map[s.label] = s.subcategories || [] })
        setSECTORS_SN(map)
      })
      .catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const [phoneCheckStatus, setPhoneCheckStatus] = React.useState(null) // null | 'checking' | 'ok' | 'taken'
  const phoneCheckRef = useRef(null)
  const originalPhone = business?.phone || ''


  useEffect(() => {
    axios.get(`${API_URL}/api/businesses/${businessId}/company`)
      .then(r => { if (r.data && Object.keys(r.data).length > 0) setInfo(prev => ({...prev, ...r.data})) })
      .catch(() => {})
    if (business) {
      setInfo(prev => ({
        ...prev,
        legalName: prev.legalName || business.name || '',
        phone1: prev.phone1 || business.phone || '',
      }))
      const rawPhone2 = (business.phone || '').replace(/\D/g, '')
      const phoneDigits2 = rawPhone2.startsWith('221') ? rawPhone2.slice(3) : rawPhone2.startsWith('00221') ? rawPhone2.slice(5) : rawPhone2
      const rawSec2 = business.sector || ''
      const secMain2 = rawSec2.split(' — ')[0] || ''
      setMainInfo({ name: business.name || '', sector: rawSec2, sectorMain: secMain2, phone: phoneDigits2 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) { alert('Logo trop lourd. Max 5MB.'); return }
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('image', file)
      const res = await axios.post(`${API_URL}/api/upload/logo/${businessId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setInfo(prev => ({...prev, logoUrl: res.data.url, logoPublicId: res.data.publicId}))
    } catch (e) { alert('Erreur upload logo. Réessayez.') }
    finally { setUploading(false) }
  }

  // Générer automatiquement le pied de page
  const generateFooter = () => {
    const parts = []
    if (info.legalName) parts.push(info.legalName)
    if (info.rc) parts.push(`RC: ${info.rc}`)
    if (info.ninea) parts.push(`NINEA: ${info.ninea}`)
    if (info.address) parts.push(info.address)
    if (info.phone1) parts.push(info.phone1)
    if (info.email) parts.push(info.email)
    setInfo(prev => ({...prev, footerText: parts.join(' | ')}))
  }

  const save = async () => {
    setSaving(true)
    try {
      // Sauvegarder les infos entreprise (logo, légal, contact)
      await axios.put(`${API_URL}/api/businesses/${businessId}/company`, info)
      onClose()
    } catch (e) { console.error(e) } finally { setSaving(false) }
  }

  const saveMainInfo = async () => {
    if (!mainInfo.name.trim()) { alert('Le nom de l\'entreprise est requis'); return }
    setSavingMain(true)
    try {
      await axios.put(`${API_URL}/api/businesses/${businessId}/main`, {
        name: mainInfo.name.trim(),
        sector: mainInfo.sector,
        phone: mainInfo.phone.replace(/\s/g, ''),
      })
      alert('✅ Informations principales mises à jour !')
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message))
    } finally { setSavingMain(false) }
  }

  const handlePinAction = async () => {
    if (pinCode.length < 4) { setPinError('Le code PIN doit contenir au moins 4 chiffres'); return }
    setPinLoading(true)
    try {
      if (pinAction === 'reset') {
        await axios.post(`${API_URL}/api/businesses/${businessId}/reset-modules`, { pin: pinCode })
        alert(' Tous les modules ont été réinitialisés.')
        setPinCode(''); setPinAction(null); setShowDangerZone(false)
        onClose()
      } else if (pinAction === 'delete') {
        await axios.delete(`${API_URL}/api/businesses/${businessId}`, { data: { pin: pinCode } })
        alert(' Votre compte a été supprimé définitivement.')
        window.location.href = '/'
      }
    } catch (e) {
      setPinError(e.response?.data?.error || 'Code PIN incorrect')
    } finally { setPinLoading(false) }
  }

  // Field supprimé — utiliser inputs directs pour éviter re-render

  return (
    <Modal title="Paramètres Entreprise" moduleId="settings" icon={<MdSettings size={20} className="text-white"/>} color="#6366f1" onClose={onClose} wide isDark={isDark}>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 p-1 rounded-xl" style={{background:th.inputBg}}>
        {[
          {id:'general', label:'🏢 Général'},
          {id:'identite', label:'🎨 Identité'},
          {id:'legal', label:'📋 Légal'},
          {id:'contact', label:'📞 Contact'},
          {id:'documents', label:'📄 Documents'},
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
            style={activeTab===tab.id?{background:'linear-gradient(135deg,#6366f1,#4f46e5)',color:'white'}:{color:th.textSub}}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Général — Infos principales */}
      {/* Tab Général — Infos principales */}
      {/* Tab Général */}
      {activeTab === 'general' && (
        <div className="space-y-4">
          <div className="rounded-xl p-3" style={{background:'rgba(99,102,241,0.06)',border:'1px solid rgba(99,102,241,0.15)'}}>
            <p className="text-xs font-semibold mb-1" style={{color:'#818cf8'}}>ℹ️ Informations principales</p>
            <p className="text-xs" style={{color:'#94a3b8'}}>Modifiez le nom, secteur et numéro de votre entreprise.</p>
          </div>

          {/* Nom — capitalizeWords exact comme Register */}
          <div>
            <label className="block text-xs font-semibold mb-2" style={{color:th.textSub}}>Nom de l'entreprise *</label>
            <input
              type="text"
              value={mainInfo.name}
              onChange={e => setMainInfo(p => ({...p, name: capitalizeWords(e.target.value)}))}
              placeholder="Ex: Dakar Prime Immobilier"
              className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none"
              style={{background:th.inputBg, border:'1px solid '+th.inputBorder, color:th.text}}
            />
          </div>

          {/* Secteur principal */}
          <div>
            <label className="block text-xs font-semibold mb-2" style={{color:th.textSub}}>Secteur d'activité</label>
            <select
              value={mainInfo.sectorMain || mainInfo.sector.split(' — ')[0] || ''}
              onChange={e => {
                const sec = e.target.value
                setMainInfo(p => ({...p, sectorMain: sec, sector: sec}))
              }}
              className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none"
              style={{background:th.inputBg, border:'1px solid '+th.inputBorder, color:th.text}}>
              <option value="">-- Choisissez votre secteur --</option>
              {Object.keys(SECTORS_SN).sort((a,b) => a.localeCompare(b,'fr')).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Sous-secteur */}
          {(mainInfo.sectorMain || mainInfo.sector.split(' — ')[0]) && SECTORS_SN[mainInfo.sectorMain || mainInfo.sector.split(' — ')[0]] && (
            <div>
              <label className="block text-xs font-semibold mb-2" style={{color:th.textSub}}>Précisez votre activité</label>
              <select
                value={mainInfo.sector.split(' — ')[1] || ''}
                onChange={e => {
                  const sub = e.target.value
                  const sec = mainInfo.sectorMain || mainInfo.sector.split(' — ')[0]
                  setMainInfo(p => ({...p, sector: sub ? sec + ' — ' + sub : sec}))
                }}
                className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none"
                style={{background:th.inputBg, border:'1px solid '+th.inputBorder, color:th.text}}>
                <option value="">-- Précisez --</option>
                {[...(SECTORS_SN[mainInfo.sectorMain || mainInfo.sector.split(' — ')[0]] || [])].sort((a,b) => a.localeCompare(b,'fr')).map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}

          {/* Téléphone avec vérification temps réel */}
          <div>
            <label className="block text-xs font-semibold mb-2" style={{color:th.textSub}}>Numéro WhatsApp principal</label>
            <PhoneInput
              value={mainInfo.phone}
              onChange={e => {
                const digits = e.target.value.replace(/\D/g,'')
                setMainInfo(p => ({...p, phone: digits}))
                setPhoneCheckStatus(null)
                const fullPhone = (mainPhoneCountry?.dial_code || '+221').replace('+','') + digits
                const origClean = originalPhone.replace(/\D/g,'')
                if (digits.length >= 8 && fullPhone !== origClean) {
                  setPhoneCheckStatus('checking')
                  if (phoneCheckRef.current) clearTimeout(phoneCheckRef.current)
                  phoneCheckRef.current = setTimeout(async () => {
                    try {
                      const res = await axios.get(`${API_URL}/api/auth/check-phone?phone=${fullPhone}&excludeId=${businessId}`)
                      setPhoneCheckStatus(res.data.exists ? 'taken' : 'ok')
                    } catch { setPhoneCheckStatus(null) }
                  }, 800)
                }
              }}
              selectedCountry={mainPhoneCountry}
              setSelectedCountry={c => { setMainPhoneCountry(c); setPhoneCheckStatus(null) }}
              placeholder="77 000 00 00"
            />
            {phoneCheckStatus === 'checking' && <p className="text-xs mt-1" style={{color:'#94a3b8'}}>⏳ Vérification...</p>}
            {phoneCheckStatus === 'taken' && <p className="text-xs mt-1 font-semibold" style={{color:'#ef4444'}}>❌ Ce numéro est déjà utilisé par un autre compte</p>}
            {phoneCheckStatus === 'ok' && <p className="text-xs mt-1 font-semibold" style={{color:'#10b981'}}>✅ Numéro disponible</p>}
            {!phoneCheckStatus && <p className="text-xs mt-1" style={{color:th.textMuted}}>⚠️ Ce numéro sert de code d'activation pour vos clients</p>}
          </div>

          <button onClick={saveMainInfo}
            disabled={savingMain || phoneCheckStatus === 'taken' || phoneCheckStatus === 'checking'}
            className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2"
            style={{background:'linear-gradient(135deg,#6366f1,#4f46e5)', opacity:(savingMain || phoneCheckStatus==='taken' || phoneCheckStatus==='checking')?0.5:1}}>
            {savingMain ? 'Sauvegarde...' : <><MdCheck size={16}/>Sauvegarder les informations principales</>}
          </button>
        </div>
      )}

      {activeTab === 'identite' && (
        <div className="space-y-4">
          {/* Logo */}
          <div>
            <label className="text-xs font-semibold mb-2 block" style={{color:th.textSub}}>Logo de l'entreprise</label>
            <div className="flex items-center gap-4">
              {/* Aperçu logo */}
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                style={{background:info.logoUrl?'transparent':th.inputBg, border:`2px dashed ${info.logoUrl?'rgba(99,102,241,0.5)':th.inputBorder}`}}>
                {info.logoUrl ? (
                  <img src={info.logoUrl} alt="logo" className="w-full h-full object-contain p-1"/>
                ) : (
                  <div className="text-center">
                    <MdSmartToy size={24} style={{color:th.textMuted, margin:'0 auto'}}/>
                    <p className="text-xs mt-1" style={{color:th.textMuted}}>Logo</p>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all mb-2 flex items-center justify-center gap-2"
                  style={{background:'rgba(99,102,241,0.15)',color:'#818cf8',border:'1px solid rgba(99,102,241,0.3)'}}>
                  {uploading ? (
                    <><div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin"/>Upload en cours...</>
                  ) : (
                    <><MdUpload size={16}/>{info.logoUrl ? 'Changer le logo' : 'Uploader le logo'}</>
                  )}
                </button>
                {info.logoUrl && (
                  <button onClick={() => setInfo(prev => ({...prev, logoUrl:'', logoPublicId:''}))}
                    className="w-full py-2 rounded-xl text-xs transition-all flex items-center justify-center gap-1"
                    style={{background:'rgba(239,68,68,0.1)',color:'#f87171',border:'1px solid rgba(239,68,68,0.2)'}}>
                    <MdDelete size={13}/>Supprimer le logo
                  </button>
                )}
                <p className="text-xs mt-2" style={{color:th.textMuted}}>PNG, JPG, SVG — max 5MB. Fond transparent recommandé.</p>
              </div>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden"/>
          </div>

          {/* Couleur de marque */}
          <div>
            <label className="text-xs font-semibold mb-2 block" style={{color:th.textSub}}>Couleur principale de la marque</label>
            <div className="flex items-center gap-3">
              <input type="color" value={info.brandColor} onChange={e=>setInfo(prev=>({...prev,brandColor:e.target.value}))}
                className="w-12 h-12 rounded-xl cursor-pointer border-0 p-1"
                style={{background:th.inputBg, border:`1px solid ${th.inputBorder}`}}/>
              <div className="flex-1">
                <input value={info.brandColor} onChange={e=>setInfo(prev=>({...prev,brandColor:e.target.value}))}
                  placeholder="#3b82f6"
                  className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none font-mono"
                  style={{background:th.inputBg, border:`1px solid ${th.inputBorder}`, color:th.text}}/>
              </div>
              {/* Palettes suggérées */}
              <div className="flex gap-1.5">
                {['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#0f172a'].map(c => (
                  <button key={c} onClick={() => setInfo(prev=>({...prev,brandColor:c}))}
                    className="w-7 h-7 rounded-lg transition-all hover:scale-110"
                    style={{background:c, border:info.brandColor===c?'2px solid white':'2px solid transparent', boxShadow:info.brandColor===c?'0 0 0 2px '+c:''}}/>
                ))}
              </div>
            </div>
          </div>

          {/* Aperçu entête document */}
          {(info.logoUrl || info.legalName) && (
            <div className="rounded-xl p-4" style={{background:'rgba(99,102,241,0.08)',border:'1px solid rgba(99,102,241,0.2)'}}>
              <p className="text-xs font-semibold mb-3" style={{color:'#818cf8'}}> Aperçu entête document</p>
              <div className="flex items-center gap-4 p-3 rounded-lg bg-white">
                {info.logoUrl && <img src={info.logoUrl} alt="logo" className="h-12 object-contain"/>}
                <div>
                  <p className="font-bold text-gray-800 text-sm">{info.legalName || business?.name}</p>
                  {info.legalForm && <p className="text-gray-500 text-xs">{info.legalForm}</p>}
                  {info.address && <p className="text-gray-400 text-xs">{info.address}</p>}
                </div>
                <div className="ml-auto text-right">
                  {info.phone1 && <p className="text-gray-600 text-xs">{info.phone1}</p>}
                  {info.email && <p className="text-gray-400 text-xs">{info.email}</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab Légal */}
      {activeTab === 'legal' && (
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Raison sociale / Nom légal</label>
            <input value={info.legalName} onChange={e=>setInfo(p=>({...p,legalName:e.target.value}))}
              placeholder={business?.name || "Ex: Merco Immo SARL"}
              className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          </div>

          <div>
            <label className="text-xs font-semibold mb-2 block" style={{color:th.textSub}}>Forme juridique</label>
            <div className="flex flex-wrap gap-2">
              {LEGAL_FORMS.map(f => (
                <button key={f} onClick={() => setInfo(p=>({...p,legalForm:f}))}
                  className="px-3 py-1.5 rounded-xl text-xs font-medium transition-all border-2"
                  style={{
                    background: info.legalForm===f?'rgba(99,102,241,0.15)':th.inputBg,
                    color: info.legalForm===f?'#818cf8':th.textSub,
                    borderColor: info.legalForm===f?'#6366f1':th.inputBorder,
                  }}>
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>NINEA</label>
              <input value={info.ninea} onChange={e=>setInfo(p=>({...p,ninea:e.target.value}))}
                placeholder="Ex: 123456789012"
                className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Registre du Commerce (RC)</label>
              <input value={info.rc} onChange={e=>setInfo(p=>({...p,rc:e.target.value}))}
                placeholder="Ex: SN-DKR-2024-XXX"
                className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            </div>
          </div>

          {/* Alerte NINEA */}
          <div className="flex items-start gap-3 px-4 py-3 rounded-xl" style={{background:'rgba(245,158,11,0.1)',border:'1px solid rgba(245,158,11,0.25)'}}>
            <span style={{fontSize:'18px'}}></span>
            <div>
              <p className="text-xs font-semibold" style={{color:'#fbbf24'}}>Documents officiels</p>
              <p className="text-xs mt-0.5" style={{color:th.textSub}}>Le NINEA et le RC sont obligatoires sur les factures et devis officiels au Sénégal.</p>
            </div>
          </div>
        </div>
      )}

      {/* Tab Contact */}
      {activeTab === 'contact' && (
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Adresse complète</label>
            <input value={info.address} onChange={e=>setInfo(p=>({...p,address:toTitleCase(e.target.value)}))}
              placeholder="Ex: Ouest Foire, près de la Case des Tout-Petits, Dakar"
              className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Téléphone principal</label>
              <PhoneInput
                value={info.phone1}
                onChange={e=>setInfo(p=>({...p,phone1:e.target.value.replace(/\D/g,'')}))}
                selectedCountry={phone1Country}
                setSelectedCountry={setPhone1Country}
                onCountryChange={c=>{setPhone1Country(c);setInfo(p=>({...p,phone1:''}))}}
                error=""
                phonePreviewLabel="Numéro principal"
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Téléphone secondaire</label>
              <PhoneInput
                value={info.phone2}
                onChange={e=>setInfo(p=>({...p,phone2:e.target.value.replace(/\D/g,'')}))}
                selectedCountry={phone2Country}
                setSelectedCountry={setPhone2Country}
                onCountryChange={c=>{setPhone2Country(c);setInfo(p=>({...p,phone2:''}))}}
                error=""
                phonePreviewLabel="Numéro secondaire"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Email</label>
            <input type="email" value={info.email} onChange={e=>setInfo(p=>({...p,email:e.target.value}))}
              placeholder="mail@mail.com"
              className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Site web</label>
            <input value={info.website} onChange={e=>setInfo(p=>({...p,website:e.target.value}))}
              placeholder="www.lebinaire.com"
              className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          </div>
        </div>
      )}

      {/* Tab Documents */}
      {activeTab === 'documents' && (
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold" style={{color:th.textSub}}>Pied de page des documents</label>
              <button onClick={generateFooter} className="text-xs px-2 py-1 rounded-lg flex items-center gap-1"
                style={{background:'rgba(99,102,241,0.15)',color:'#818cf8'}}>
                <MdAutoAwesome size={12}/>Générer auto
              </button>
            </div>
            <textarea value={info.footerText} onChange={e=>setInfo(p=>({...p,footerText:e.target.value}))}
              placeholder=""
              rows={3} className="w-full px-4 py-2.5 rounded-xl text-sm resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500"
              style={{background:th.inputBg, border:`1px solid ${th.inputBorder}`, color:th.text}}/>
          </div>

          <div>
            <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Conditions de paiement par défaut</label>
            <textarea value={info.paymentConditions} onChange={e=>setInfo(p=>({...p,paymentConditions:e.target.value}))}
              rows={2} className="w-full px-4 py-2.5 rounded-xl text-sm resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500"
              style={{background:th.inputBg, border:`1px solid ${th.inputBorder}`, color:th.text}}/>
          </div>

          {/* Liens de paiement marchand */}
          <div className="rounded-2xl p-5 mt-4" style={{background:th.inputBorder,border:`1px solid ${th.border}`}}>
            <h4 className="font-bold text-sm mb-4 flex items-center gap-2" style={{color:th.text}}>
               Liens de paiement marchand
              <span className="text-xs font-normal px-2 py-0.5 rounded-full" style={{background:'rgba(16,185,129,0.15)',color:'#10b981'}}>Sécurisé & Irrévocable</span>
            </h4>
            <p className="text-xs mb-4" style={{color:th.textMuted}}>Ces liens sont envoyés automatiquement au client quand il veut payer via WhatsApp. Le paiement par lien marchand est irrévocable.</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}> Lien marchand Wave</label>
                <input value={info.waveMerchantLink||''} onChange={e=>setInfo(p=>({...p,waveMerchantLink:e.target.value}))}
                  placeholder="https://pay.wave.com/m/votre-id/c/sn/"
                  className="w-full px-3 py-2 rounded-xl text-sm focus:outline-none"
                  style={{background:th.card,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
                <p className="text-xs mt-1" style={{color:th.textMuted}}>Trouvez ce lien dans Wave Business → Mon QR code → Partager le lien</p>
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}> Lien marchand Orange Money</label>
                <input value={info.orangeMerchantLink||''} onChange={e=>setInfo(p=>({...p,orangeMerchantLink:e.target.value}))}
                  placeholder="https://om.orange.sn/pay/votre-id"
                  className="w-full px-3 py-2 rounded-xl text-sm focus:outline-none"
                  style={{background:th.card,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
                <p className="text-xs mt-1" style={{color:th.textMuted}}>Trouvez ce lien dans Orange Money Business → Mon compte marchand</p>
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>️ QR Code Wave (upload)</label>
                <div className="flex items-center gap-3">
                  {info.waveQrUrl && <img src={info.waveQrUrl} alt="QR Wave" className="w-16 h-16 rounded-lg object-contain" style={{background:'white',padding:4}}/>}
                  <label className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm cursor-pointer font-semibold"
                    style={{background:'rgba(29,196,239,0.15)',color:'#1dc4ef',border:'1px solid rgba(29,196,239,0.2)'}}>
                     Uploader QR Wave
                    <input type="file" accept="image/*" className="hidden" onChange={async e=>{
                      const file = e.target.files[0]; if(!file) return
                      // Aperçu + confirmation avant upload
                      const previewUrl = URL.createObjectURL(file)
                      const img = document.createElement('img')
                      img.src = previewUrl
                      const confirmed = window.confirm(
                        ' Vous uploadez ce fichier comme QR CODE WAVE.\n\n' +
                        'Fichier: ' + file.name + '\n\n' +
                        ' Assurez-vous que c\'est bien votre QR Wave (fond bleu ciel) et NON Orange Money.\n\n' +
                        'Confirmez-vous ?'
                      )
                      if(!confirmed) { e.target.value = ''; return }
                      const fd = new FormData()
                      fd.append('file', file)
                      fd.append('folder', 'qr-codes')
                      fd.append('qrType', 'wave')
                      try {
                        const res = await axios.post(`${API_URL}/api/businesses/${businessId}/upload`, fd)
                        // Auto-insérer le lien extrait du QR dans le champ Wave
                        const waveLink = res.data.decodedUrl || res.data.url
                        setInfo(p=>({...p, waveQrUrl: res.data.url, waveMerchantLink: waveLink}))
                        alert(' QR Wave uploadé !\nLien extrait automatiquement: ' + waveLink)
                      } catch(err) { alert('Erreur upload: ' + (err.response?.data?.error || err.message)) }
                      e.target.value = ''
                    }}/>
                  </label>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>️ QR Code Orange Money (upload)</label>
                <div className="flex items-center gap-3">
                  {info.orangeQrUrl && <img src={info.orangeQrUrl} alt="QR Orange Money" className="w-16 h-16 rounded-lg object-contain" style={{background:'white',padding:4}}/>}
                  <label className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm cursor-pointer font-semibold"
                    style={{background:'rgba(255,98,0,0.15)',color:'#ff6200',border:'1px solid rgba(255,98,0,0.2)'}}>
                     Uploader QR Orange Money
                    <input type="file" accept="image/*" className="hidden" onChange={async e=>{
                      const file = e.target.files[0]; if(!file) return
                      // Aperçu + confirmation avant upload
                      const confirmed = window.confirm(
                        ' Vous uploadez ce fichier comme QR CODE ORANGE MONEY.\n\n' +
                        'Fichier: ' + file.name + '\n\n' +
                        ' Assurez-vous que c\'est bien votre QR Orange Money (fond orange) et NON Wave.\n\n' +
                        'Confirmez-vous ?'
                      )
                      if(!confirmed) { e.target.value = ''; return }
                      const fd = new FormData()
                      fd.append('file', file)
                      fd.append('folder', 'qr-codes')
                      fd.append('qrType', 'orange')
                      try {
                        const res = await axios.post(`${API_URL}/api/businesses/${businessId}/upload`, fd)
                        // Auto-insérer le lien extrait du QR dans le champ Orange
                        const orangeLink = res.data.decodedUrl || res.data.url
                        setInfo(p=>({...p, orangeQrUrl: res.data.url, orangeMerchantLink: orangeLink}))
                        alert(' QR Orange Money uploadé !\nLien extrait automatiquement: ' + orangeLink)
                      } catch(err) { alert('Erreur upload: ' + (err.response?.data?.error || err.message)) }
                      e.target.value = ''
                    }}/>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Aperçu pied de page */}
          {info.footerText && (
            <div className="rounded-xl overflow-hidden" style={{border:'1px solid rgba(99,102,241,0.2)'}}>
              <div className="px-4 py-2" style={{background:'rgba(99,102,241,0.1)'}}>
                <p className="text-xs font-semibold" style={{color:'#818cf8'}}> Aperçu pied de page</p>
              </div>
              <div className="p-4 bg-white">
                <div className="border-t border-gray-200 pt-3 text-center">
                  <p className="text-gray-400 text-xs">{info.footerText}</p>
                  {info.paymentConditions && (
                    <p className="text-gray-300 text-xs mt-1">{info.paymentConditions}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Numérotation automatique */}
          <div className="flex items-center justify-between px-4 py-3 rounded-xl" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`}}>
            <div>
              <p className="text-sm font-medium" style={{color:th.text}}>Numérotation automatique</p>
              <p className="text-xs" style={{color:th.textSub}}>Chaque devis reçoit un numéro unique (DEV-2026-001)</p>
            </div>
            <div className="px-3 py-1 rounded-lg text-xs font-semibold" style={{background:'rgba(16,185,129,0.15)',color:'#34d399'}}>
              Activé
            </div>
          </div>
        </div>
      )}

      <button onClick={save} disabled={saving}
        className="w-full mt-5 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2"
        style={{background:'linear-gradient(135deg,#6366f1,#4f46e5)'}}>
        {saving?'Sauvegarde...':<><MdCheck size={16}/>Sauvegarder les paramètres</>}
      </button>

      {/* ─── DANGER ZONE ─── */}
      <div className="mt-6 rounded-xl overflow-hidden" style={{border:'1px solid rgba(239,68,68,0.3)'}}>
        <button onClick={() => setShowDangerZone(!showDangerZone)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold"
          style={{background:'rgba(239,68,68,0.08)',color:'#f87171'}}>
          <span> Zone de danger</span>
          <span>{showDangerZone ? '▲' : '▼'}</span>
        </button>
        {showDangerZone && (
          <div className="p-4 space-y-3" style={{background:'rgba(239,68,68,0.04)'}}>
            {!pinAction ? (
              <div className="space-y-2">
                <button onClick={() => { setPinAction('reset'); setPinError('') }}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                  style={{background:'rgba(245,158,11,0.15)',color:'#fbbf24',border:'1px solid rgba(245,158,11,0.3)'}}>
                   Réinitialiser tous les modules
                </button>
                <button onClick={() => { setPinAction('delete'); setPinError('') }}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                  style={{background:'rgba(239,68,68,0.15)',color:'#f87171',border:'1px solid rgba(239,68,68,0.3)'}}>
                   Supprimer mon compte définitivement
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="px-3 py-2 rounded-lg" style={{background:'rgba(239,68,68,0.1)'}}>
                  <p className="text-xs font-semibold" style={{color:'#f87171'}}>
                    {pinAction === 'delete' ? ' Suppression définitive — toutes vos données seront perdues.' : ' Tous vos modules seront réinitialisés à zéro.'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Code PIN de votre compte</label>
                  <input type="password" value={pinCode} onChange={e => { setPinCode(e.target.value.replace(/\D/g,'')); setPinError('') }}
                    placeholder="Entrez le PIN choisi lors de votre inscription"
                    maxLength={8} inputMode="numeric"
                    className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none"
                    style={{background:th.inputBg,border:`1px solid ${pinError?'#ef4444':th.inputBorder}`,color:th.text}}/>
                  {pinError && <p className="text-xs mt-1" style={{color:'#f87171'}}>{pinError}</p>}
                  <p className="text-xs mt-1" style={{color:th.textMuted}}>C'est le code PIN que vous avez choisi lors de votre inscription</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setPinAction(null); setPinCode(''); setPinError('') }}
                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold"
                    style={{background:th.inputBg,color:th.textSub}}>
                    Annuler
                  </button>
                  <button onClick={handlePinAction} disabled={pinLoading || pinCode.length < 4}
                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40"
                    style={{background:pinAction==='delete'?'#ef4444':'#f59e0b'}}>
                    {pinLoading ? 'Vérification...' : pinAction === 'delete' ? 'Supprimer définitivement' : 'Réinitialiser'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}


// ─── MODULE STATISTIQUES ───
// ─── MODULE STATISTIQUES ───
const StatsModal = ({ businessId, business, onClose, isDark }) => {
  const th = getTheme(isDark)
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    axios.get(`${API_URL}/api/stats/${businessId}`)
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const maxDaily = stats ? Math.max(...(stats.dailyMessages || []).map(d => d.count), 1) : 1

  return (
    <Modal title="Statistiques" moduleId="stats" icon={<MdBarChart size={20} className="text-white"/>} color="#3b82f6" onClose={onClose} wide isDark={isDark}>
      {loading ? (
        <div className="text-center py-12">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"/>
          <p className="text-sm" style={{color:th.textSub}}>Chargement des statistiques...</p>
        </div>
      ) : !stats ? (
        <div className="text-center py-8" style={{color:th.textSub}}>Erreur de chargement</div>
      ) : (
        <div className="space-y-5">

          {/* KPIs principaux */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Messages reçus', value: stats.totalUserMessages, color:'#60a5fa', bg:'rgba(59,130,246,0.1)', icon:'' },
              { label: 'Réponses IA', value: stats.totalAIMessages, color:'#34d399', bg:'rgba(16,185,129,0.1)', icon:'' },
              { label: 'Clients total', value: stats.totalClients, color:'#a78bfa', bg:'rgba(139,92,246,0.1)', icon:'' },
              { label: 'Taux de réponse', value: stats.totalUserMessages > 0 ? Math.round((stats.totalAIMessages/stats.totalUserMessages)*100)+'%' : '0%', color:'#fbbf24', bg:'rgba(245,158,11,0.1)', icon:'⚡' },
            ].map((s,i) => (
              <div key={i} className="rounded-2xl p-4" style={{background:s.bg, border:`1px solid ${s.color}33`}}>
                <div className="flex items-center gap-2 mb-1">
                  <span style={{fontSize:'18px'}}>{s.icon}</span>
                  <p className="text-xs font-medium" style={{color:th.textSub}}>{s.label}</p>
                </div>
                <p className="text-3xl font-bold" style={{color:s.color}}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Nouveaux clients */}
          {stats.newClientsThisWeek > 0 && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{background:'rgba(16,185,129,0.08)',border:'1px solid rgba(16,185,129,0.2)'}}>
              <span style={{fontSize:'20px'}}></span>
              <div>
                <p className="text-sm font-semibold" style={{color:'#34d399'}}>{stats.newClientsThisWeek} nouveau(x) client(s) cette semaine</p>
                <p className="text-xs" style={{color:th.textSub}}>Par rapport à la semaine dernière</p>
              </div>
            </div>
          )}

          {/* Graphique messages par jour */}
          <div>
            <p className="text-sm font-bold mb-3" style={{color:th.text}}> Messages des 7 derniers jours</p>
            <div className="flex items-end gap-1.5 h-28">
              {(stats.dailyMessages || []).map((d, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <p className="text-xs font-bold" style={{color:'#60a5fa'}}>{d.count > 0 ? d.count : ''}</p>
                  <div className="w-full rounded-t-lg transition-all" style={{
                    height: `${Math.max((d.count / maxDaily) * 80, d.count > 0 ? 8 : 2)}px`,
                    background: d.count > 0 ? 'linear-gradient(180deg,#60a5fa,#3b82f6)' : th.inputBg,
                    minHeight: '2px'
                  }}/>
                  <p className="text-xs text-center leading-tight" style={{color:th.textMuted, fontSize:'9px'}}>{d.day}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Top questions */}
          {stats.topQuestions?.length > 0 && (
            <div>
              <p className="text-sm font-bold mb-3" style={{color:th.text}}>❓ Questions les plus posées</p>
              <div className="space-y-2">
                {stats.topQuestions.map((q, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2.5 rounded-xl" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`}}>
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                      style={{background:`hsl(${210 + i*20},80%,55%)`}}>{i+1}</span>
                    <p className="text-sm flex-1 truncate" style={{color:th.text}}>{q.question}</p>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{background:'rgba(59,130,246,0.15)',color:'#60a5fa'}}>{q.count}x</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Modules configurés */}
          <div>
            <p className="text-sm font-bold mb-3" style={{color:th.text}}>⚙️ État des modules</p>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(stats.modules || {}).map(([mod, count]) => (
                <div key={mod} className="px-3 py-2.5 rounded-xl text-center" style={{background:count>0?'rgba(16,185,129,0.1)':'rgba(107,114,128,0.1)',border:`1px solid ${count>0?'rgba(16,185,129,0.2)':'rgba(107,114,128,0.2)'}`}}>
                  <p className="text-lg font-bold" style={{color:count>0?'#34d399':'#9ca3af'}}>{count}</p>
                  <p className="text-xs capitalize" style={{color:th.textMuted}}>{mod}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      )}
    </Modal>
  )
}


// ─── MODULE CONVERSATIONS ───
// ─── MODULE CONVERSATIONS ───
const ConversationsModal = ({ businessId, business, onClose, isDark }) => {
  const th = getTheme(isDark)
  const [conversations, setConversations] = useState([])
  const [selected, setSelected] = useState(null)
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [sending, setSending] = useState(false)
  const [searchConv, setSearchConv] = useState('')
  const messagesEndRef = useRef(null)

  useEffect(() => {
    loadConversations()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const loadConversations = () => {
    setLoading(true)
    axios.get(`${API_URL}/api/conversations/${businessId}`)
      .then(r => setConversations(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  const openConversation = (conv) => {
    setSelected(conv)
    setLoadingHistory(true)
    axios.get(`${API_URL}/api/conversations/${businessId}/client/${conv.id}`)
      .then(r => {
        setHistory(r.data.history || [])
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
      })
      .catch(() => {})
      .finally(() => setLoadingHistory(false))
  }

  const sendReply = async () => {
    if (!replyText.trim() || !selected) return
    setSending(true)
    try {
      await axios.post(`${API_URL}/api/conversations/${businessId}/client/${selected.id}/reply`, {
        message: replyText
      })
      setHistory(prev => [...prev, {
        role: 'assistant',
        content: `[MANUEL] ${replyText}`,
        timestamp: new Date(),
        isManual: true
      }])
      setReplyText('')
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
    } catch (e) {
      alert('Erreur envoi message')
    } finally { setSending(false) }
  }

  const deleteConv = async (convId) => {
    if (!window.confirm('Supprimer cette conversation ?')) return
    await axios.delete(`${API_URL}/api/conversations/${businessId}/client/${convId}`)
    setConversations(prev => prev.filter(c => c.id !== convId))
    if (selected?.id === convId) { setSelected(null); setHistory([]) }
  }

  const filtered = conversations.filter(c =>
    !searchConv || c.phone.includes(searchConv) || c.lastMessage.toLowerCase().includes(searchConv.toLowerCase())
  )

  const formatTime = (t) => {
    if (!t) return ''
    const d = new Date(t)
    const now = new Date()
    const diff = now - d
    if (diff < 60000) return 'À l\'instant'
    if (diff < 3600000) return `${Math.floor(diff/60000)}min`
    if (diff < 86400000) return d.toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'})
    return d.toLocaleDateString('fr-FR', {day:'2-digit',month:'short'})
  }

  return (
    <Modal title="Conversations WhatsApp" moduleId="conversations" icon={<MdChat size={20} className="text-white"/>} color="#25d366" onClose={onClose} wide isDark={isDark}>
      <div className="flex gap-3 h-96">

        {/* Liste conversations */}
        <div className="w-2/5 flex flex-col gap-2">
          <input value={searchConv} onChange={e=>setSearchConv(e.target.value)}
            placeholder="Rechercher..." className="px-3 py-2 rounded-xl text-xs focus:outline-none"
            style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>

          <div className="flex-1 overflow-y-auto space-y-1 pr-1">
            {loading ? (
              <div className="text-center py-6" style={{color:th.textSub}}>Chargement...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm" style={{color:th.textSub}}>Aucune conversation</p>
                <p className="text-xs mt-1" style={{color:th.textMuted}}>Les messages WhatsApp apparaîtront ici</p>
              </div>
            ) : filtered.map(conv => (
              <div key={conv.id} onClick={() => openConversation(conv)}
                className="px-3 py-2.5 rounded-xl cursor-pointer transition-all"
                style={{
                  background: selected?.id===conv.id ? 'rgba(37,211,102,0.15)' : th.inputBg,
                  border: `1px solid ${selected?.id===conv.id ? 'rgba(37,211,102,0.3)' : th.inputBorder}`
                }}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{background:'linear-gradient(135deg,#25d366,#128c7e)'}}>
                      {conv.phone.slice(-2)}
                    </div>
                    <p className="text-xs font-semibold truncate" style={{color:th.text}}>{conv.phone}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {conv.unread > 0 && (
                      <span className="w-4 h-4 rounded-full flex items-center justify-center text-white text-xs font-bold"
                        style={{background:'#25d366',fontSize:'9px'}}>{conv.unread}</span>
                    )}
                    <p className="text-xs" style={{color:th.textMuted,fontSize:'9px'}}>{formatTime(conv.lastMessageTime)}</p>
                  </div>
                </div>
                <p className="text-xs truncate" style={{color:th.textSub}}>{conv.lastMessage}</p>
              </div>
            ))}
          </div>

          <button onClick={loadConversations} className="flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs" style={{background:th.inputBg,color:th.textSub}}>
            <MdRefresh size={13}/>Actualiser
          </button>
        </div>

        {/* Historique conversation */}
        <div className="flex-1 flex flex-col" style={{borderLeft:`1px solid ${th.inputBorder}`}}>
          {!selected ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MdChat size={32} style={{color:th.textMuted, margin:'0 auto 8px'}}/>
                <p className="text-sm" style={{color:th.textSub}}>Sélectionnez une conversation</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="px-3 py-2.5 flex items-center justify-between" style={{borderBottom:`1px solid ${th.inputBorder}`}}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                    style={{background:'linear-gradient(135deg,#25d366,#128c7e)'}}>
                    {selected.phone.slice(-2)}
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{color:th.text}}>{selected.phone}</p>
                    <p className="text-xs" style={{color:'#25d366'}}>WhatsApp</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={async () => {
                    try {
                      const res = await axios.post(`${API_URL}/api/automation/resume/${businessId}/client/${selected.id}`)
                      alert(' Compte-rendu IA:\n\n' + res.data.resume)
                    } catch(e) { alert('Erreur génération') }
                  }}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs"
                  style={{background:'rgba(139,92,246,0.15)',color:'#a78bfa'}}>
                  <MdAutoAwesome size={12}/>CR
                  </button>
                  <button onClick={() => deleteConv(selected.id)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center hover:text-red-400 transition-all"
                    style={{color:th.textMuted}}><MdDelete size={14}/></button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
                {loadingHistory ? (
                  <div className="text-center py-4" style={{color:th.textSub}}>Chargement...</div>
                ) : history.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role==='user'?'justify-start':'justify-end'}`}>
                    <div className="max-w-xs px-3 py-2 rounded-2xl"
                      style={{
                        background: msg.role==='user' ? th.inputBg : msg.isManual ? 'rgba(59,130,246,0.2)' : 'rgba(37,211,102,0.2)',
                        border: `1px solid ${msg.role==='user' ? th.inputBorder : msg.isManual ? 'rgba(59,130,246,0.3)' : 'rgba(37,211,102,0.3)'}`,
                      }}>
                      <p className="text-xs leading-relaxed" style={{color:th.text}}>
                        {msg.isManual && <span className="text-blue-400 font-semibold">[Manuel] </span>}
                        {msg.content?.replace('[MANUEL] ','')}
                      </p>
                      <p className="text-xs mt-1" style={{color:th.textMuted,fontSize:'9px'}}>
                        {formatTime(msg.timestamp)}
                        {msg.role==='assistant' && !msg.isManual && ' •  IA'}
                        {msg.isManual && ' •  Manuel'}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef}/>
              </div>

              {/* Réponse manuelle */}
              <div className="px-3 py-2 flex gap-2" style={{borderTop:`1px solid ${th.inputBorder}`}}>
                <input value={replyText} onChange={e=>setReplyText(e.target.value)}
                  onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&sendReply()}
                  placeholder="Répondre manuellement..."
                  className="flex-1 px-3 py-2 rounded-xl text-xs focus:outline-none"
                  style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
                <button onClick={sendReply} disabled={!replyText.trim()||sending}
                  className="w-8 h-8 rounded-xl flex items-center justify-center text-white disabled:opacity-40"
                  style={{background:'linear-gradient(135deg,#25d366,#128c7e)'}}>
                  <MdSend size={14}/>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}


// ─── MODULE NOTIFICATIONS ───
const NotificationsModal = ({ businessId, business, onClose, isDark }) => {
  const th = getTheme(isDark)
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [pendingPayments, setPendingPayments] = useState([])
  const [confirmingPay, setConfirmingPay] = useState(null)

  useEffect(() => {
    axios.get(`${API_URL}/api/notifications/${businessId}`)
      .then(r => setNotifications(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
    // Charger paiements en attente
    axios.get(`${API_URL}/api/businesses/${businessId}/payments/pending`)
      .then(r => setPendingPayments(r.data || []))
      .catch(() => {})
  }, [businessId])

  const handleConfirmPayment = async (paymentId, valid) => {
    setConfirmingPay(paymentId)
    try {
      await axios.post(`${API_URL}/api/businesses/${businessId}/payments/${paymentId}/confirm`, { valid })
      setPendingPayments(p => p.filter(x => x.id !== paymentId))
    } catch(e) { alert('Erreur: ' + e.message) }
    finally { setConfirmingPay(null) }
  } // eslint-disable-line react-hooks/exhaustive-deps

  const typeColors = {
    new_lead: { bg: 'rgba(16,185,129,0.1)', border: 'rgba(16,185,129,0.25)', color: '#34d399' },
    unread_message: { bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.25)', color: '#60a5fa' },
    order_update: { bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.25)', color: '#fbbf24' },
  }

  const formatTime = (t) => {
    if (!t) return ''
    const d = new Date(t)
    const diff = new Date() - d
    if (diff < 60000) return 'À l\'instant'
    if (diff < 3600000) return `Il y a ${Math.floor(diff/60000)} min`
    if (diff < 86400000) return `Il y a ${Math.floor(diff/3600000)}h`
    return d.toLocaleDateString('fr-FR', {day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})
  }

  const unreadCount = notifications.filter(n => !n.read).length

  return (
    <Modal title="Notifications" moduleId="notifications" icon={<MdNotifications size={20} className="text-white"/>} color="#f59e0b" onClose={onClose} wide isDark={isDark}>

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <span className="px-2.5 py-1 rounded-full text-xs font-bold text-white" style={{background:'#f59e0b'}}>
              {unreadCount} nouvelle(s)
            </span>
          )}
        </div>
        <button onClick={() => setNotifications(notifications.map(n => ({...n, read: true})))}
          className="text-xs px-3 py-1.5 rounded-lg" style={{background:th.inputBg,color:th.textSub}}>
          Tout marquer lu
        </button>
      </div>

      {/* ─── PAIEMENTS EN ATTENTE ─── */}
      {pendingPayments.length > 0 && (
        <div className="mb-4 rounded-xl overflow-hidden" style={{border:'2px solid rgba(16,185,129,0.3)',background:'rgba(16,185,129,0.05)'}}>
          <div className="flex items-center gap-2 px-4 py-3" style={{background:'rgba(16,185,129,0.1)',borderBottom:'1px solid rgba(16,185,129,0.2)'}}>
            <span className="text-base"></span>
            <span className="font-bold text-sm" style={{color:'#10b981'}}>Paiements à vérifier ({pendingPayments.length})</span>
            <span className="text-xs ml-auto" style={{color:'rgba(16,185,129,0.7)'}}>Vérifiez sur Wave/Orange Money</span>
          </div>
          {pendingPayments.map((p, i) => (
            <div key={i} className="p-4" style={{borderBottom:i<pendingPayments.length-1?'1px solid rgba(16,185,129,0.15)':'none'}}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-bold text-sm" style={{color:th.text}}>{p.clientName} · {p.clientPhone}</p>
                  <p className="text-xs" style={{color:th.textMuted}}>
                    {p.method} · {p.time} {p.senderName ? `· Expéditeur: ${p.senderName}` : ''}
                  </p>
                  {p.orderDetails && <p className="text-xs mt-1" style={{color:th.textMuted}}> {p.orderDetails}</p>}
                </div>
                <p className="font-bold text-base" style={{color:'#10b981'}}>{parseInt(p.amount||0).toLocaleString('fr-FR')} FCFA</p>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => handleConfirmPayment(p.id, true)} disabled={confirmingPay===p.id}
                  className="flex-1 py-2 rounded-xl text-xs font-bold text-white"
                  style={{background:'linear-gradient(135deg,#10b981,#059669)',opacity:confirmingPay===p.id?0.5:1}}>
                   Valider — Paiement reçu
                </button>
                <button onClick={() => handleConfirmPayment(p.id, false)} disabled={confirmingPay===p.id}
                  className="flex-1 py-2 rounded-xl text-xs font-bold"
                  style={{background:'rgba(239,68,68,0.12)',color:'#f87171',opacity:confirmingPay===p.id?0.5:1}}>
                   Rejeter
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-center py-10">
          <div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"/>
          <p className="text-sm" style={{color:th.textSub}}>Chargement...</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-10">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{background:th.inputBg}}>
            <MdNotifications size={28} style={{color:th.textMuted}}/>
          </div>
          <p className="text-sm font-semibold" style={{color:th.text}}>Aucune notification</p>
          <p className="text-xs mt-1" style={{color:th.textSub}}>Les alertes apparaîtront ici</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {notifications.map((notif, i) => {
            const tc = typeColors[notif.type] || typeColors.unread_message
            return (
              <div key={i} onClick={() => setNotifications(notifications.map((n,j) => j===i ? {...n,read:true} : n))}
                className="flex items-start gap-3 px-4 py-3 rounded-xl cursor-pointer transition-all"
                style={{
                  background: notif.read ? th.inputBg : tc.bg,
                  border: `1px solid ${notif.read ? th.inputBorder : tc.border}`,
                  opacity: notif.read ? 0.7 : 1
                }}>
                <span style={{fontSize:'22px'}}>{notif.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <p className="text-sm font-semibold truncate" style={{color:notif.read?th.text:tc.color}}>
                      {notif.title}
                    </p>
                    {!notif.read && (
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{background:tc.color}}/>
                    )}
                  </div>
                  <p className="text-xs truncate" style={{color:th.textSub}}>{notif.message}</p>
                  <p className="text-xs mt-1" style={{color:th.textMuted}}>{formatTime(notif.time)}</p>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </Modal>
  )
}


// ─────────────────────────────────────────────
// ─── MODULE RELANCES ───

const RELANCE_TEMPLATES_BY_SECTOR = (sector = '') => {
  const s = sector.toLowerCase()
  if (s.includes('immo') || s.includes('btp')) return [
    { id: 'r1', label: 'Suivi visite', message: 'Bonjour {nom}  Des questions sur notre bien ? ', delai: 2 },
    { id: 'r2', label: 'Relance devis', message: 'Bonjour {nom}, notre offre vous attend. On en parle ? ', delai: 3 },
    { id: 'r3', label: 'Suivi dossier', message: 'Bonjour {nom}  Votre dossier est prêt ! ', delai: 7 },
  ]
  if (s.includes('clinique') || s.includes('santé')) return [
    { id: 'r1', label: 'Rappel RDV', message: 'Bonjour {nom}, RDV dans 24h. Vous confirmez ? ', delai: 1 },
    { id: 'r2', label: 'Suivi patient', message: 'Bonjour {nom}, comment vous sentez-vous ? ', delai: 3 },
  ]
  if (s.includes('restaurant') || s.includes('food')) return [
    { id: 'r1', label: 'Client absent', message: 'Bonjour {nom}  Vous nous manquez ! Menu du jour ️', delai: 7 },
    { id: 'r2', label: 'Fidélité', message: 'Bonjour {nom} ! 10% de réduction pour vous ', delai: 14 },
  ]
  return [
    { id: 'r1', label: 'Relance douce', message: 'Bonjour {nom}  Des questions ? On est là !', delai: 2 },
    { id: 'r2', label: 'Offre spéciale', message: 'Bonjour {nom} ! Une offre exclusive pour vous ', delai: 7 },
  ]
}

const RelancesModal = ({ businessId, business, onClose, isDark }) => {
  const th = getTheme(isDark)
  const [relances, setRelances] = useState([])
  const [activeTab, setActiveTab] = useState('config')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [label, setLabel] = useState('')
  const [message, setMessage] = useState('')
  const [delai, setDelai] = useState(3)
  const [generating, setGenerating] = useState(false)
  const [generatingSug, setGeneratingSug] = useState(false)
  const [aiSuggestions, setAiSuggestions] = useState([])
  const [searchRelance, setSearchRelance] = useState('')
  const suggestions = RELANCE_TEMPLATES_BY_SECTOR(business?.sector)

  useEffect(() => {
    axios.get(`${API_URL}/api/businesses/${businessId}/modules`)
      .then(r => setRelances(r.data.relances || []))
      .catch(() => {}).finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const addFromTemplate = (tpl) => {
    if (relances.find(r => r.label === tpl.label)) {
      setRelances(prev => prev.filter(r => r.label !== tpl.label))
    } else {
      setRelances(prev => [...prev, { id: genId(), ...tpl, active: true }])
    }
  }

  const generateWithAI = async () => {
    setGenerating(true)
    try {
      const res = await axios.post(`${API_URL}/api/generate/relance`, {
        sector: business?.sector || '', businessName: business?.name || '', delai,
        language: localStorage.getItem('lang') || 'fr'
      })
      if (res.data.label) setLabel(res.data.label)
      if (res.data.message) setMessage(res.data.message)
    } catch (e) {
      setLabel(`Relance J+${delai}`)
      setMessage(`Bonjour {nom}  Des questions sur ${business?.name || 'nos services'} ?`)
    } finally { setGenerating(false) }
  }

  const generateAISuggestions = async () => {
    setGeneratingSug(true)
    try {
      const results = []
      for (const d of [1, 3, 7]) {
        const res = await axios.post(`${API_URL}/api/generate/relance`, {
          sector: business?.sector || '', businessName: business?.name || '', delai: d,
          language: localStorage.getItem('lang') || 'fr'
        })
        if (res.data.label) results.push({ id: genId(), label: res.data.label, message: res.data.message, delai: d })
      }
      setAiSuggestions(results)
    } catch (e) { console.error(e) }
    finally { setGeneratingSug(false) }
  }

  const add = () => {
    if (!label || !message) return
    setRelances(prev => [...prev, { id: genId(), label, message, delai, active: true }])
    setLabel(''); setMessage(''); setDelai(3)
    setActiveTab('config')
  }

  const toggle = (id) => setRelances(relances.map(r => r.id === id ? {...r, active: !r.active} : r))
  const del = (id) => setRelances(relances.filter(r => r.id !== id))
  const insertVar = (v) => setMessage(prev => prev + v)


  // Export JSON
  const exportJSON = () => {
    if (relances.length === 0) { alert('Aucun élément à exporter'); return }
    const blob = new Blob([JSON.stringify(relances.map(r => ({ label: r.label, message: r.message, delai: r.delai })), null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `relances-${Date.now()}.json`; a.click()
    URL.revokeObjectURL(url)
  }

  // Import JSON
  const importJSON = (e) => {
    const file = e.target.files[0]; if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result)
        const items = Array.isArray(data) ? data : (data.items || data.data || [])
        const newItems = items.filter(i => i).map(i => ({ id: Date.now().toString(36)+Math.random().toString(36).slice(2), label: i.label||i.name||'', message: i.message||i.text||'', delai: i.delai||i.delay||3, active: i.active !== false }))
        if (newItems.length === 0) { alert('Aucun élément valide'); return }
        if (!window.confirm(`Importer ${newItems.length} élément(s) ?`)) return
        setRelances(prev => [...prev, ...newItems])
        alert(`✅ ${newItems.length} élément(s) importés !`)
      } catch(err) { alert('Erreur JSON: ' + err.message) }
    }
    reader.readAsText(file); e.target.value = ''
  }

  const save = async () => {
    setSaving(true)
    try {
      await axios.put(`${API_URL}/api/businesses/${businessId}/modules/relances`, { data: relances })
      onClose()
    } catch (e) { console.error(e) } finally { setSaving(false) }
  }

  const filtered = relances.filter(r =>
    !searchRelance || r.label.toLowerCase().includes(searchRelance.toLowerCase()) ||
    r.message.toLowerCase().includes(searchRelance.toLowerCase())
  )

  const allSuggestions = [...aiSuggestions, ...suggestions]

  const SpinnerIcon = () => <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"/>

  return (
    <Modal title="Relances Automatiques" moduleId="relances" icon={<MdRefresh size={20} className="text-white"/>} color="#f97316" onClose={onClose} wide isDark={isDark}>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { label: 'Total', value: relances.length, color:'#fb923c', bg:'rgba(249,115,22,0.12)', border:'rgba(249,115,22,0.25)' },
          { label: 'Actives', value: relances.filter(r=>r.active).length, color:'#34d399', bg:'rgba(16,185,129,0.12)', border:'rgba(16,185,129,0.25)' },
          { label: 'Inactives', value: relances.filter(r=>!r.active).length, color:'#9ca3af', bg:'rgba(107,114,128,0.12)', border:'rgba(107,114,128,0.25)' },
        ].map((s,i) => (
          <div key={i} className="rounded-xl p-3 text-center" style={{background:s.bg,border:`1px solid ${s.border}`}}>
            <p className="text-xl font-bold" style={{color:s.color}}>{s.value}</p>
            <p className="text-xs" style={{color:th.textMuted}}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Export/Import JSON — Relances */}
      <div style={{display:"flex", gap:6, marginBottom:12, flexWrap:"wrap"}}>
        <button onClick={exportJSON} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold" style={{background:"rgba(139,92,246,0.12)",color:"#a78bfa",border:"1px solid rgba(139,92,246,0.25)"}}>
          <MdDownload size={13}/>Export JSON
        </button>
        <label className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer" style={{background:"rgba(245,158,11,0.12)",color:"#fbbf24",border:"1px solid rgba(245,158,11,0.25)"}}>
          <MdUpload size={13}/>Import JSON
          <input type="file" accept=".json" onChange={importJSON} className="hidden"/>
        </label>
      </div>
      <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{background:th.inputBg}}>
        {[
          {id:'config', label:`⚙️ (${relances.length})`},
          {id:'add', label:' Ajouter'},
          {id:'suggest', label:' Suggestions'},
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
            style={activeTab===tab.id?{background:'linear-gradient(135deg,#f97316,#ea580c)',color:'white'}:{color:th.textSub}}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'config' && (
        <div>
          <input value={searchRelance} onChange={e=>setSearchRelance(e.target.value)}
            placeholder="Rechercher..." className="w-full px-3 py-2 rounded-xl text-xs focus:outline-none mb-3"
            style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          {loading && <p className="text-center py-4 text-sm" style={{color:th.textSub}}>Chargement...</p>}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-6">
              <p className="text-sm mb-2" style={{color:th.textSub}}>Aucune relance configurée</p>
              <button onClick={() => setActiveTab('suggest')} className="text-orange-400 text-xs">Voir suggestions →</button>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
              {filtered.map(r => (
                <div key={r.id} className="rounded-xl p-3" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,opacity:r.active?1:0.5}}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggle(r.id)}
                        className="w-9 h-5 rounded-full relative flex-shrink-0"
                        style={{background:r.active?'#f97316':'#94a3b8'}}>
                        <span className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all"
                          style={{left:r.active?'16px':'2px'}}/>
                      </button>
                      <p className="text-sm font-semibold" style={{color:th.text}}>{r.label}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{background:'rgba(249,115,22,0.15)',color:'#fb923c'}}>J+{r.delai}</span>
                      <button onClick={() => del(r.id)} className="w-6 h-6 rounded flex items-center justify-center" style={{color:th.textMuted}}>
                        <MdDelete size={13}/>
                      </button>
                    </div>
                  </div>
                  <p className="text-xs" style={{color:th.textSub}}>{r.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'add' && (
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Nom</label>
            <input value={label} onChange={e=>setLabel(e.target.value)} placeholder="Ex: Relance après 3 jours"
              className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold" style={{color:th.textSub}}>Message</label>
              <div className="flex gap-1">
                {['{nom}','{date}','{service}'].map(v => (
                  <button key={v} onClick={() => insertVar(v)} className="text-xs px-1.5 py-0.5 rounded" style={{background:'rgba(249,115,22,0.15)',color:'#fb923c'}}>{v}</button>
                ))}
              </div>
            </div>
            <textarea value={message} onChange={e=>setMessage(e.target.value)} rows={3}
              placeholder="Ex: Bonjour {nom}, avez-vous des questions ?"
              className="w-full px-4 py-2.5 rounded-xl text-sm resize-none focus:outline-none" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          </div>
          <div>
            <label className="text-xs font-semibold mb-2 block" style={{color:th.textSub}}>Délai : <span style={{color:'#fb923c'}}>J+{delai}</span></label>
            <input type="range" min="1" max="30" value={delai} onChange={e=>setDelai(parseInt(e.target.value))} className="w-full" style={{accentColor:'#f97316'}}/>
          </div>
          <button onClick={generateWithAI} disabled={generating}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2"
            style={{background:'linear-gradient(135deg,#a855f7,#7c3aed)'}}>
            {generating ? <SpinnerIcon/> : <MdAutoAwesome size={16}/>}
            {generating ? 'Génération...' : "✨ Générer avec l'IA"}
          </button>
          <button onClick={add} disabled={!label||!message}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 flex items-center justify-center gap-2"
            style={{background:'linear-gradient(135deg,#f97316,#ea580c)'}}>
            <MdAdd size={16}/>Ajouter
          </button>
        </div>
      )}

      {activeTab === 'suggest' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs" style={{color:th.textSub}}>Pour <span style={{color:th.text,fontWeight:'600'}}>{business?.sector}</span></p>
            <button onClick={generateAISuggestions} disabled={generatingSug}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold disabled:opacity-50"
              style={{background:'linear-gradient(135deg,#a855f7,#7c3aed)',color:'white'}}>
              {generatingSug ? <SpinnerIcon/> : <MdAutoAwesome size={13}/>}
              {generatingSug ? '...' : '✨ IA'}
            </button>
          </div>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {allSuggestions.map((tpl, i) => {
              const added = relances.find(r => r.label === tpl.label)
              return (
                <div key={i} className="rounded-xl p-3" style={{background:added?'rgba(249,115,22,0.08)':th.inputBg,border:`1px solid ${added?'rgba(249,115,22,0.2)':th.inputBorder}`}}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-semibold" style={{color:added?'#fb923c':th.text}}>
                      {tpl.label} <span className="text-xs" style={{color:'#fb923c'}}>J+{tpl.delai}</span>
                    </p>
                    <button onClick={() => addFromTemplate(tpl)}
                      className="text-xs px-3 py-1 rounded-lg font-semibold"
                      style={{background:added?'rgba(16,185,129,0.15)':'rgba(249,115,22,0.15)',color:added?'#34d399':'#fb923c'}}>
                      {added ? '✓' : '+'}
                    </button>
                  </div>
                  <p className="text-xs" style={{color:th.textSub}}>{tpl.message}</p>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <button onClick={save} disabled={saving}
        className="w-full mt-4 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2"
        style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>
        {saving ? 'Sauvegarde...' : <><MdCheck size={16}/>Sauvegarder</>}
      </button>
    </Modal>
  )
}


// ─── MODULE MODÈLES DE MESSAGES ───

const MODELES_BY_SECTOR = (sector = '') => {
  const s = (sector || '').toLowerCase()
  if (s.includes('tourisme') || s.includes('hôtel') || s.includes('hotel') || s.includes('voyage') || s.includes('agence')) return [
    { id: 'm1', label: 'Devis séjour', category: 'Séjour', message: 'Bonjour {nom}  Votre devis pour {service} est prêt. Montant : {montant} FCFA. Valable 7 jours. ' },
    { id: 'm2', label: 'Confirmation réservation', category: 'Séjour', message: 'Bonjour {nom}  Votre réservation est confirmée ! Départ le {date}. Notre équipe vous contactera sous 24h. ✈️' },
    { id: 'm3', label: 'Documents requis', category: 'Visa & Formalités', message: 'Bonjour {nom}  Pour finaliser votre dossier, merci de nous fournir : passeport valide, photos, {service}. Merci !' },
    { id: 'm4', label: 'Rappel départ', category: 'Séjour', message: 'Bonjour {nom} ✈️ Rappel : votre voyage commence le {date}. RDV à {heure}. Bon voyage ! ' },
  ]
  if (s.includes('immo') || s.includes('btp')) return [
    { id: 'm1', label: 'Confirmation visite', category: 'RDV', message: 'Bonjour {nom}  Votre visite est confirmée le {date} à {heure}. À bientôt ! ' },
    { id: 'm2', label: 'Devis envoyé', category: 'Devis', message: 'Bonjour {nom}  Votre devis est prêt. Montant : {montant} FCFA. Valable 30 jours.' },
    { id: 'm3', label: 'Dossier complet', category: 'Suivi', message: 'Bonne nouvelle {nom} !  Votre dossier est complet. Nous passons à l\'étape suivante !' },
  ]
  if (s.includes('clinique') || s.includes('santé')) return [
    { id: 'm1', label: 'Confirmation RDV', category: 'RDV', message: 'Bonjour {nom}  RDV confirmé le {date} à {heure}. Présentez-vous 10 min avant. ' },
    { id: 'm2', label: 'Résultats disponibles', category: 'Suivi', message: 'Bonjour {nom}  Vos résultats sont disponibles. Contactez notre secrétariat.' },
  ]
  if (s.includes('restaurant') || s.includes('food') || s.includes('traiteur')) return [
    { id: 'm1', label: 'Commande confirmée', category: 'Commande', message: 'Bonjour {nom}  Commande confirmée ! Prêt dans {duree} minutes. Merci ! ️' },
    { id: 'm2', label: 'Commande prête', category: 'Livraison', message: 'Bonne nouvelle {nom} !  Votre commande est prête. Bon appétit !' },
  ]
  return [
    { id: 'm1', label: 'Confirmation commande', category: 'Commande', message: 'Bonjour {nom}  Commande reçue ! Référence : {reference}. Merci !' },
    { id: 'm2', label: 'Devis prêt', category: 'Devis', message: 'Bonjour {nom}  Votre devis est disponible. Montant : {montant} FCFA.' },
    { id: 'm3', label: 'Paiement reçu', category: 'Paiement', message: 'Bonjour {nom}  Paiement de {montant} FCFA reçu. Merci ! ' },
  ]
}

const CAT_COLORS = {
  'RDV': { bg:'rgba(99,102,241,0.15)', color:'#818cf8' },
  'Devis': { bg:'rgba(139,92,246,0.15)', color:'#a78bfa' },
  'Commande': { bg:'rgba(245,158,11,0.15)', color:'#fbbf24' },
  'Livraison': { bg:'rgba(59,130,246,0.15)', color:'#60a5fa' },
  'Suivi': { bg:'rgba(16,185,129,0.15)', color:'#34d399' },
  'Paiement': { bg:'rgba(239,68,68,0.15)', color:'#f87171' },
  'Consultation': { bg:'rgba(6,182,212,0.15)', color:'#22d3ee' },
  'Autre': { bg:'rgba(107,114,128,0.15)', color:'#9ca3af' },
}

const getCatsBySector = (sector = '') => {
  const s = sector.toLowerCase()
  if (s.includes('clinique') || s.includes('santé')) return ['RDV','Consultation','Suivi','Paiement','Autre']
  if (s.includes('immo') || s.includes('btp')) return ['RDV','Devis','Suivi','Paiement','Autre']
  if (s.includes('restaurant') || s.includes('food')) return ['Commande','Livraison','RDV','Paiement','Autre']
  return ['RDV','Devis','Commande','Livraison','Suivi','Paiement','Consultation','Autre']
}

const ModelesModal = ({ businessId, business, onClose, isDark }) => {
  const th = getTheme(isDark)
  const [modeles, setModeles] = useState([])
  const [activeTab, setActiveTab] = useState('list')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [searchM, setSearchM] = useState('')
  const [copying, setCopying] = useState(null)
  const [newLabel, setNewLabel] = useState('')
  const [newMessage, setNewMessage] = useState('')
  const [newCategory, setNewCategory] = useState('Autre')
  const [generatingModeles, setGeneratingModeles] = useState(false)
  const [aiModeleSuggestions, setAiModeleSuggestions] = useState([])
  const suggestions = MODELES_BY_SECTOR(business?.sector)

  useEffect(() => {
    axios.get(`${API_URL}/api/businesses/${businessId}/modules`)
      .then(r => setModeles(r.data.modeles || []))
      .catch(() => {}).finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const addFromTemplate = (tpl) => {
    if (modeles.find(m => m.label === tpl.label)) {
      setModeles(prev => prev.filter(m => m.label !== tpl.label))
    } else {
      setModeles(prev => [...prev, { id: genId(), ...tpl }])
    }
  }

  const generateModelesWithAI = async () => {
    setGeneratingModeles(true)
    try {
      const res = await axios.post(`${API_URL}/api/generate/modeles`, {
        sector: business?.sector || '', businessName: business?.name || '',
        language: localStorage.getItem('lang') || 'fr'
      })
      if (res.data.modeles) setAiModeleSuggestions(res.data.modeles)
    } catch (e) { console.error(e) }
    finally { setGeneratingModeles(false) }
  }

  const add = () => {
    if (!newLabel || !newMessage) return
    setModeles(prev => [...prev, { id: genId(), label: newLabel, message: newMessage, category: newCategory }])
    setNewLabel(''); setNewMessage(''); setNewCategory('Autre')
    setActiveTab('list')
  }

  const del = (id) => setModeles(modeles.filter(m => m.id !== id))

  const copyMsg = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopying(id)
      setTimeout(() => setCopying(null), 2000)
    })
  }

  const insertVar = (v) => setNewMessage(prev => prev + v)


  // Export JSON
  const exportJSON = () => {
    if (modeles.length === 0) { alert('Aucun élément à exporter'); return }
    const blob = new Blob([JSON.stringify(modeles.map(m => ({ label: m.label, message: m.message, category: m.category })), null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `modeles-${Date.now()}.json`; a.click()
    URL.revokeObjectURL(url)
  }

  // Import JSON
  const importJSON = (e) => {
    const file = e.target.files[0]; if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result)
        const items = Array.isArray(data) ? data : (data.items || data.data || [])
        const newItems = items.filter(i => i).map(i => ({ id: Date.now().toString(36)+Math.random().toString(36).slice(2), label: i.label||i.name||'', message: i.message||i.text||'', category: i.category||'Autre' }))
        if (newItems.length === 0) { alert('Aucun élément valide'); return }
        if (!window.confirm(`Importer ${newItems.length} élément(s) ?`)) return
        setModeles(prev => [...prev, ...newItems])
        alert(`✅ ${newItems.length} élément(s) importés !`)
      } catch(err) { alert('Erreur JSON: ' + err.message) }
    }
    reader.readAsText(file); e.target.value = ''
  }

  const save = async () => {
    setSaving(true)
    try {
      await axios.put(`${API_URL}/api/businesses/${businessId}/modules/modeles`, { data: modeles })
      onClose()
    } catch (e) { console.error(e) } finally { setSaving(false) }
  }

  const filtered = modeles.filter(m =>
    !searchM || m.label.toLowerCase().includes(searchM.toLowerCase()) ||
    m.message.toLowerCase().includes(searchM.toLowerCase())
  )

  const allSuggestions = [...aiModeleSuggestions, ...suggestions]

  const SpinnerSm = () => <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"/>

  return (
    <Modal title="Modèles de Messages" moduleId="modeles" icon={<MdSave size={20} className="text-white"/>} color="#06b6d4" onClose={onClose} wide isDark={isDark}>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {[
          { label: 'Modèles', value: modeles.length, color:'#22d3ee', bg:'rgba(6,182,212,0.12)', border:'rgba(6,182,212,0.25)' },
          { label: 'Catégories', value: [...new Set(modeles.map(m=>m.category))].length, color:'#a78bfa', bg:'rgba(139,92,246,0.12)', border:'rgba(139,92,246,0.25)' },
        ].map((s,i) => (
          <div key={i} className="rounded-xl p-3 text-center" style={{background:s.bg,border:`1px solid ${s.border}`}}>
            <p className="text-xl font-bold" style={{color:s.color}}>{s.value}</p>
            <p className="text-xs" style={{color:th.textMuted}}>{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{background:th.inputBg}}>
        {[
          {id:'list', label:` (${modeles.length})`},
          {id:'add', label:' Ajouter'},
          {id:'suggest', label:' Suggestions'},
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
            style={activeTab===tab.id?{background:'linear-gradient(135deg,#06b6d4,#0891b2)',color:'white'}:{color:th.textSub}}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'list' && (
        <div>
          <input value={searchM} onChange={e=>setSearchM(e.target.value)}
            placeholder="Rechercher..." className="w-full px-3 py-2 rounded-xl text-xs focus:outline-none mb-3"
            style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          {loading && <p className="text-center py-4 text-sm" style={{color:th.textSub}}>Chargement...</p>}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-6">
              <p className="text-sm mb-2" style={{color:th.textSub}}>Aucun modèle configuré</p>
              <button onClick={() => setActiveTab('suggest')} className="text-cyan-400 text-xs">Voir suggestions →</button>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
              {filtered.map(m => {
                const cc = CAT_COLORS[m.category] || CAT_COLORS['Autre']
                return (
                  <div key={m.id} className="rounded-xl p-3" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`}}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{background:cc.bg,color:cc.color}}>{m.category}</span>
                        <p className="text-sm font-semibold" style={{color:th.text}}>{m.label}</p>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => copyMsg(m.message, m.id)}
                          className="text-xs px-2 py-1 rounded-lg"
                          style={{background:copying===m.id?'rgba(16,185,129,0.15)':'rgba(6,182,212,0.1)',color:copying===m.id?'#34d399':'#22d3ee'}}>
                          {copying===m.id?'✓':''}
                        </button>
                        <button onClick={() => del(m.id)} className="w-6 h-6 rounded flex items-center justify-center" style={{color:th.textMuted}}>
                          <MdDelete size={13}/>
                        </button>
                      </div>
                    </div>
                    <p className="text-xs" style={{color:th.textSub}}>{m.message}</p>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'add' && (
        <div className="space-y-3">
          {/* Export/Import JSON — Modèles */}
          <div style={{display:"flex", gap:6, marginBottom:12, flexWrap:"wrap"}}>
            <button onClick={exportJSON} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold" style={{background:"rgba(139,92,246,0.12)",color:"#a78bfa",border:"1px solid rgba(139,92,246,0.25)"}}>
              <MdDownload size={13}/>Export JSON
            </button>
            <label className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer" style={{background:"rgba(245,158,11,0.12)",color:"#fbbf24",border:"1px solid rgba(245,158,11,0.25)"}}>
              <MdUpload size={13}/>Import JSON
              <input type="file" accept=".json" onChange={importJSON} className="hidden"/>
            </label>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Nom</label>
              <input value={newLabel} onChange={e=>setNewLabel(e.target.value)} placeholder="Ex: Confirmation commande"
                className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            </div>
            <div className="w-32">
              <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Catégorie</label>
              <select value={newCategory} onChange={e=>setNewCategory(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl text-sm focus:outline-none" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}>
                {getCatsBySector(business?.sector).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold" style={{color:th.textSub}}>Message</label>
              <div className="flex gap-1">
                {['{nom}','{date}','{heure}','{montant}','{reference}'].map(v => (
                  <button key={v} onClick={() => insertVar(v)} className="text-xs px-1.5 py-0.5 rounded" style={{background:'rgba(6,182,212,0.15)',color:'#22d3ee'}}>{v}</button>
                ))}
              </div>
            </div>
            <textarea value={newMessage} onChange={e=>setNewMessage(e.target.value)} rows={4}
              placeholder="Ex: Bonjour {nom}  Votre commande est confirmée..."
              className="w-full px-4 py-2.5 rounded-xl text-sm resize-none focus:outline-none" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
          </div>
          <button onClick={add} disabled={!newLabel||!newMessage}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 flex items-center justify-center gap-2"
            style={{background:'linear-gradient(135deg,#06b6d4,#0891b2)'}}>
            <MdAdd size={16}/>Ajouter ce modèle
          </button>
        </div>
      )}

      {activeTab === 'suggest' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs" style={{color:th.textSub}}>Pour <span style={{color:th.text,fontWeight:'600'}}>{business?.sector}</span></p>
            <button onClick={generateModelesWithAI} disabled={generatingModeles}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold disabled:opacity-50"
              style={{background:'linear-gradient(135deg,#a855f7,#7c3aed)',color:'white'}}>
              {generatingModeles ? <SpinnerSm/> : <MdAutoAwesome size={13}/>}
              {generatingModeles ? '...' : '✨ IA'}
            </button>
          </div>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {allSuggestions.map((tpl, i) => {
              const added = modeles.find(m => m.label === tpl.label)
              const cc = CAT_COLORS[tpl.category] || CAT_COLORS['Autre']
              return (
                <div key={i} className="rounded-xl p-3" style={{background:added?'rgba(6,182,212,0.08)':th.inputBg,border:`1px solid ${added?'rgba(6,182,212,0.2)':th.inputBorder}`}}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{background:cc.bg,color:cc.color}}>{tpl.category}</span>
                      <p className="text-sm font-semibold" style={{color:added?'#22d3ee':th.text}}>{tpl.label}</p>
                    </div>
                    <button onClick={() => addFromTemplate(tpl)}
                      className="text-xs px-3 py-1 rounded-lg font-semibold"
                      style={{background:added?'rgba(16,185,129,0.15)':'rgba(6,182,212,0.15)',color:added?'#34d399':'#22d3ee'}}>
                      {added ? '✓' : '+'}
                    </button>
                  </div>
                  <p className="text-xs" style={{color:th.textSub}}>{tpl.message}</p>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <button onClick={save} disabled={saving}
        className="w-full mt-4 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2"
        style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>
        {saving ? 'Sauvegarde...' : <><MdCheck size={16}/>Sauvegarder</>}
      </button>
    </Modal>
  )
}


// ─── MODULE RÉSEAUX SOCIAUX ───

const SOCIAL_PLATFORMS = [
  { id: 'facebook', name: 'Facebook', icon: '', color: '#1877f2', bg: 'rgba(24,119,242,0.12)', border: 'rgba(24,119,242,0.3)', placeholder: 'Partagez votre actualité sur Facebook...' },
  { id: 'instagram', name: 'Instagram', icon: '', color: '#e1306c', bg: 'rgba(225,48,108,0.12)', border: 'rgba(225,48,108,0.3)', placeholder: 'Décrivez votre photo Instagram...' },
  { id: 'tiktok', name: 'TikTok', icon: '', color: '#ff0050', bg: 'rgba(255,0,80,0.12)', border: 'rgba(255,0,80,0.3)', placeholder: 'Décrivez votre vidéo TikTok...' },
  { id: 'linkedin', name: 'LinkedIn', icon: '', color: '#0a66c2', bg: 'rgba(10,102,194,0.12)', border: 'rgba(10,102,194,0.3)', placeholder: 'Partagez votre post professionnel LinkedIn...' },
  { id: 'twitter', name: 'X (Twitter)', icon: '', color: '#1da1f2', bg: 'rgba(29,161,242,0.12)', border: 'rgba(29,161,242,0.3)', placeholder: 'Rédigez votre tweet (280 caractères max)...' },
  { id: 'youtube', name: 'YouTube', icon: '▶️', color: '#ff0000', bg: 'rgba(255,0,0,0.12)', border: 'rgba(255,0,0,0.3)', placeholder: 'Titre et description de votre vidéo YouTube...' },
  { id: 'whatsapp', name: 'WhatsApp Status', icon: '', color: '#25d366', bg: 'rgba(37,211,102,0.12)', border: 'rgba(37,211,102,0.3)', placeholder: 'Message pour votre statut WhatsApp...' },
]

const CONTENT_TYPES = [
  { id: 'promo', label: 'Promotion', icon: '' },
  { id: 'info', label: 'Information', icon: '' },
  { id: 'produit', label: 'Produit', icon: '️' },
  { id: 'evenement', label: 'Événement', icon: '' },
  { id: 'temoignage', label: 'Témoignage', icon: '⭐' },
  { id: 'conseil', label: 'Conseil', icon: '' },
]

const SocialModal = ({ businessId, business, onClose, isDark }) => {
  const th = getTheme(isDark)
  const [selectedPlatforms, setSelectedPlatforms] = useState(['facebook', 'instagram'])
  const [contentType, setContentType] = useState('promo')
  const [topic, setTopic] = useState('')
  const [generatedPosts, setGeneratedPosts] = useState({}) // eslint-disable-line no-unused-vars
  const [generating, setGenerating] = useState(false)
  const [publishing, setPublishing] = useState({})
  const [published, setPublished] = useState({})
  const [history, setHistory] = useState([])
  const [activeTab, setActiveTab] = useState('compose')
  const [editingPost, setEditingPost] = useState({})
  const [copied, setCopied] = useState(null)

  // Charger historique depuis DB
  useEffect(() => {
    axios.get(`${API_URL}/api/businesses/${businessId}/modules`)
      .then(r => { if (r.data.social?.length) setHistory(r.data.social) })
      .catch(() => {})
  }, [businessId]) // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlatform = (id) => {
    if (selectedPlatforms.includes(id)) {
      if (selectedPlatforms.length === 1) return
      setSelectedPlatforms(prev => prev.filter(p => p !== id))
    } else {
      setSelectedPlatforms(prev => [...prev, id])
    }
  }

  const generateWithAI = async () => {
    if (!topic.trim()) return
    setGenerating(true)
    try {
      const res = await axios.post(`${API_URL}/api/generate/social`, {
        sector: business?.sector || '',
        businessName: business?.name || '',
        contentType,
        topic,
        platforms: selectedPlatforms,
        language: localStorage.getItem('lang') || 'fr'
      })
      if (res.data.posts) {
        setGeneratedPosts(res.data.posts)
        setEditingPost(res.data.posts)
      }
    } catch (e) {
      // Fallback local
      const fallback = {}
      selectedPlatforms.forEach(p => {
            // eslint-disable-next-line no-unused-vars
        const pl = SOCIAL_PLATFORMS.find(pl => pl.id === p)
        const types = { promo: ' PROMOTION', info: ' INFO', produit: '️ NOUVEAU', evenement: ' ÉVÉNEMENT', temoignage: '⭐ AVIS CLIENT', conseil: ' CONSEIL' }
        fallback[p] = `${types[contentType]} — ${business?.name}\n\n${topic}\n\n${p === 'twitter' ? '' : ` Contactez-nous sur WhatsApp !\n\n`}#${business?.name?.replace(/\s/g,'')} #Sénégal #${business?.sector?.split(' ')[0] || 'Business'}`
        if (p === 'twitter') fallback[p] = fallback[p].slice(0, 280)
      })
      setGeneratedPosts(fallback)
      setEditingPost(fallback)
    } finally { setGenerating(false) }
  }

  const copyPost = (platformId) => {
    const text = editingPost[platformId] || ''
    navigator.clipboard.writeText(text).then(() => {
      setCopied(platformId)
      setTimeout(() => setCopied(null), 2000)
    })
  }

  // Sauvegarder l'historique en DB
  const saveHistory = async (newHistory) => {
    try {
      await axios.put(`${API_URL}/api/businesses/${businessId}/modules/social`, {
        data: newHistory.slice(0, 50) // garder les 50 derniers
      })
    } catch (e) { console.error('Save social:', e.message) }
  }

  const publishPost = async (platformId) => {
    setPublishing(prev => ({ ...prev, [platformId]: true }))
    try {
      const post = {
        id: genId(),
        platform: platformId,
        content: editingPost[platformId],
        publishedAt: new Date().toISOString(),
        type: contentType,
        topic,
      }
      const newHistory = [post, ...history]
      setHistory(newHistory)
      setPublished(prev => ({ ...prev, [platformId]: true }))
      setTimeout(() => setPublished(prev => ({ ...prev, [platformId]: false })), 3000)
      await saveHistory(newHistory) // ← sauvegarder en DB
    } catch (e) { console.error(e) }
    finally { setPublishing(prev => ({ ...prev, [platformId]: false })) }
  }

  const charLimit = { twitter: 280, whatsapp: 700, tiktok: 2200, instagram: 2200, facebook: 63206, linkedin: 3000, youtube: 5000 }

  const SpinnerSm = () => <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>

  return (
    <Modal title="Réseaux Sociaux" moduleId="social" icon={<MdShare size={20} className="text-white"/>} color="#e1306c" onClose={onClose} wide isDark={isDark}>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 p-1 rounded-xl" style={{background:th.inputBg}}>
        {[
          {id:'compose', label:'✍️ Composer'},
          {id:'history', label:` Historique (${history.length})`},
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
            style={activeTab===tab.id?{background:'linear-gradient(135deg,#e1306c,#c13584)',color:'white'}:{color:th.textSub}}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'compose' && (
        <div className="space-y-4">
          {/* Sélection plateformes */}
          <div>
            <label className="text-xs font-semibold mb-2 block" style={{color:th.textSub}}>Plateformes</label>
            <div className="flex flex-wrap gap-2">
              {SOCIAL_PLATFORMS.map(pl => (
                <button key={pl.id} onClick={() => togglePlatform(pl.id)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all"
                  style={{
                    background: selectedPlatforms.includes(pl.id) ? pl.bg : th.inputBg,
                    border: `1px solid ${selectedPlatforms.includes(pl.id) ? pl.border : th.inputBorder}`,
                    color: selectedPlatforms.includes(pl.id) ? pl.color : th.textSub,
                  }}>
                  <span>{pl.icon}</span>{pl.name}
                  {selectedPlatforms.includes(pl.id) && <span>✓</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Type de contenu */}
          <div>
            <label className="text-xs font-semibold mb-2 block" style={{color:th.textSub}}>Type de contenu</label>
            <div className="flex flex-wrap gap-2">
              {CONTENT_TYPES.map(ct => (
                <button key={ct.id} onClick={() => setContentType(ct.id)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all"
                  style={{
                    background: contentType===ct.id ? 'rgba(225,48,108,0.15)' : th.inputBg,
                    border: `1px solid ${contentType===ct.id ? 'rgba(225,48,108,0.3)' : th.inputBorder}`,
                    color: contentType===ct.id ? '#e1306c' : th.textSub,
                  }}>
                  {ct.icon} {ct.label}
                </button>
              ))}
            </div>
          </div>

          {/* Sujet */}
          <div>
            <label className="text-xs font-semibold mb-1 block" style={{color:th.textSub}}>Sujet / Idée</label>
            <div className="flex gap-2">
              <input value={topic} onChange={e => setTopic(e.target.value)}
                placeholder={`Ex: Nouvelle offre ${business?.name || ''} — 20% de réduction ce weekend`}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm focus:outline-none"
                style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}
                onKeyDown={e => e.key === 'Enter' && generateWithAI()}/>
              <button onClick={generateWithAI} disabled={!topic.trim() || generating}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 flex items-center gap-2 flex-shrink-0"
                style={{background:'linear-gradient(135deg,#a855f7,#7c3aed)'}}>
                {generating ? <SpinnerSm/> : <MdAutoAwesome size={16}/>}
                {generating ? '...' : '✨ IA'}
              </button>
            </div>
          </div>

          {/* Posts générés */}
          {Object.keys(editingPost).length > 0 && (
            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {selectedPlatforms.map(platformId => {
                const pl = SOCIAL_PLATFORMS.find(p => p.id === platformId)
                if (!pl) return null
                const text = editingPost[platformId] || ''
                const limit = charLimit[platformId]
                const over = text.length > limit
                return (
                  <div key={platformId} className="rounded-xl p-3" style={{background:pl.bg,border:`1px solid ${pl.border}`}}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span style={{fontSize:'18px'}}>{pl.icon}</span>
                        <span className="text-sm font-bold" style={{color:pl.color}}>{pl.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs" style={{color:over?'#f87171':th.textMuted}}>
                          {text.length}/{limit}
                        </span>
                        <button onClick={() => copyPost(platformId)}
                          className="text-xs px-2 py-1 rounded-lg"
                          style={{background:copied===platformId?'rgba(16,185,129,0.2)':'rgba(255,255,255,0.1)',color:copied===platformId?'#34d399':pl.color}}>
                          {copied===platformId ? '✓ Copié' : ' Copier'}
                        </button>
                        <button onClick={() => publishPost(platformId)} disabled={publishing[platformId]}
                          className="text-xs px-3 py-1 rounded-lg font-semibold text-white"
                          style={{background:published[platformId]?'#10b981':pl.color,opacity:publishing[platformId]?0.6:1}}>
                          {published[platformId] ? '✓ Publié' : publishing[platformId] ? '...' : '▶ Publier'}
                        </button>
                      </div>
                    </div>
                    <textarea
                      value={text}
                      onChange={e => setEditingPost(prev => ({...prev, [platformId]: e.target.value}))}
                      rows={4}
                      className="w-full text-xs resize-none focus:outline-none rounded-lg p-2"
                      style={{background:'rgba(0,0,0,0.2)',color:th.text,border:`1px solid ${over?'#ef4444':'transparent'}`}}/>
                    {over && <p className="text-xs mt-1" style={{color:'#f87171'}}>Dépasse la limite de {limit} caractères pour {pl.name}</p>}
                  </div>
                )
              })}
            </div>
          )}

          {/* Info si aucun post généré */}
          {Object.keys(editingPost).length === 0 && (
            <div className="text-center py-6 rounded-xl" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`}}>
              <p style={{fontSize:'32px', marginBottom:'8px'}}>✍️</p>
              <p className="text-sm font-semibold" style={{color:th.text}}>Choisissez vos plateformes et entrez un sujet</p>
              <p className="text-xs mt-1" style={{color:th.textSub}}>L'IA va générer un post adapté à chaque réseau social</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {history.length === 0 ? (
            <div className="text-center py-8">
              <p style={{fontSize:'32px', marginBottom:'8px'}}></p>
              <p className="text-sm" style={{color:th.textSub}}>Aucune publication pour le moment</p>
            </div>
          ) : history.map((post, i) => {
            const pl = SOCIAL_PLATFORMS.find(p => p.id === post.platform) // eslint-disable-line no-unused-vars
            return (
              <div key={i} className="rounded-xl p-3" style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`}}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span>{pl?.icon}</span>
                    <span className="text-sm font-semibold" style={{color:pl?.color}}>{pl?.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{background:'rgba(16,185,129,0.15)',color:'#34d399'}}>Publié</span>
                  </div>
                  <span className="text-xs" style={{color:th.textMuted}}>
                    {new Date(post.publishedAt).toLocaleDateString('fr-FR', {day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}
                  </span>
                </div>
                <p className="text-xs" style={{color:th.textSub}}>{post.content.slice(0, 120)}...</p>
              </div>
            )
          })}
        </div>
      )}

      {history.length > 0 && (
        <button onClick={() => saveHistory(history)}
          className="w-full mt-4 py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
          style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>
          <MdCheck size={16}/>Sauvegarder l'historique ({history.length} posts)
        </button>
      )}
    </Modal>
  )
}



// ─── MODAL EMPLOYÉS ───
const EmployesModal = ({ businessId, business, onClose, isDark, API_URL }) => {
  const th = getTheme(isDark)
  const [employes, setEmployes] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('liste')
  const [showAdd, setShowAdd] = useState(false)
  const [msgTab, setMsgTab] = useState('individuel')
  const [selectedEmp, setSelectedEmp] = useState(null)
  const [msgText, setMsgText] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [newEmp, setNewEmp] = useState({ nom: '', prenom: '', poste: '', departement: '', phone: '', email: '', salaire: '', dateEmbauche: '' })
  const DEPTS = ['Direction', 'Commercial', 'Technique', 'Comptabilité', 'RH', 'Marketing', 'Logistique', 'Autre']

  // Charger les employés depuis la DB
  useEffect(() => {
    axios.get(`${API_URL}/api/businesses/${businessId}/modules`)
      .then(r => {
        const data = r.data?.employes || []
        setEmployes(Array.isArray(data) ? data : [])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [businessId]) // eslint-disable-line react-hooks/exhaustive-deps

  const saveEmployes = async (list) => {
    try {
      await axios.put(`${API_URL}/api/businesses/${businessId}/modules/employes`, { data: list })
    } catch(e) { console.error('Save employes:', e.message) }
  }

  const addEmploye = () => {
    if (!newEmp.nom || !newEmp.phone) { alert('Nom et téléphone requis'); return }
    const emp = { ...newEmp, id: Date.now().toString(), phone: newEmp.phone.replace(/\s/g,'') }
    const updated = [...employes, emp]
    setEmployes(updated)
    saveEmployes(updated)
    setNewEmp({ nom: '', prenom: '', poste: '', departement: '', phone: '', email: '', salaire: '', dateEmbauche: '' })
    setShowAdd(false)
  }

  const deleteEmploye = (id) => {
    if (!window.confirm('Supprimer cet employé ?')) return
    const updated = employes.filter(e => e.id !== id)
    setEmployes(updated)
    saveEmployes(updated)
  }

  const sendMessage = async () => {
    if (!msgText.trim()) { alert('Message vide'); return }
    setSending(true)
    try {
      const targets = msgTab === 'individuel'
        ? (selectedEmp ? [selectedEmp] : [])
        : employes.filter(e => e.phone)
      if (targets.length === 0) { alert('Aucun destinataire'); setSending(false); return }
      await axios.post(`${API_URL}/api/businesses/${businessId}/send-bulk`, {
        recipients: targets.map(e => ({ phone: e.phone, name: `${e.prenom} ${e.nom}`.trim() })),
        message: msgText
      })
      setSent(true)
      setMsgText('')
      setTimeout(() => setSent(false), 3000)
    } catch(e) { alert('Erreur envoi: ' + e.message) }
    finally { setSending(false) }
  }

  const inputStyle = { background: th.inputBg, border: `1px solid ${th.inputBorder}`, color: th.text, borderRadius: 10, padding: '8px 12px', fontSize: 13, width: '100%', outline: 'none' }

  return (
    <Modal title="Module Employés" moduleId="employes" icon={<span style={{fontSize:18}}>👥</span>} color="#0ea5e9" onClose={onClose} wide isDark={isDark}>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 p-1 rounded-xl" style={{background:th.inputBg}}>
        {[{id:'liste',label:'👥 Employés'},{id:'message',label:'💬 Messagerie'}].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className="flex-1 py-2 rounded-lg text-xs font-semibold"
            style={activeTab===t.id?{background:'linear-gradient(135deg,#0ea5e9,#0284c7)',color:'white'}:{color:th.textSub}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab Liste ── */}
      {activeTab === 'liste' && (
        <div>
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm font-semibold" style={{color:th.text}}>{employes.length} employé(s)</p>
            <button onClick={() => setShowAdd(!showAdd)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white"
              style={{background:'linear-gradient(135deg,#0ea5e9,#0284c7)'}}>
              <MdAdd size={14}/>{showAdd ? 'Annuler' : 'Ajouter'}
            </button>
          </div>

          {/* Formulaire ajout */}
          {showAdd && (
            <div className="rounded-xl p-4 mb-4" style={{background:'rgba(14,165,233,0.06)',border:'1px solid rgba(14,165,233,0.2)'}}>
              <p className="text-xs font-bold mb-3" style={{color:'#38bdf8'}}>Nouvel employé</p>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input value={newEmp.nom} onChange={e=>setNewEmp(p=>({...p,nom:e.target.value}))} placeholder="Nom *" style={inputStyle}/>
                <input value={newEmp.prenom} onChange={e=>setNewEmp(p=>({...p,prenom:e.target.value}))} placeholder="Prénom" style={inputStyle}/>
                <input value={newEmp.poste} onChange={e=>setNewEmp(p=>({...p,poste:e.target.value}))} placeholder="Poste" style={inputStyle}/>
                <select value={newEmp.departement} onChange={e=>setNewEmp(p=>({...p,departement:e.target.value}))} style={inputStyle}>
                  <option value="">Département</option>
                  {DEPTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <input value={newEmp.phone} onChange={e=>setNewEmp(p=>({...p,phone:e.target.value}))} placeholder="Téléphone WhatsApp *" style={inputStyle}/>
                <input value={newEmp.email} onChange={e=>setNewEmp(p=>({...p,email:e.target.value}))} placeholder="Email" style={inputStyle}/>
                <input value={newEmp.salaire} onChange={e=>setNewEmp(p=>({...p,salaire:e.target.value}))} placeholder="Salaire (FCFA)" style={inputStyle}/>
                <input value={newEmp.dateEmbauche} onChange={e=>setNewEmp(p=>({...p,dateEmbauche:e.target.value}))} type="date" style={inputStyle}/>
              </div>
              <button onClick={addEmploye}
                className="w-full py-2 rounded-xl text-xs font-bold text-white mt-2"
                style={{background:'linear-gradient(135deg,#0ea5e9,#0284c7)'}}>
                <MdCheck size={14} style={{display:'inline',marginRight:4}}/>Ajouter l'employé
              </button>
            </div>
          )}

          {/* Liste employés */}
          {loading ? (
            <p className="text-center text-sm py-8" style={{color:th.textMuted}}>Chargement...</p>
          ) : employes.length === 0 ? (
            <div className="text-center py-12">
              <p style={{fontSize:40}}>👥</p>
              <p className="text-sm font-semibold mt-2" style={{color:th.text}}>Aucun employé</p>
              <p className="text-xs mt-1" style={{color:th.textMuted}}>Ajoutez vos employés pour leur envoyer des messages</p>
            </div>
          ) : (
            <div className="space-y-2">
              {employes.map(emp => (
                <div key={emp.id} className="rounded-xl p-3 flex items-center gap-3"
                  style={{background:th.card,border:`1px solid ${th.cardBorder}`}}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm"
                    style={{background:'linear-gradient(135deg,#0ea5e9,#0284c7)'}}>
                    {(emp.prenom||emp.nom||'?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{color:th.text}}>{emp.prenom} {emp.nom}</p>
                    <p className="text-xs truncate" style={{color:th.textMuted}}>{emp.poste}{emp.departement ? ` · ${emp.departement}` : ''}</p>
                    <p className="text-xs" style={{color:'#38bdf8'}}>{emp.phone}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setSelectedEmp(emp); setActiveTab('message'); setMsgTab('individuel') }}
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{background:'rgba(14,165,233,0.15)',color:'#38bdf8'}}>
                      <MdSend size={14}/>
                    </button>
                    <button onClick={() => deleteEmploye(emp.id)}
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{background:'rgba(239,68,68,0.1)',color:'#f87171'}}>
                      <MdDelete size={14}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab Messagerie ── */}
      {activeTab === 'message' && (
        <div>
          {/* Sous-tabs individuel/groupé */}
          <div className="flex gap-2 mb-4">
            {[{id:'individuel',label:'👤 Individuel'},{id:'groupe',label:'👥 Groupé'}].map(t => (
              <button key={t.id} onClick={() => { setMsgTab(t.id); if(t.id==='groupe') setSelectedEmp(null) }}
                className="px-4 py-2 rounded-xl text-xs font-bold"
                style={msgTab===t.id?{background:'linear-gradient(135deg,#0ea5e9,#0284c7)',color:'white'}:{background:th.inputBg,color:th.textSub}}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Sélection employé (individuel) */}
          {msgTab === 'individuel' && (
            <div className="mb-4">
              <label className="text-xs font-semibold mb-2 block" style={{color:th.textSub}}>Sélectionner un employé</label>
              <select value={selectedEmp?.id || ''} onChange={e => setSelectedEmp(employes.find(emp => emp.id === e.target.value) || null)}
                style={inputStyle}>
                <option value="">-- Choisir un employé --</option>
                {employes.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.prenom} {emp.nom} — {emp.poste}</option>
                ))}
              </select>
            </div>
          )}

          {/* Info groupé */}
          {msgTab === 'groupe' && (
            <div className="rounded-xl p-3 mb-4" style={{background:'rgba(14,165,233,0.06)',border:'1px solid rgba(14,165,233,0.2)'}}>
              <p className="text-xs" style={{color:'#38bdf8'}}>
                📢 Ce message sera envoyé à tous vos <strong>{employes.filter(e=>e.phone).length} employés</strong> ayant un numéro WhatsApp.
              </p>
            </div>
          )}

          {/* Zone message */}
          <div className="mb-4">
            <label className="text-xs font-semibold mb-2 block" style={{color:th.textSub}}>Message</label>
            <textarea value={msgText} onChange={e=>setMsgText(e.target.value)} rows={5}
              placeholder={msgTab==='groupe' ? "Réunion d'équipe demain à 9h. Merci d'être présents." : "Votre message à l'employé..."}
              className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none resize-none"
              style={{background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.text}}/>
            <p className="text-xs mt-1" style={{color:th.textMuted}}>{msgText.length}/1000 caractères</p>
          </div>

          {/* Bouton envoyer */}
          <button onClick={sendMessage} disabled={sending || !msgText.trim() || (msgTab==='individuel' && !selectedEmp)}
            className="w-full py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2"
            style={{background:sent?'linear-gradient(135deg,#10b981,#059669)':'linear-gradient(135deg,#0ea5e9,#0284c7)',opacity:(sending||!msgText.trim())?0.5:1}}>
            {sent ? <><MdCheck size={16}/>Message envoyé !</> : sending ? 'Envoi...' : <><MdSend size={16}/>{msgTab==='groupe'?`Envoyer à ${employes.filter(e=>e.phone).length} employés`:'Envoyer le message'}</>}
          </button>
        </div>
      )}
    </Modal>
  )
}

// ─── MODAL UPGRADE / PAIEMENT ───
const PLANS_CONFIG = {
  starter: { name: 'Starter', price: '15 000', color: '#3b82f6', features: [' Texte uniquement', 'FAQ + Compétences + Leads', '1 000 messages/mois', 'Support Email'] },
  pro: { name: 'Pro', price: '35 000', color: '#8b5cf6', popular: true, features: [' Texte +  Audio', 'Tout Starter +', 'Devis PDF + Catalogue', 'Suivi commandes', '5 000 messages/mois', 'Support prioritaire'] },
  premium: { name: 'Premium', price: '65 000', color: '#f59e0b', features: [' Texte +  Audio +  Images', 'Tout Pro +', 'RDV Calendrier', 'Emails automatiques', 'Relances IA', 'Messages illimités', 'Support dédié 24/7'] },
}

const UpgradeModal = ({ businessId, currentPlan, onClose, isDark }) => {
  const th = getTheme(isDark)
  const [loading, setLoading] = useState(null)
  const [error, setError] = useState('')
  const [dynamicPlans, setDynamicPlans] = useState(null)

  // Charger les vrais prix depuis la DB admin
  useEffect(() => {
    fetch(`${API_URL}/api/admin/config`)
      .then(r => r.json())
      .then(data => {
        if (data.starterPrice) {
          setDynamicPlans({
            starter: { ...PLANS_CONFIG.starter, price: Number(data.starterPrice).toLocaleString('fr-FR') },
            pro: { ...PLANS_CONFIG.pro, price: Number(data.proPrice).toLocaleString('fr-FR') },
            premium: { ...PLANS_CONFIG.premium, price: Number(data.premiumPrice).toLocaleString('fr-FR') },
          })
        }
      })
      .catch(() => {})
  }, [])

  const activePlans = dynamicPlans || PLANS_CONFIG

  const handlePay = async (plan) => {
    if (!businessId) { setError('ID business manquant — rechargez la page'); return }
    setLoading(plan); setError('')
    console.log(' Paiement:', API_URL + '/api/payments/checkout/' + businessId, plan)
    try {
      const res = await axios.post(`${API_URL}/api/payments/checkout/${businessId}`, { plan })
      if (res.data.payment_url) {
        // Ouvrir PayTech dans popup pour rester dans l'app
        const popup = window.open(res.data.payment_url, 'PayTech Payment',
          'width=600,height=700,scrollbars=yes,resizable=yes,top=100,left=' + (window.screen.width/2 - 300))
        if (!popup) {
          // Si popup bloqué, rediriger normalement
          window.location.href = res.data.payment_url
        } else {
          // Surveiller la fermeture du popup
          const timer = setInterval(() => {
            if (popup.closed) {
              clearInterval(timer)
              window.location.reload()
            }
          }, 1000)
        }
      } else {
        setError('Pas d\'URL de paiement reçu: ' + JSON.stringify(res.data))
      }
    } catch (e) {
      const status = e.response?.status
      const msg = e.response?.data?.error || e.message
      if (status === 404) {
        setError('Route introuvable (404) — vérifiez que payments.js est dans src/routes/ du backend')
      } else {
        setError(`Erreur ${status || 'réseau'}: ${msg}`)
      }
      console.error(' Paiement erreur:', e.response?.data || e.message)
    } finally { setLoading(null) }
  }

  return (
    <Modal title="Choisir un plan" icon={<MdUpgrade size={20} className="text-white"/>} color="#3b82f6" onClose={onClose} wide extraWide isDark={isDark}>
      <div className="text-center mb-5">
        <p className="text-sm" style={{color:th.textSub}}>Choisissez le plan adapté à votre activité</p>
        <p className="text-xs mt-1" style={{color:th.textMuted}}> Paiement sécurisé — Wave · Orange Money · Carte bancaire</p>
      </div>
      {error && <div className="mb-4 px-4 py-3 rounded-xl text-sm" style={{background:'rgba(239,68,68,0.1)',color:'#f87171',border:'1px solid rgba(239,68,68,0.2)'}}>{error}</div>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {Object.entries(activePlans).map(([planId, plan]) => (
          <div key={planId} className="rounded-2xl p-5 relative flex flex-col"
            style={{
              background: plan.popular ? `${plan.color}18` : th.inputBg,
              border: `2px solid ${currentPlan===planId ? '#10b981' : plan.popular ? plan.color+'55' : th.inputBorder}`,
              boxShadow: plan.popular ? `0 0 40px ${plan.color}22` : 'none',
              transform: plan.popular ? 'scale(1.02)' : 'none',
            }}>
            {plan.popular && (
              <div className="absolute -top-3.5 left-1/2 text-xs font-bold text-white px-4 py-1.5 rounded-full whitespace-nowrap"
                style={{background:plan.color, transform:'translateX(-50%)'}}>⭐ Plus populaire</div>
            )}
            {currentPlan === planId && (
              <div className="absolute -top-3.5 right-3 text-xs font-bold text-white px-3 py-1.5 rounded-full"
                style={{background:'#10b981'}}>✓ Actuel</div>
            )}
            <div className="mb-3">
              <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{color:plan.color}}>{plan.name}</p>
              <p className="text-2xl font-black whitespace-nowrap mb-1" style={{color:th.text}}>
                {plan.price} <span className="text-sm font-normal" style={{color:th.textMuted}}>FCFA/mois</span>
              </p>
            </div>
            <div className="flex-1 space-y-2 mb-4">
              {plan.features.map((f, fi) => (
                <div key={fi} className="flex items-center gap-2">
                  <span style={{color:plan.color,fontSize:'12px'}}>✓</span>
                  <span className="text-xs" style={{color:th.textSub}}>{f}</span>
                </div>
              ))}
            </div>
            <button onClick={() => handlePay(planId)}
              disabled={!!loading || currentPlan===planId}
              className="w-full py-3 rounded-xl text-sm font-bold text-white disabled:opacity-50 flex items-center justify-center gap-2"
              style={{
                background: currentPlan===planId ? '#10b981' : `linear-gradient(135deg,${plan.color},${plan.color}bb)`,
                boxShadow: plan.popular ? `0 8px 24px ${plan.color}44` : 'none'
              }}>
              {loading===planId && <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
              {currentPlan===planId ? '✓ Plan actif' : loading===planId ? 'Chargement...' : 'Choisir ce plan →'}
            </button>
          </div>
        ))}
      </div>
      <div className="mt-4 pt-4" style={{borderTop:`1px solid ${getTheme(isDark).inputBorder}`}}>
        <p className="text-xs text-center mb-3" style={{color:getTheme(isDark).textMuted}}>Paiement sécurisé via</p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          {/* Visa */}
          <div style={{width:54,height:54,borderRadius:10,background:'#1A1F71',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
            <span style={{color:'white',fontWeight:'900',fontSize:'16px',fontStyle:'italic',letterSpacing:'-1px'}}>VISA</span>
          </div>
          {/* Mastercard */}
          <div style={{width:54,height:54,borderRadius:10,background:'#252525',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
            <div style={{position:'relative',display:'flex'}}>
              <div style={{width:24,height:24,borderRadius:'50%',background:'#EB001B'}}/>
              <div style={{width:24,height:24,borderRadius:'50%',background:'#F79E1B',marginLeft:'-10px'}}/>
            </div>
          </div>
          {/* Orange Money */}
          <div style={{width:54,height:54,borderRadius:10,background:'#FF6200',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',flexShrink:0,gap:1}}>
            <svg width="28" height="20" viewBox="0 0 60 40" fill="none">
              <ellipse cx="30" cy="20" rx="28" ry="18" fill="white" opacity="0.9"/>
              <text x="30" y="25" textAnchor="middle" fontSize="14" fontWeight="900" fill="#FF6200">OM</text>
            </svg>
            <span style={{color:'white',fontSize:'7px',fontWeight:'700',lineHeight:1}}>Orange Money</span>
          </div>
          {/* Wave */}
          <div style={{width:54,height:54,borderRadius:10,background:'#1ebdee',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',flexShrink:0,gap:2}}>
            <svg width="30" height="22" viewBox="0 0 60 44" fill="none">
              <path d="M6 32 Q15 10 30 22 Q45 34 54 12" stroke="white" strokeWidth="6" strokeLinecap="round" fill="none"/>
            </svg>
            <span style={{color:'white',fontSize:'9px',fontWeight:'800',lineHeight:1}}>Wave</span>
          </div>
        </div>
      </div>
    </Modal>
  )
}


export default function BusinessDashboard() {
  const {user, logout} = useAuth()
  useInactivity() // Auto-déconnexion après 10 min d'inactivité
  const navigate = useNavigate() // eslint-disable-line no-unused-vars
  const { isDark, toggle: toggleTheme } = useTheme()
  const th = getTheme(isDark)

  const [business,setBusiness]=useState(null); const [loading,setLoading]=useState(true)
  const [stats,setStats]=useState(null); const [statsLoading,setStatsLoading]=useState(true)
  const [activeNav,setActiveNav]=useState('dashboard'); const [openModal,setOpenModal]=useState(null)
  const [lockedModule,setLockedModule]=useState(null)
  const [sidebarOpen,setSidebarOpen]=useState(true); const [mobileOpen,setMobileOpen]=useState(false)
  const [showSettings,setShowSettings]=useState(false)
  const [showStats,setShowStats]=useState(false)
  const [showRelances,setShowRelances]=useState(false)
  const [showUpgrade,setShowUpgrade]=useState(false)
  const [subscriptionStatus,setSubscriptionStatus]=useState(null)
  const [universalNumber,setUniversalNumber]=useState('221788270202')

  // Charger config plateforme pour numéro universel
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/config`)
      .then(r => { if (r.data?.universalNumber) setUniversalNumber(r.data.universalNumber.replace(/\D/g,'')) })
      .catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const [showSocial,setShowSocial]=useState(false)
  const [showModeles,setShowModeles]=useState(false)
  const [showConversations,setShowConversations]=useState(false)
  const [showNotifications,setShowNotifications]=useState(false)
  // eslint-disable-next-line no-unused-vars
  const [notifCount,setNotifCount]=useState(0)

  // Charger notifications + subscription en parallèle
  useEffect(() => {
    if (business?._id) {
      Promise.all([
        axios.get(`${API_URL}/api/notifications/${business._id}`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/payments/status/${business._id}`).catch(() => ({ data: null })),
      ]).then(([notifRes, subRes]) => {
        setNotifCount(notifRes.data.filter ? notifRes.data.filter(n => !n.read).length : 0)
        if (subRes.data) setSubscriptionStatus(subRes.data)
      })
    }
  }, [business?._id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Bloquer navigation arrière
  useEffect(() => {
    window.history.pushState(null, '', window.location.pathname)
    const handlePop = () => window.history.pushState(null, '', window.location.pathname)
    window.addEventListener('popstate', handlePop)
    return () => window.removeEventListener('popstate', handlePop)
  }, [])

  const loadBusiness = useCallback(() => {
    const bid = user?.businessId
    const url = bid ? `${API_URL}/api/businesses/${bid}` : `${API_URL}/api/businesses`
    axios.get(url)
      .then(r => {
        if (bid) setBusiness(r.data)
        else { const all = Array.isArray(r.data) ? r.data : []; setBusiness(all[0] || null) }
      })
      .catch(console.error).finally(() => setLoading(false))
  }, [user])

  useEffect(()=>{ loadBusiness() },[loadBusiness])

  // Charger les vraies statistiques sans clignotement
  useEffect(()=>{
    if (business?._id) {
      // Ne pas mettre statsLoading=true si on a déjà des stats (évite le clignotement)
      if (!stats) setStatsLoading(true)
      axios.get(`${API_URL}/api/stats/${business._id}`)
        .then(r=>setStats(r.data))
        .catch(()=>{})
        .finally(()=>setStatsLoading(false))
    }
  },[business?._id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout=()=>{ window.location.href='/'; logout() }
  const businessName=business?.name||''
  const businessPhone=business?.phone||user?.phone||''
  const plan = business?.plan || 'starter'
  // En dev (localhost) tout est ouvert, en prod on vérifie le plan
  const isDevMode = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  const isTrial = plan === 'trial'
  const allowedMods = (isDevMode || isTrial) ? ALL_MODULES : (PLAN_MODULES[plan] || PLAN_MODULES.starter)

  const handleModuleClick=(modId,modTitle)=>{ if(!allowedMods.includes(modId)) setShowUpgrade(true); else setOpenModal(modId) }
  const handleModalClose=()=>{ setOpenModal(null); loadBusiness() }



  // Vraies stats
  const statsData=[
    {
      label:"Messages aujourd'hui",
      value: statsLoading ? '...' : (stats?.messagesToday ?? '0'),
      icon:<MdChat size={22}/>,
      color:'#3b82f6',light:'rgba(59,130,246,0.15)',
      trend: stats?.messagesWeek > 0 ? `+${stats.messagesWeek} semaine` : ''
    },
    {
      label:'Clients uniques',
      value: statsLoading ? '...' : (stats?.clientsTotal ?? stats?.totalClients ?? '0'),
      icon:<MdPeople size={22}/>,
      color:'#10b981',light:'rgba(16,185,129,0.15)',
      trend: '',
      onClick: () => setOpenModal('conversations')
    },
    {
      label:'Taux de réponse',
      value: statsLoading ? '...' : (stats?.totalUserMessages > 0 ? `${stats?.responseRate ?? 0}%` : '—'),
      icon:<MdTrendingUp size={22}/>,
      color:'#8b5cf6',light:'rgba(139,92,246,0.15)',
      trend: ''
    },
  ]

  const modules=[
    {id:'faq',icon:<MdQuestionAnswer size={22}/>,title:'FAQ',desc:'Questions/Réponses récurrentes. Zéro hallucination.',badge:'Zéro Hallucination',btn:'Configurer',color:'#3b82f6',light:'rgba(59,130,246,0.12)'},
    {id:'rdv',icon:<MdCalendarToday size={22}/>,title:'Rendez-vous',desc:'Calendrier & créneaux de réservation.',badge:'Auto-booking',btn:'Gérer',color:'#10b981',light:'rgba(16,185,129,0.12)'},
    {id:'catalog',icon:<MdShoppingCart size={22}/>,title:getCatalogConfig(business?.sector)?.title || 'Catalogue',desc:getCatalogConfig(business?.sector)?.itemLabel ? `${getCatalogConfig(business?.sector).itemLabel.charAt(0).toUpperCase()+getCatalogConfig(business?.sector).itemLabel.slice(1)}s, descriptions & tarifs.` : 'Produits, descriptions & tarifs.',badge:'Social Commerce',btn:'Gérer',color:'#f59e0b',light:'rgba(245,158,11,0.12)'},
    {id:'leads',icon:<MdPersonSearch size={22}/>,title:'Collecte Leads',desc:'Questions de qualification automatiques.',badge:'CRM',btn:'Configurer',color:'#06b6d4',light:'rgba(6,182,212,0.12)'},
    {id:'devis',icon:<MdRequestQuote size={22}/>,title:'Devis Auto',desc:'Grille tarifaire & estimations.',badge:'Automatisé',btn:'Configurer',color:'#8b5cf6',light:'rgba(139,92,246,0.12)'},
    {id:'suivi',icon:<MdLocalShipping size={22}/>,title:'Suivi Commande',desc:'Statuts envoyés automatiquement.',badge:'Temps réel',btn:'Gérer',color:'#ec4899',light:'rgba(236,72,153,0.12)'},
    {id:'skills',icon:<MdPsychology size={22}/>,title:'Compétences',desc:'Instructions & règles personnalisées.',badge:'Libre',btn:'Ajouter',color:'#f97316',light:'rgba(249,115,22,0.12)'},
    {id:'personality',icon:<MdAutoAwesome size={22}/>,title:'Personnalité',desc:'Ton, style et langue de votre agent.',badge:'Brand',btn:'Configurer',color:'#a855f7',light:'rgba(168,85,247,0.12)'},
  ]

  const navItems=[
    {id:'dashboard',icon:<MdBarChart size={20}/>,label:'Dashboard'},
  ]

  const SidebarContent=()=>(
    <>
      <div style={{height:64, display:'flex', alignItems:'center', padding: sidebarOpen?'0 16px':'0', justifyContent: sidebarOpen?'flex-start':'center', borderBottom:`1px solid ${th.sidebarBorder}`, gap:12, flexShrink:0}}>
        <div style={{width:40,height:40,borderRadius:12,background:'linear-gradient(135deg,#3b82f6,#1d4ed8)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
          <MdSmartToy size={22} className="text-white"/>
        </div>
        {sidebarOpen && (
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm" style={{color:th.text}}>Agent IA</p>
            <p className="text-xs text-blue-400">by LeBinaire</p>
          </div>
        )}
        {sidebarOpen && (
          <button onClick={()=>setSidebarOpen(s=>!s)} className="hidden lg:flex w-7 h-7 rounded-lg items-center justify-center flex-shrink-0" style={{color:th.textSub}}>
            <MdChevronLeft size={18} style={{transform:'',transition:'transform 0.3s'}}/>
          </button>
        )}
        {!sidebarOpen && (
          <button onClick={()=>setSidebarOpen(s=>!s)} className="hidden lg:flex w-7 h-7 rounded-lg items-center justify-center absolute" style={{color:th.textSub, right:'-14px', top:'22px', background:th.sidebar, border:`1px solid ${th.sidebarBorder}`, borderRadius:'50%', zIndex:30}}>
            <MdChevronLeft size={16} style={{transform:'rotate(180deg)',transition:'transform 0.3s'}}/>
          </button>
        )}
      </div>

      <nav className="flex-1 px-3 py-2 space-y-0.5">
        {navItems.map(item=>(
          <button key={item.id} onClick={()=>{setActiveNav(item.id);setMobileOpen(false)}}
            className={`w-full flex items-center rounded-xl transition-all ${sidebarOpen?"gap-3 px-3 py-2.5 text-left":"justify-center py-2.5"}`}
            style={activeNav===item.id
              ? {background:'linear-gradient(135deg,#3b82f6,#1d4ed8)',color:'white',boxShadow:'0 4px 15px rgba(59,130,246,0.35)'}
              : {color:th.navText}
            }
            onMouseEnter={e=>{ if(activeNav!==item.id) e.currentTarget.style.background=th.navHover }}
            onMouseLeave={e=>{ if(activeNav!==item.id) e.currentTarget.style.background='transparent' }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              background: activeNav===item.id ? 'rgba(255,255,255,0.15)' : (item.color||'#3b82f6')+'18',
              color: activeNav===item.id ? 'white' : (item.color||'#3b82f6'),
            }}>{item.icon}</div>
            {sidebarOpen && <span className="text-sm font-medium flex-1">{item.label}</span>}
            {sidebarOpen&&activeNav===item.id&&<div className="w-1.5 h-1.5 bg-white rounded-full"/>}
          </button>
        ))}

          {/* Raccourcis — sous Dashboard */}
          <div className="mt-1 pt-1 space-y-0.5" style={{borderTop:'1px solid rgba(255,255,255,0.08)'}}>
            <button onClick={()=>setShowStats(true)}
              className={`w-full flex items-center rounded-xl transition-all ${sidebarOpen?"gap-3 px-3 py-1.5":"justify-center py-1.5"}`}
              style={{color:th.navText}} onMouseEnter={e=>e.currentTarget.style.background=th.navHover} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{width:36,height:36,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,background:"#3b82f618",color:"#3b82f6"}}><MdBarChart size={18}/></div>
              {sidebarOpen&&<span className="text-sm font-medium">Statistiques</span>}
            </button>
            <button onClick={()=>setShowConversations(true)}
              className={`w-full flex items-center rounded-xl transition-all ${sidebarOpen?"gap-3 px-3 py-1.5":"justify-center py-1.5"}`}
              style={{color:th.navText}} onMouseEnter={e=>e.currentTarget.style.background=th.navHover} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{width:36,height:36,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,background:"#8b5cf618",color:"#8b5cf6"}}><MdChat size={18}/></div>
              {sidebarOpen&&<span className="text-sm font-medium">Conversations</span>}
            </button>
            <button onClick={()=>setShowNotifications(true)}
              className={`w-full flex items-center rounded-xl transition-all ${sidebarOpen?"gap-3 px-3 py-1.5":"justify-center py-1.5"}`}
              style={{color:th.navText}} onMouseEnter={e=>e.currentTarget.style.background=th.navHover} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{width:36,height:36,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,background:"#f59e0b18",color:"#f59e0b"}}><MdNotifications size={18}/></div>
              {sidebarOpen&&<span className="text-sm font-medium">Notifications</span>}
            </button>
            <button onClick={()=>setShowSettings(true)}
              className={`w-full flex items-center rounded-xl transition-all ${sidebarOpen?"gap-3 px-3 py-1.5":"justify-center py-1.5"}`}
              style={{color:th.navText}} onMouseEnter={e=>e.currentTarget.style.background=th.navHover} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{width:36,height:36,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,background:"#6366f118",color:"#6366f1"}}><MdSettings size={18}/></div>
              {sidebarOpen&&<span className="text-sm font-medium">Paramètres entreprise</span>}
            </button>
            <button onClick={()=>setShowModeles(true)}
              className={`w-full flex items-center rounded-xl transition-all ${sidebarOpen?"gap-3 px-3 py-1.5":"justify-center py-1.5"}`}
              style={{color:th.navText}} onMouseEnter={e=>e.currentTarget.style.background=th.navHover} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{width:36,height:36,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,background:"#10b98118",color:"#10b981"}}><MdSave size={18}/></div>
              {sidebarOpen&&<span className="text-sm font-medium">Modèles de messages</span>}
            </button>
            <button onClick={()=>setShowRelances(true)}
              className={`w-full flex items-center rounded-xl transition-all ${sidebarOpen?"gap-3 px-3 py-1.5":"justify-center py-1.5"}`}
              style={{color:th.navText}} onMouseEnter={e=>e.currentTarget.style.background=th.navHover} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{width:36,height:36,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,background:"#f9731618",color:"#f97316"}}><MdRefresh size={18}/></div>
              {sidebarOpen&&<span className="text-sm font-medium">Relances automatiques</span>}
            </button>
            <button onClick={()=>setShowSocial(true)}
              className={`w-full flex items-center rounded-xl transition-all ${sidebarOpen?"gap-3 px-3 py-1.5":"justify-center py-1.5"}`}
              style={{color:th.navText}} onMouseEnter={e=>e.currentTarget.style.background=th.navHover} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{width:36,height:36,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,background:"#e1306c18",color:"#e1306c"}}><MdShare size={18}/></div>
              {sidebarOpen&&<span className="text-sm font-medium">Réseaux sociaux</span>}
            </button>
            <button onClick={()=>setOpenModal('widget')}
              className={`w-full flex items-center rounded-xl transition-all ${sidebarOpen?"gap-3 px-3 py-1.5":"justify-center py-1.5"}`}
              style={{color:th.navText}} onMouseEnter={e=>e.currentTarget.style.background=th.navHover} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <div style={{width:36,height:36,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,background:"#3b82f618",color:"#3b82f6"}}><MdCode size={18}/></div>
              {sidebarOpen&&<span className="text-sm font-medium">Widget WhatsApp</span>}
            </button>
          </div>
      </nav>

      {/* Toggle thème */}
      {sidebarOpen && (
        <div className="px-3 mb-2">
          <button onClick={() => setShowUpgrade(true)}
            className="w-full px-3 py-2 rounded-xl transition-all mb-1.5 text-sm font-semibold text-center"
            style={{
              background: subscriptionStatus?.isTrial
                ? 'linear-gradient(135deg,rgba(16,185,129,0.2),rgba(5,150,105,0.2))'
                : 'linear-gradient(135deg,rgba(59,130,246,0.2),rgba(139,92,246,0.2))',
              color: subscriptionStatus?.isTrial ? '#34d399' : '#a78bfa',
              border: `1px solid ${subscriptionStatus?.isTrial ? 'rgba(16,185,129,0.3)' : 'rgba(139,92,246,0.3)'}`
            }}>
            {subscriptionStatus?.isPaid
              ? '⚡ Changer de plan'
              : subscriptionStatus?.isTrial
                ? ` Essai gratuit — ${subscriptionStatus.daysLeft}j restants`
                : '⚡ Choisir un plan'}
          </button>
          {subscriptionStatus?.isActive && subscriptionStatus?.status === 'active' && subscriptionStatus?.daysLeft > 0 && subscriptionStatus?.daysLeft <= 7 && (
            <div className="px-3 py-2 rounded-xl mb-2 text-xs" style={{background:'rgba(245,158,11,0.1)',color:'#fbbf24',border:'1px solid rgba(245,158,11,0.2)'}}>
               Expire dans {subscriptionStatus.daysLeft} jour(s)
            </div>
          )}
          <button onClick={toggleTheme}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all"
            style={{background:th.userBox, color:th.textSub}}>
            {isDark ? <MdLightMode size={18}/> : <MdDarkMode size={18}/>}
            <span className="text-sm">{isDark ? 'Thème clair' : 'Thème sombre'}</span>
          </button>
        </div>
      )}

      <div className="p-2 mx-2 mb-2 rounded-2xl" style={{background:th.userBox,border:`1px solid ${th.sidebarBorder}`}}>
        {sidebarOpen&&(
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0" style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>
              {businessName.charAt(0)||'?'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" style={{color:th.text}}>{businessName||'...'}</p>
              <p className="text-xs capitalize" style={{color:th.textSub}}>{plan} plan</p>
            </div>
          </div>
        )}
        <button onClick={handleLogout}
          className={`w-full flex items-center rounded-xl transition-all ${sidebarOpen?"gap-3 px-3 py-2":"justify-center py-2"}`}
          style={{color:'#f87171'}}
          onMouseEnter={e=>e.currentTarget.style.background='rgba(239,68,68,0.1)'}
          onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
          <div style={{width:34,height:34,borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,background:'rgba(239,68,68,0.1)'}}>
            <MdLogout size={16} style={{color:'#f87171'}}/>
          </div>
          {sidebarOpen&&<span className="text-sm font-semibold">Déconnexion</span>}
        </button>
      </div>
    </>
  )

  const sidebarW=sidebarOpen?'w-64':'w-20'

  // Bloquer si essai expiré
  const isExpired = subscriptionStatus && subscriptionStatus.status === 'expired' && !subscriptionStatus.isActive

  if (isExpired) return (
    <div style={{background:'linear-gradient(135deg,#0f172a,#1e1b4b,#0f172a)',minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:'24px',padding:'32px'}}>
      <p style={{fontSize:'64px',margin:0}}>⏰</p>
      <h1 style={{color:'white',fontSize:'28px',fontWeight:'800',textAlign:'center',margin:0}}>Votre période d'essai est terminée</h1>
      <p style={{color:'rgba(255,255,255,0.6)',fontSize:'16px',textAlign:'center',maxWidth:'400px',margin:0}}>
        Choisissez un plan pour continuer à utiliser Agent IA Sénégal.
      </p>
      <button onClick={() => setShowUpgrade(true)}
        style={{background:'linear-gradient(135deg,#3b82f6,#1d4ed8)',color:'white',border:'none',padding:'16px 40px',borderRadius:'16px',fontSize:'16px',fontWeight:'700',cursor:'pointer',boxShadow:'0 8px 32px rgba(59,130,246,0.4)'}}>
        ⚡ Choisir mon plan →
      </button>
      <button onClick={handleLogout} style={{color:'rgba(255,255,255,0.4)',background:'none',border:'none',cursor:'pointer',fontSize:'14px'}}>
        Se déconnecter
      </button>
      {showUpgrade && <UpgradeModal businessId={business?._id} currentPlan={business?.plan} onClose={() => setShowUpgrade(false)} isDark={isDark}/>}
    </div>
  )


  return (
    <div className="flex min-h-screen transition-colors duration-300" style={{background:th.bg}}>

      {/* Modals */}
      {openModal==='faq'&&<FAQModal businessId={business?._id} business={business} onClose={handleModalClose} isDark={isDark}/>}
      {openModal==='rdv'&&<RDVModal businessId={business?._id} business={business} onClose={handleModalClose} isDark={isDark}/>}
      {openModal==='catalog'&&<CatalogModal businessId={business?._id} business={business} onClose={handleModalClose} isDark={isDark}/>}
      {openModal==='leads'&&<LeadsModal businessId={business?._id} business={business} onClose={handleModalClose} isDark={isDark}/>}
      {openModal==='devis'&&<DevisModal businessId={business?._id} business={business} onClose={handleModalClose} isDark={isDark}/>}
      {openModal==='suivi'&&<SuiviModal businessId={business?._id} business={business} onClose={handleModalClose} isDark={isDark}/>}
      {openModal==='skills'&&<SkillsModal businessId={business?._id} business={business} onClose={handleModalClose} isDark={isDark}/>}
      {openModal==='personality'&&<PersonalityModal businessId={business?._id} business={business} onClose={handleModalClose} isDark={isDark}/>}
      {showUpgrade&&<UpgradeModal businessId={business?._id} currentPlan={business?.plan} onClose={()=>setShowUpgrade(false)} isDark={isDark} wide/>}
      {showSocial&&<SocialModal businessId={business?._id} business={business} onClose={()=>setShowSocial(false)} isDark={isDark}/>}
      {showRelances&&<RelancesModal businessId={business?._id} business={business} onClose={()=>setShowRelances(false)} isDark={isDark}/>}
      {showModeles&&<ModelesModal businessId={business?._id} business={business} onClose={()=>setShowModeles(false)} isDark={isDark}/>}
      {showStats&&<StatsModal businessId={business?._id} business={business} onClose={()=>setShowStats(false)} isDark={isDark}/>}
      {showConversations&&<ConversationsModal businessId={business?._id} business={business} onClose={()=>setShowConversations(false)} isDark={isDark}/>}
      {showNotifications&&<NotificationsModal businessId={business?._id} business={business} onClose={()=>{setShowNotifications(false);setNotifCount(0)}} isDark={isDark}/>}
      {showSettings&&<SettingsModal businessId={business?._id} business={business} onClose={()=>{setShowSettings(false);loadBusiness()}} isDark={isDark}/>}
      {openModal==='playground'&&<PlaygroundModal businessId={business?._id} businessName={businessName} onClose={()=>setOpenModal(null)} isDark={isDark}/>}
      {openModal==='qrcode'&&<QRCodeModal business={business} onClose={()=>setOpenModal(null)} isDark={isDark} universalNumber={universalNumber}/>}
      {openModal==='widget'&&<WidgetModal business={business} onClose={()=>setOpenModal(null)} isDark={isDark} universalNumber={universalNumber}/>}
      {openModal==='employes'&&<EmployesModal businessId={business._id} business={business} onClose={()=>setOpenModal(null)} isDark={isDark} API_URL={API_URL}/>}
      {openModal==='paiements'&&<PaiementsModal businessId={business?._id} business={business} onClose={()=>setOpenModal(null)} isDark={isDark}/>}
      {lockedModule&&<LockedModal module={lockedModule} plan={plan} onClose={()=>setLockedModule(null)} isDark={isDark}/>}

      {mobileOpen&&<div className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={()=>setMobileOpen(false)}/>}

      {/* Sidebar Desktop */}
      <div className={`hidden lg:flex fixed h-screen flex-col ${sidebarW} transition-all duration-300 z-20`}
        style={{background:th.sidebar, backdropFilter:'blur(20px)', borderRight:`1px solid ${th.sidebarBorder}`}}>
        <SidebarContent/>
      </div>

      {/* Sidebar Mobile */}
      <div className={`fixed h-screen flex-col w-64 z-40 lg:hidden transition-transform duration-300 ${mobileOpen?'translate-x-0':'-translate-x-full'} flex`}
        style={{background:th.sidebar, backdropFilter:'blur(20px)', borderRight:`1px solid ${th.sidebarBorder}`}}>
        <SidebarContent/>
      </div>

      {/* Main */}
      <main className={`flex-1 transition-all duration-300 p-3 sm:p-4 lg:p-8 ${sidebarOpen?'lg:ml-64':'lg:ml-20'}`}>

        {/* Header */}
        <div className="flex items-center justify-between mb-6 lg:mb-8">
          <div className="flex items-center gap-3">
            <button onClick={()=>setMobileOpen(true)} className="lg:hidden w-10 h-10 rounded-xl flex items-center justify-center" style={{background:th.card,color:th.textSub}}>
              <MdMenu size={22}/>
            </button>
            <div>
              {loading?(
                <div className="space-y-2">
                  <div className="h-6 w-56 rounded-xl animate-pulse" style={{background:th.card}}/>
                  <div className="h-3 w-72 rounded animate-pulse" style={{background:th.card}}/>
                </div>
              ):(
                <>
                  <h1 className="text-xl lg:text-2xl font-bold" style={{color:th.text}}>
                    Bienvenue chez <span style={{color:'#3b82f6'}}>{businessName}</span>
                  </h1>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-xs font-semibold text-green-500">● Agent actif</span>
                    <span className="text-xs" style={{color:th.textMuted}}>·</span>
                    <span className="text-xs hidden sm:inline" style={{color:th.textSub}}>Votre Assistant WhatsApp Intelligent</span>
                    <span className="text-xs" style={{color:th.textMuted}}>·</span>
                    <span className="text-xs font-mono" style={{color:th.text}}>{formatPhone(businessPhone)}</span>
                    {business?.sector && (
                      <>
                        <span className="text-xs" style={{color:th.textMuted}}>·</span>
                        <span className="text-xs capitalize hidden md:inline" style={{color:th.textSub}}>{business.sector}</span>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 lg:gap-3">
            <button onClick={()=>setOpenModal('qrcode')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold"
              style={{background:'rgba(37,211,102,0.15)',color:'#25d366',border:'1px solid rgba(37,211,102,0.25)'}}>
              <span></span><span className="hidden lg:inline">QR Code</span>
            </button>
            <button onClick={()=>setOpenModal('playground')}
              className="flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 py-2 rounded-xl text-xs lg:text-sm font-semibold text-white"
              style={{background:'linear-gradient(135deg,#10b981,#059669)',boxShadow:'0 4px 15px rgba(16,185,129,0.3)'}}>
              <MdPlayArrow size={16}/><span className="hidden sm:inline">Tester l'Agent</span>
            </button>
            {/* Icône cloche notifications */}
            <button onClick={()=>setShowNotifications(true)}
              className="relative w-10 h-10 rounded-xl flex items-center justify-center transition-all"
              style={{background:'rgba(245,158,11,0.12)',color:'#f59e0b',border:'1px solid rgba(245,158,11,0.2)'}}>
              <MdNotifications size={20}/>
              {notifCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-white font-bold"
                  style={{background:'#ef4444',fontSize:'9px'}}>{notifCount > 9 ? '9+' : notifCount}</span>
              )}
            </button>

          </div>
        </div>

        {/* 3 Stats avec vraies données */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6 mb-6 lg:mb-8">
          {(loading || statsLoading) ? [1,2,3].map(i=><SkeletonStat key={i} isDark={isDark}/>) : statsData.map((s,i)=>(
            <div key={i} className="rounded-2xl p-5 lg:p-6 transition-all hover:scale-[1.02]"
              onClick={s.onClick || undefined}
              style={{background:th.card,backdropFilter:'blur(20px)',border:`1px solid ${th.cardBorder}`,boxShadow:th.cardShadow,cursor:s.onClick?'pointer':'default'}}>
              <div className="flex items-center justify-between mb-4">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{background:s.light,color:s.color}}>{s.icon}</div>
                {s.trend&&<span className="text-green-500 text-xs font-semibold px-2 py-1 rounded-lg" style={{background:'rgba(16,185,129,0.1)'}}>{s.trend}</span>}
              </div>
              <p className="text-3xl font-bold mb-1" style={{color:th.text}}>{s.value}</p>
              <p className="text-sm" style={{color:th.textSub}}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* Modules */}
        <div className="mb-6 lg:mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base lg:text-lg font-semibold" style={{color:th.text}}>Configuration de l'Agent IA</h2>
            <span className="text-xs px-3 py-1.5 rounded-lg" style={{background:th.card,border:`1px solid ${th.cardBorder}`,color:th.textSub}}> FR · EN · WO · Audio</span>
          </div>
          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4">
            {modules.map(mod=>{
              const locked=!allowedMods.includes(mod.id)
              return (
                <div key={mod.id} className={`rounded-2xl p-4 lg:p-5 flex flex-col gap-3 transition-all hover:scale-[1.02] relative ${locked?'opacity-70':''}`}
                  style={{background:th.card,backdropFilter:'blur(20px)',border:`1px solid ${locked?th.cardBorder:th.cardBorder}`,boxShadow:th.cardShadow}}>
                  {locked&&<div className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center" style={{background:th.userBox}}><MdLock size={12} style={{color:th.textSub}}/></div>}
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{background:mod.light,color:locked?th.textSub:mod.color}}>{mod.icon}</div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-lg ml-1"
                      style={{background:locked?th.inputBg:mod.light, color:locked?th.textSub:mod.color, border:`1px solid ${locked?th.inputBorder:mod.color+'44'}`, fontSize:'9px'}}>
                      {locked?'Verrouillé':mod.badge}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-bold text-sm" style={{color:th.text}}>{mod.title}</h3>
                    <p className="text-xs mt-1 leading-relaxed" style={{color:th.textSub,fontSize:'12px'}}>{mod.desc}</p>
                  </div>
                  <button onClick={()=>handleModuleClick(mod.id,mod.title)}
                    className="w-full py-2 rounded-xl text-xs font-semibold text-white transition-all hover:opacity-90 mt-auto flex items-center justify-center gap-1"
                    style={{background:locked?th.userBox:`linear-gradient(135deg,${mod.color},${mod.color}bb)`,color:locked?th.textSub:'white'}}>
                    {locked&&<MdLock size={12}/>}{locked?'Passer au Pro':mod.btn}
                  </button>
                </div>
              )
            })}
          </div>
        </div>

        {/* Conversations */}
        <div className="rounded-2xl overflow-hidden" style={{background:th.card,backdropFilter:'blur(20px)',border:`1px solid ${th.cardBorder}`}}>
          <div className="flex items-center justify-between p-5 lg:p-6" style={{borderBottom:`1px solid ${th.cardBorder}`}}>
            <h2 className="text-base lg:text-lg font-semibold" style={{color:th.text}}>Conversations récentes</h2>
            <span className="text-xs px-3 py-1.5 rounded-lg" style={{background:th.userBox,color:th.textSub}}>Aujourd'hui</span>
          </div>
          {stats?.recentConversations?.length > 0 ? (
            <div className="divide-y" style={{borderColor:th.cardBorder}}>
              {stats.recentConversations.map((conv, i) => (
                <div key={i} className="flex items-center gap-3 p-4 hover:opacity-80 cursor-pointer"
                  onClick={() => setOpenModal('conversations')}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm"
                    style={{background:'linear-gradient(135deg,#3b82f6,#1d4ed8)'}}>
                    {conv.phone?.slice(-2) || '??'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{color:th.text}}>+{conv.phone}</p>
                    <p className="text-xs truncate" style={{color:th.textMuted}}>{conv.lastMessage || 'Nouveau client'}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-xs px-2 py-0.5 rounded-lg" style={{background:conv.lastRole==='assistant'?'rgba(16,185,129,0.1)':'rgba(59,130,246,0.1)',color:conv.lastRole==='assistant'?'#10b981':'#3b82f6'}}>
                      {conv.lastRole==='assistant'?'IA':'Client'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
          <div className="p-10 text-center">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{background:th.userBox}}>
              <MdChat size={26} style={{color:th.textMuted}}/>
            </div>
            <p className="text-sm" style={{color:th.textSub}}>Aucune conversation pour le moment</p>
            <p className="text-xs mt-1" style={{color:th.textMuted}}>Les conversations WhatsApp apparaîtront ici</p>
          </div>
          )}
        </div>
      </main>
    </div>
  )
}
